/**
 * LetterCraft DIY — Size rules / Apply / Color / i18n / AJAX cart
 */
(function () {
  'use strict';
/* hm-scene-refit-20261013d */

  const TURKISH_UPPER = {
    i: 'İ',
    ı: 'I',
    ş: 'Ş',
    ğ: 'Ğ',
    ü: 'Ü',
    ö: 'Ö',
    ç: 'Ç',
    ñ: 'Ñ',
  };

  const PUA_START = 0xe000;

  /** Magnetic letter sizes (not the virtual "Semboller" picker). */
  const MAGNETIC_LETTER_SIZE_IDS = ['h95', 'h120', 'h170', 'h220'];
  /**
   * Shared magnetic letter photos are one visual height (--lc-h: 1).
   * Symbols keep their own mm against this 12 cm letter box so switching
   * 9,5 / 12 / 17 / 22 cm does not resize already-picked specials.
   */
  const MAGNETIC_VISUAL_LETTER_MM = 120;

  /** Allowed letter sizes for a special (ABC Mix allow-list). sizeId = asset folder. */
  function specialAllowedSizeIds(sp) {
    if (Array.isArray(sp?.sizeIds) && sp.sizeIds.length) return sp.sizeIds;
    if (sp?.sizeId) return [sp.sizeId];
    return null;
  }

  /** Magnetic specials: show only when current size is in sizeIds (or legacy sizeId). */
  function isSpecialAllowedForMagnetic(sp, sizeId) {
    const allowed = specialAllowedSizeIds(sp);
    if (!allowed) return true;
    return allowed.includes(sizeId);
  }


  /** Decode HTML entities from Liquid `| t` so textContent shows real apostrophes. */
  function decodeHtmlEntities(str) {
    if (str == null || str === '') return str;
    const s = String(str);
    if (!s.includes('&')) return s;
    if (typeof document !== 'undefined') {
      const el = document.createElement('textarea');
      el.innerHTML = s;
      return el.value;
    }
    return s
      .replace(/&#39;|&apos;|&#x27;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
  }

  function t(i18n, key, fallback) {
    if (i18n && i18n[key]) return decodeHtmlEntities(i18n[key]);
    return decodeHtmlEntities(fallback || key);
  }

  function toTurkishUpper(char) {
    if (Object.prototype.hasOwnProperty.call(TURKISH_UPPER, char)) return TURKISH_UPPER[char];
    return char.toUpperCase();
  }

  function isPua(ch) {
    if (!ch) return false;
    const cp = ch.codePointAt(0);
    return cp >= PUA_START && cp < PUA_START + 256;
  }

  /** True for PUA or Unicode specials registered in specialByChar (♥ 🎄 👫 …). */
  function isMappedSpecial(ch, specialByChar) {
    return Boolean(ch && specialByChar?.has(ch));
  }

  /** Accept legacy stems (AG_AC_/C_I_FT/VE) and new merchant stems (AGAC/CIFT/ET). */
  const RGB_STEM_ALIASES = {
    AG_AC_: 'AGAC',
    AGAC: 'AGAC',
    C_I_FT: 'CIFT',
    CIFT: 'CIFT',
    VE: 'ET',
    ET: 'ET',
  };
  function normalizeRgbStem(stem) {
    if (!stem) return stem;
    return RGB_STEM_ALIASES[stem] || stem;
  }

  /**
   * Magnetic: one photo per letter — map KUCUK_* → BUYUK_* (same PNG as uppercase).
   * Merchant short names (letter-*-S.png) still resolve via BUYUK_→short error fallback.
   * RGB keeps separate size/case packs; do not call this for rgb craftMode.
   */
  function normalizeMagneticLetterStem(stem) {
    if (!stem) return stem;
    const s = String(stem);
    if (/^KUCUK_/i.test(s)) return s.replace(/^KUCUK_/i, 'BUYUK_');
    return s;
  }

  /** Short stem for merchant packs: BUYUK_A / KUCUK_A → A (letter-yellow-A.png). */
  function magneticShortStem(stem) {
    if (!stem) return stem;
    const s = String(stem);
    if (/^BUYUK_/i.test(s)) return s.replace(/^BUYUK_/i, '');
    if (/^KUCUK_/i.test(s)) return s.replace(/^KUCUK_/i, '');
    return s;
  }

  /**
   * Magnetic letters share one latest photo set across all size tabs (scene Alt Boyut scales).
   * Live CDN pack: letter-white-BUYUK_A.png / letter-yellow-A.png (not the older letter-95-*).
   * Specials stay size-locked (handled before this path).
   */
  function magneticSharedLetterFile(stem, color) {
    const norm = normalizeMagneticLetterStem(stem) || stem;
    if (!norm) return '';
    let file = `letter-${norm}.png`;
    return applyMagneticColorPrefix(file, color) || file;
  }

  function usesSharedMagneticAssets(charset, config) {
    if (config?.craftMode === 'rgb' || config?.forceRgbAssets === true) return false;
    return (
      config?.craftMode === 'magnetic' ||
      (charset?.sharedMagneticAssets &&
        config?.craftMode !== 'rail' &&
        config?.craftMode !== 'floor')
    );
  }

  /** Reverse-map special stem (e.g. KALP) to Unicode char (♥) via charset. */
  function charForSpecialStem(charset, stem) {
    if (!stem) return null;
    const bySymbol = charset?.symbolStemByChar || {};
    for (const [ch, st] of Object.entries(bySymbol)) {
      if (st === stem) return ch;
    }
    const sizes = charset?.sizes || {};
    for (const entry of Object.values(sizes)) {
      const chars = entry?.chars || {};
      for (const [ch, st] of Object.entries(chars)) {
        if (st === stem) return ch;
      }
    }
    return null;
  }

  /**
   * Emoji shown in the text input / cart label for symbol stems.
   * Storage chars stay charset keys (♥, +, …); display can be richer emoji.
   */
  const STEM_DISPLAY_EMOJI = {
    ARTI: '➕',
    BUYUKTUR: '▶️',
    DIEZ: '#️⃣',
    DOLAR: '💲',
    EGIK_CIZGI: '➗',
    EKSI: '➖',
    ET: '&',
    EURO: '💶',
    /* Single code point (not ❤+FE0F) so Array.from / caret / mirror stay 1 slot */
    KALP: '💖',
    KARE: '⬛',
    KISA_CIZGI: '➖',
    KUCUKTUR: '◀️',
    NOKTA: '•',
    SAG_OK: '➡️',
    SOL_OK: '⬅️',
    YUKARI_OK: '⬆️',
    ASAGI_OK: '⬇️',
    SORU_ISARETI: '❓',
    UNLEM: '❗',
    YUZDE: '💯',
    YILBASI_AGACI: '🎄',
    AGAC: '🎄',
    CIFT: '👫',
    // Cups: tall glass (bardak) vs coffee cup (fincan) — must stay distinct
    KAHVE_BARDAGI: '🥤',
    BARDAK: '🥤',
    KAHVE_FINCANI: '☕',
    FINCAN: '☕',
    KARE_LOGO: '⬜',
    YUVARLAK_LOGO: '⚪',
    YUVARLAK_LOGO2: '🔵',
    ESKI: '✦',
    BEGENI: '👍',
    KEDI: '🐱',
    OFF: '📴',
  };

  /** Coffee / cup stems → distinct emojis (bardak/bardağı=🥤, fincan=☕). */
  function emojiForCupStem(stem) {
    const key = String(stem || '').toUpperCase();
    if (!key) return null;
    // Fincan first so KAHVE_FINCANI never falls through to bardak
    if (key.includes('FINCAN')) return '☕';
    if (key.includes('BARDAGI') || key === 'KAHVE_BARDAGI') return '🥤';
    if (key === 'BARDAK' || key.includes('BARDAK')) return '🥤';
    if (key.includes('KAHVE')) return '☕';
    return null;
  }

  function emojiForStem(stem) {
    if (!stem) return null;
    const key = String(stem).toUpperCase();
    if (STEM_DISPLAY_EMOJI[key]) return STEM_DISPLAY_EMOJI[key];
    const cup = emojiForCupStem(key);
    if (cup) return cup;
    // Only remap RGB legacy photo stems (AG_AC_/C_I_FT) — never VE→ET for display
    if (key === 'AG_AC_' || key === 'AGAC') return STEM_DISPLAY_EMOJI.AGAC || null;
    if (key === 'C_I_FT' || key === 'CIFT') return STEM_DISPLAY_EMOJI.CIFT || null;
    if (key.includes('KARE_LOGO')) return '⬜';
    if (key.includes('YUVARLAK_LOGO2')) return '🔵';
    if (key.includes('YUVARLAK')) return '⚪';
    return null;
  }

  /** Display glyph for a special: emoji → Unicode char → never raw "Kalp"/stem codes. */
  function specialDisplayGlyph(sp) {
    if (!sp) return '◆';
    const byStem = emojiForStem(sp.stem);
    if (byStem) return byStem;
    if (sp.char && !isPua(sp.char)) return sp.char;
    // Shape-only specials (logos, cups): short pictograph, not Turkish label codes
    const stem = String(sp.stem || '').toUpperCase();
    const cup = emojiForCupStem(stem);
    if (cup) return cup;
    if (stem.includes('KARE_LOGO')) return '⬜';
    if (stem.includes('YUVARLAK_LOGO2')) return '🔵';
    if (stem.includes('YUVARLAK')) return '⚪';
    if (stem === 'ESKI') return '✦';
    return '◆';
  }

  /**
   * Prefer a single visible Unicode code point as the stored input char.
   * PUA glyphs often have ~0 advance width and break the textarea/mirror layout.
   */
  function storageCharFromDisplayGlyph(glyph) {
    if (!glyph) return null;
    const units = Array.from(String(glyph));
    if (!units.length) return null;
    // Skip pure diamond placeholder — caller may still assign PUA
    if (units.length === 1 && units[0] === '◆') return null;
    return units[0];
  }

  /** Input/cart display for any typed char (symbols as emoji, letters unchanged). */
  function charDisplayGlyph(ch, specialByChar, charset, sizeId) {
    if (!ch) return '';
    if (isMappedSpecial(ch, specialByChar)) {
      return specialDisplayGlyph(specialByChar.get(ch));
    }
    const stem =
      stemForChar(charset, sizeId, ch) ||
      charset?.symbolStemByChar?.[ch] ||
      charset?.symbolStemByChar?.[toTurkishUpper(ch)] ||
      null;
    return emojiForStem(stem) || ch;
  }

  function textNeedsDisplayMirror(text, specialByChar, charset, sizeId) {
    for (const ch of Array.from(text || '')) {
      if (isMappedSpecial(ch, specialByChar)) return true;
      if (charDisplayGlyph(ch, specialByChar, charset, sizeId) !== ch) return true;
    }
    return false;
  }

  /** Width-bearing BMP placeholders when emoji storage would collide (never PUA/0-width). */
  const WIDTH_BEARING_STORE = ['▣', '▤', '▥', '▦', '▧', '▨', '▩', '▪', '▫', '◈', '◇', '◆', '○', '◎', '●'];

  /**
   * Map specials to insertable chars.
   * Prefer explicit sp.char / stem→Unicode (♥ for KALP); then emoji storage
   * (🥤/☕/🎄…) so the textarea has real advance width. Avoid PUA (0-width break).
   */
  function createSpecialCharMap(specials, charset) {
    const byChar = new Map();
    const list = [];
    let bearingIndex = 0;
    let puaIndex = 0;
    (specials || []).forEach((sp) => {
      const explicit = sp?.char && !isPua(sp.char) ? sp.char : null;
      const mappedStem = charForSpecialStem(charset, sp?.stem);
      const fromEmoji = storageCharFromDisplayGlyph(specialDisplayGlyph(sp));
      // Prefer real emoji over narrow text hearts (♥/❤) — those break mirror layout.
      const explicitIsNarrowHeart = explicit && /^[♥❤]$/.test(explicit);
      const mappedIsNarrowHeart = mappedStem && /^[♥❤]$/.test(mappedStem);
      let ch = null;
      if (fromEmoji && !byChar.has(fromEmoji) && (explicitIsNarrowHeart || mappedIsNarrowHeart || !explicit)) {
        ch = fromEmoji;
      }
      if (!ch) ch = (!explicitIsNarrowHeart && explicit) || (!mappedIsNarrowHeart && mappedStem) || fromEmoji || null;
      if (!ch || byChar.has(ch)) {
        // Collision: unique width-bearing BMP so caret/mirror stay aligned
        while (bearingIndex < WIDTH_BEARING_STORE.length && byChar.has(WIDTH_BEARING_STORE[bearingIndex])) {
          bearingIndex += 1;
        }
        if (bearingIndex < WIDTH_BEARING_STORE.length) {
          ch = WIDTH_BEARING_STORE[bearingIndex++];
        } else {
          while (byChar.has(String.fromCodePoint(PUA_START + puaIndex))) puaIndex += 1;
          ch = String.fromCodePoint(PUA_START + puaIndex++);
        }
      }
      const meta = { ...sp, char: ch };
      byChar.set(ch, meta);
      list.push(meta);
    });
    return { byChar, list };
  }

  function sizeEntry(charset, sizeId) {
    return charset?.sizes?.[sizeId] || null;
  }

  function allowedCharSet(charset, sizeId) {
    // "Semboller" has no letter charset — allow any magnetic letter so text persists
    // while picking specials (same continuity as switching H9.5 ↔ H22).
    if (sizeId === 'symbols') {
      const set = new Set();
      MAGNETIC_LETTER_SIZE_IDS.forEach((id) => {
        Object.keys(sizeEntry(charset, id)?.chars || {}).forEach((ch) => set.add(ch));
      });
      return set;
    }
    const entry = sizeEntry(charset, sizeId);
    return new Set(Object.keys(entry?.chars || {}));
  }

  function stemForChar(charset, sizeId, ch) {
    const entry = sizeEntry(charset, sizeId);
    return entry?.chars?.[ch] || null;
  }

  function assetFileFor(charset, sizeId, stem) {
    const entry = sizeEntry(charset, sizeId);
    return entry?.files?.[stem] || null;
  }

  function mmForSize(config, sizeId) {
    const size = (config.sizes || []).find((s) => s.id === sizeId);
    if (size?.mm) return String(size.mm);
    const m = String(sizeId || '').match(/(\d+)/);
    return m ? m[1] : '95';
  }

  /**
   * Opaque height / canvas height of the live magnetic letter photos
   * (letter-white-BUYUK_*, 571×621 class). Tight symbols are scaled against the
   * shared 12 cm letter box — not the active ölçü tab.
   */
  const MAGNETIC_LETTER_FILL = 0.92;
  const MAGNETIC_GLYPH_FILL = {
    'letter-120-YUKARI_OK.png': 0.93,
    'letter-120-ASAGI_OK.png': 0.93,
    // Left/right canvases are wider with short ink; same fill as up/down
    // so all four 12 cm arrows share one preview size.
    'letter-120-SOL_OK.png': 0.93,
    'letter-120-SAG_OK.png': 0.93,
    'letter-120-KALP.png': 0.55,
    'letter-120-OFF.png': 0.99,
    'letter-135-KARE_LOGO.png': 0.99,
    'letter-135-YUVARLAK_LOGO.png': 0.99,
    'letter-140-BARDAK.png': 0.99,
    'letter-150-CIFT.png': 0.99,
    'letter-155-BEGENI.png': 0.94,
    'letter-155-KAHVE_BARDAGI.png': 1,
    'letter-170-YILBASI_AGACI.png': 0.95,
    'letter-178-KEDI.png': 0.97,
    'letter-220-KARE_LOGO.png': 1,
    'letter-220-YUVARLAK_LOGO.png': 1
  };

  function magneticGlyphScale(special) {
    const base = MAGNETIC_VISUAL_LETTER_MM;
    const sym = Number(special?.mm) || base;
    const fill = MAGNETIC_GLYPH_FILL[String(special?.file || '')] || MAGNETIC_LETTER_FILL;
    const ratio = (sym / base) * (MAGNETIC_LETTER_FILL / fill);
    if (!Number.isFinite(ratio) || ratio <= 0) return 1;
    return Math.round(ratio * 1000) / 1000;
  }

  function normalizeForSize(raw, charset, sizeId, specialByChar) {
    const allowed = allowedCharSet(charset, sizeId);
    let out = '';
    for (const ch of Array.from(raw || '')) {
      if (isMappedSpecial(ch, specialByChar)) {
        out += ch;
        continue;
      }
      if (ch === ' ') {
        out += ' '; // boşluk her ölçüde serbest; ücret billable sayımında
        continue;
      }
      // Prefer exact char, then upper
      if (allowed.has(ch)) {
        out += ch;
        continue;
      }
      const up = toTurkishUpper(ch);
      if (allowed.has(up)) {
        out += up;
        continue;
      }
      const low = ch.toLocaleLowerCase('tr-TR');
      if (allowed.has(low)) {
        out += low;
        continue;
      }
      // drop unsupported
    }
    return out;
  }

  function normalizeDisplayText(raw, specialByChar, charset, sizeId) {
    return Array.from(raw || '')
      .map((ch) => charDisplayGlyph(ch, specialByChar, charset, sizeId))
      .join('');
  }

  function analyzeText(text, specialByChar) {
    let letters = 0;
    let symbols = 0;
    let spaces = 0;
    let specials = 0;
    for (const ch of Array.from(text || '')) {
      if (ch === ' ') spaces += 1;
      else if (isMappedSpecial(ch, specialByChar)) specials += 1;
      else if (/[0-9A-Za-zÇçĞğİıÖöŞşÜüÑñ]/.test(ch)) letters += 1;
      else symbols += 1;
    }
    const glyphs = letters + symbols + specials;
    return {
      letters,
      symbols,
      spaces,
      specials,
      glyphs,
      billable: glyphs + spaces,
      hasLetters: letters > 0 || specials > 0 || symbols > 0,
      onlySymbols: letters === 0 && (symbols > 0 || specials > 0),
    };
  }

  function countValidLetters(text, specialByChar) {
    return analyzeText(text, specialByChar).billable;
  }

  function isSymbolChar(ch, charset, sizeId) {
    if (isPua(ch)) return true;  // PUA specials treated as symbols
    const stem = stemForChar(charset, sizeId, ch);
    if (!stem) return false;
    return !/^(BUYUK_|KUCUK_)?[A-Zİ]$/i.test(stem) && !/^[0-9]$/.test(stem) && stem !== 'SPACE';
  }

  function isLetterChar(ch, charset, sizeId) {
    if (ch === ' ' || isPua(ch)) return false;
    const stem = stemForChar(charset, sizeId, ch);
    return Boolean(stem) && !isSymbolChar(ch, charset, sizeId);
  }

  /** Harf sayısına göre 1A / 3A / 5A — eşikler config.adapterRules */
  const DEFAULT_ADAPTER_RULES = [
    { max: 5, adapter: '1a' },
    { max: 12, adapter: '3a' },
    { max: 999, adapter: '5a' },
  ];

  function adapterKeyForCount(count, rules) {
    const list = (Array.isArray(rules) && rules.length ? rules : DEFAULT_ADAPTER_RULES)
      .slice()
      .sort((a, b) => Number(a.max) - Number(b.max));
    const n = Math.max(0, Number(count) || 0);
    for (const rule of list) {
      if (n <= Number(rule.max)) return rule.adapter;
    }
    return list[list.length - 1]?.adapter || '5a';
  }

  function cartAddUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/add.js`;
  }

  function cartSectionIds() {
    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    if (!ids.includes('cart-drawer-section')) ids.push('cart-drawer-section');
    return [...new Set(ids)];
  }

  async function fetchCartSections(sectionIds) {
    if (!sectionIds?.length) return {};
    const root = window.Shopify?.routes?.root || '/';
    try {
      const data = await fetch(`${root}?sections=${sectionIds.join(',')}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
      return data && typeof data === 'object' ? data : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * Horizon cart drawer: CartLinesUpdateEvent + section morph.
   * Empty→filled uses startViewTransition; opening the drawer mid-transition
   * cancels it and leaves "Sepetiniz boş". Always await a fresh section render
   * before the caller opens the drawer.
   */
  async function notifyHorizonCart(addResponse, lines) {
    const root = window.Shopify?.routes?.root || '/';
    let cartJson = null;
    try {
      cartJson = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
    } catch (_) {}

    const sectionIds = cartSectionIds();
    let sections = { ...(addResponse?.sections || {}) };
    const missing = sectionIds.filter((id) => !sections[id]);
    if (missing.length) {
      Object.assign(sections, await fetchCartSections(missing));
    }
    // Prefer live cart HTML so empty-drawer markup is fully replaced.
    Object.assign(sections, await fetchCartSections(['cart-drawer-section']));

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines || [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) : undefined,
        detail: {
          items: cartJson?.items || [],
          source: 'letter-craft',
          itemCount: cartJson?.item_count,
          sections,
          didError: false,
        },
      });
      // Awaitable morph — avoids empty-cart startViewTransition being cancelled.
      try {
        const { sectionRenderer } = await import('@theme/section-renderer');
        await sectionRenderer.renderSection('cart-drawer-section', {
          cache: false,
          mode: 'hydration',
          injectStylesheet: true,
        });
      } catch (_) {}
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    } catch (_) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: cartJson || addResponse, bubbles: true })
      );
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: cartJson || addResponse, bubbles: true })
      );
    }
  }

  function sanitizeInput(value, maxChars, charset, sizeId, specialByChar) {
    const cleaned = normalizeForSize(value, charset, sizeId, specialByChar);
    return Array.from(cleaned).slice(0, maxChars).join('');
  }

  /** Flatten RGB specials from shared + every size (for mixed-string recognition). */
  function rgbAllSpecials(charset) {
    const seen = new Set();
    const out = [];
    const push = (sp) => {
      if (!sp?.id || seen.has(sp.id)) return;
      seen.add(sp.id);
      out.push(sp);
    };
    (charset?.sharedSpecials || []).forEach(push);
    Object.values(charset?.sizes || {}).forEach((entry) => {
      (entry?.specials || []).forEach(push);
    });
    return out;
  }

  /** True if a mapped special may be newly typed/inserted at sizeId. */
  function isRgbSpecialAllowedForSize(sp, sizeId) {
    if (!sp || sp.comingSoon) return false;
    if (sp.insertSpace || sp.stem === 'SPACE' || sp.char === ' ') return true;
    if (sp.allRgbSizes) return true;
    return isSpecialAllowedForMagnetic(sp, sizeId);
  }

  /**
   * Natural RGB size class for a glyph (asset + price), independent of UI selection.
   * Digits → h200, couple/tree → h170, letters/&/heart → h120.
   */
  function rgbNaturalSizeIdForChar(ch, charset, specialByChar) {
    if (!ch || ch === ' ') return null;
    if (isMappedSpecial(ch, specialByChar)) {
      const sp = specialByChar.get(ch);
      if (sp?.insertSpace || sp?.stem === 'SPACE') return null;
      const allowed = specialAllowedSizeIds(sp);
      if (allowed?.length) return allowed[0];
      if (sp?.sizeId) return sp.sizeId;
    }
    const stem = charset?.symbolStemByChar?.[ch];
    if (stem) {
      const hit = rgbAllSpecials(charset).find(
        (sp) => String(sp.stem || '').toUpperCase() === String(stem).toUpperCase()
      );
      if (hit) {
        const allowed = specialAllowedSizeIds(hit);
        if (allowed?.length) return allowed[0];
        if (hit.sizeId) return hit.sizeId;
      }
    }
    if (/[0-9]/.test(ch)) return 'h200';
    const up = toTurkishUpper(ch);
    if (allowedCharSet(charset, 'h200').has(ch) || allowedCharSet(charset, 'h200').has(up)) {
      return 'h200';
    }
    if (allowedCharSet(charset, 'h120').has(ch) || allowedCharSet(charset, 'h120').has(up)) {
      return 'h120';
    }
    return 'h120';
  }

  /** True if ch is typeable for the active RGB size (12=letters, 17=specials, 20=digits). */
  function isRgbCharAllowedForSize(ch, charset, sizeId, specialByChar) {
    return normalizeRgbTypedChar(ch, charset, sizeId, specialByChar) != null;
  }

  /** Normalize a newly typed RGB char for the active size; null if not allowed. */
  function normalizeRgbTypedChar(ch, charset, sizeId, specialByChar) {
    if (!ch) return null;
    if (ch === ' ') return ' ';
    if (isMappedSpecial(ch, specialByChar)) {
      const sp = specialByChar.get(ch);
      if (!isRgbSpecialAllowedForSize(sp, sizeId)) return null;
      if (sp?.insertSpace || sp?.stem === 'SPACE') return ' ';
      return ch;
    }
    const allowed = allowedCharSet(charset, sizeId);
    if (allowed.has(ch)) {
      if (/[a-zçğıöşüñ]/i.test(ch)) return toTurkishUpper(ch);
      return ch;
    }
    const up = toTurkishUpper(ch);
    if (allowed.has(up)) return up;
    const low = ch.toLocaleLowerCase('tr-TR');
    if (allowed.has(low)) return low;
    return null;
  }

  /**
   * Move a UTF-16 offset off the middle of a surrogate pair (emoji etc.).
   * selectionStart/setSelectionRange use UTF-16 units; Array.from counts code points.
   */
  function utf16SafeBoundary(str, offset) {
    const s = String(str || '');
    let i = Math.max(0, Math.min(Number(offset) || 0, s.length));
    if (i > 0 && i < s.length) {
      const prev = s.charCodeAt(i - 1);
      const cur = s.charCodeAt(i);
      // High surrogate + low surrogate → sitting between the pair; jump past it.
      if (prev >= 0xd800 && prev <= 0xdbff && cur >= 0xdc00 && cur <= 0xdfff) i += 1;
    }
    return i;
  }

  /** UTF-16 unit offset after `codePointCount` Unicode code points in str. */
  function utf16OffsetForCodePoints(str, codePointCount) {
    const cps = Array.from(String(str || ''));
    const n = Math.max(0, Math.min(Number(codePointCount) || 0, cps.length));
    return cps.slice(0, n).join('').length;
  }

  /** Clamp RGB glyph count without stripping other size classes (full mixed string). */
  function clampRgbGlyphs(value, maxGlyphs) {
    const unlimited = !maxGlyphs || maxGlyphs <= 0;
    if (unlimited) return value || '';
    let glyphs = 0;
    let out = '';
    for (const ch of Array.from(value || '')) {
      if (ch === ' ') {
        out += ch;
        continue;
      }
      if (glyphs >= maxGlyphs) continue;
      out += ch;
      glyphs += 1;
    }
    return out;
  }

  /**
   * Legacy: keep only chars valid for selected size.
   * Mixed RGB preview/price/cart must NOT use this — use clampRgbGlyphs + natural size.
   */
  function sanitizeRgbInput(value, maxGlyphs, charset, sizeId, specialByChar) {
    const unlimited = !maxGlyphs || maxGlyphs <= 0;
    let glyphs = 0;
    let out = '';
    for (const ch of Array.from(value || '')) {
      const next = normalizeRgbTypedChar(ch, charset, sizeId, specialByChar);
      if (next == null) continue;
      if (next === ' ') {
        out += next;
        continue;
      }
      if (!unlimited && glyphs >= maxGlyphs) continue;
      out += next;
      glyphs += 1;
    }
    return out;
  }

  /**
   * RGB shared textarea: keep chars already in the box across size switches;
   * only block newly inserted chars that the active size does not allow.
   */
  function filterRgbInputChange(prevValue, nextValue, maxGlyphs, charset, sizeId, specialByChar) {
    const prevCounts = new Map();
    for (const ch of Array.from(prevValue || '')) {
      prevCounts.set(ch, (prevCounts.get(ch) || 0) + 1);
    }
    const unlimited = !maxGlyphs || maxGlyphs <= 0;
    let glyphs = 0;
    let out = '';
    for (const ch of Array.from(nextValue || '')) {
      const typed = normalizeRgbTypedChar(ch, charset, sizeId, specialByChar);
      if (typed != null) {
        if (typed !== ' ') {
          if (!unlimited && glyphs >= maxGlyphs) continue;
          glyphs += 1;
        }
        out += typed;
        if ((prevCounts.get(ch) || 0) > 0) prevCounts.set(ch, prevCounts.get(ch) - 1);
        else if ((prevCounts.get(typed) || 0) > 0) prevCounts.set(typed, prevCounts.get(typed) - 1);
        continue;
      }
      // Retain prior text invalid for the new size (do not wipe on size change).
      const retained = prevCounts.get(ch) || 0;
      if (retained > 0) {
        prevCounts.set(ch, retained - 1);
        if (ch !== ' ') {
          if (!unlimited && glyphs >= maxGlyphs) continue;
          glyphs += 1;
        }
        out += ch;
      }
    }
    return out;
  }

  /** Bucket mixed RGB glyphs by natural size for price + cart line splits. */

  /** Magnetic h170: red is digits-only; white allows letters+digits. */
  function magneticColorAllowsChar(ch, colorId, charset, sizeId, specialByChar) {
    if (!ch) return false;
    if (ch === ' ') return true;
    const color = String(colorId || 'white').toLowerCase();
    if (color === 'red') return /^[0-9]$/.test(ch);
    const allowed = allowedCharSet(charset, sizeId);
    if (allowed.has(ch)) return true;
    const up = typeof toTurkishUpper === 'function' ? toTurkishUpper(ch) : String(ch).toUpperCase();
    if (up && allowed.has(up)) return true;
    const low = typeof toTurkishLower === 'function' ? toTurkishLower(ch) : String(ch).toLowerCase();
    if (low && allowed.has(low)) return true;
    if (isMappedSpecial(ch, specialByChar) && isSpecialAllowedForMagnetic(specialByChar.get(ch), sizeId)) {
      return true;
    }
    return false;
  }

  function magneticSupportsMixedColors(config, sizeId) {
    const size = (config?.sizes || []).find((s) => s.id === sizeId);
    if (!size) return false;
    const colors = size.colors || [];
    return colors.includes('red') && colors.includes('white');
  }

  function normalizeMagneticTypedChar(ch, colorId, charset, sizeId, specialByChar) {
    if (!ch) return null;
    if (ch === ' ') return ' ';
    const color = String(colorId || 'white').toLowerCase();
    if (color === 'red') return /^[0-9]$/.test(ch) ? ch : null;
    const allowed = allowedCharSet(charset, sizeId);
    if (allowed.has(ch)) return ch;
    const up = typeof toTurkishUpper === 'function' ? toTurkishUpper(ch) : String(ch).toUpperCase();
    if (up && allowed.has(up)) return up;
    const low = typeof toTurkishLower === 'function' ? toTurkishLower(ch) : String(ch).toLowerCase();
    if (low && allowed.has(low)) return low;
    if (isMappedSpecial(ch, specialByChar) && isSpecialAllowedForMagnetic(specialByChar.get(ch), sizeId)) {
      return ch;
    }
    return null;
  }

  /**
   * Keep existing glyphs (any color) when switching swatches; only gate newly typed
   * chars against the active color (red=digits, white=letters+digits).
   */
  function filterMagneticColorInput(
    prevValue,
    prevColors,
    nextValue,
    activeColorId,
    maxChars,
    charset,
    sizeId,
    specialByChar
  ) {
    const prevChars = Array.from(prevValue || '');
    const prevCols = Array.from(prevColors || []);
    while (prevCols.length < prevChars.length) prevCols.push('white');
    const prevQueue = prevChars.map((ch, i) => ({ ch, color: prevCols[i] || 'white' }));
    const unlimited = !maxChars || maxChars <= 0;
    let glyphs = 0;
    let out = '';
    const outColors = [];
    for (const ch of Array.from(nextValue || '')) {
      // Prefer retaining a matching prior unit (same char) to keep its color.
      let retainedIdx = -1;
      for (let i = 0; i < prevQueue.length; i += 1) {
        if (prevQueue[i].ch === ch) {
          retainedIdx = i;
          break;
        }
      }
      if (retainedIdx >= 0) {
        const unit = prevQueue.splice(retainedIdx, 1)[0];
        if (ch !== ' ') {
          if (!unlimited && glyphs >= maxChars) continue;
          glyphs += 1;
        }
        out += unit.ch;
        outColors.push(unit.color);
        continue;
      }
      const typed = normalizeMagneticTypedChar(ch, activeColorId, charset, sizeId, specialByChar);
      if (typed == null) continue;
      if (typed !== ' ') {
        if (!unlimited && glyphs >= maxChars) continue;
        glyphs += 1;
      }
      out += typed;
      outColors.push(String(activeColorId || 'white').toLowerCase());
    }
    return { text: out, colors: outColors };
  }

  function rgbGlyphSizeBuckets(text, charset, specialByChar) {
    const buckets = {
      h120: { letters: 0, hearts: 0, glyphs: 0 },
      h170: { glyphs: 0 },
      h200: { glyphs: 0 },
      spaces: 0,
    };
    for (const ch of Array.from(text || '')) {
      if (ch === ' ') {
        buckets.spaces += 1;
        continue;
      }
      const sid = rgbNaturalSizeIdForChar(ch, charset, specialByChar) || 'h120';
      if (sid === 'h200') {
        buckets.h200.glyphs += 1;
      } else if (sid === 'h170') {
        buckets.h170.glyphs += 1;
      } else if (isHeartSpecialChar(ch, specialByChar)) {
        buckets.h120.hearts += 1;
        buckets.h120.glyphs += 1;
      } else {
        buckets.h120.letters += 1;
        buckets.h120.glyphs += 1;
      }
    }
    return buckets;
  }

  function rgbSpacerPriceCents(addons, sizeId) {
    const map = addons?.spacerPrices || {};
    const key = String(sizeId || '').toLowerCase();
    const cents = Number(map[key] || 0);
    return Number.isFinite(cents) ? cents : 0;
  }

  function rgbControllerPriceCents(addons) {
    const cents = Number(addons?.controllerPrice || 0);
    return Number.isFinite(cents) ? cents : 0;
  }

  function isRailSize(charset, sizeId) {
    const mode = sizeEntry(charset, sizeId)?.mode;
    return mode === 'rail' || mode === 'floor';
  }

  function isMetricSize(charset, sizeId) {
    return isRailSize(charset, sizeId);
  }

  function railStockMm(widthMm) {
    const stocks = [600, 900, 1200, 1500, 2000];
    const need = Number(widthMm) || 0;
    if (need <= 0) return 0;
    return stocks.find((stock) => stock >= need) || stocks[stocks.length - 1];
  }

  /** Paid sliding glyphs. Spaces keep their width for the rod, and are not billed as letters. */
  function slidingBillable(text, charset, sizeId, specialByChar) {
    let paid = 0;
    let spaces = 0;
    for (const ch of Array.from(text || '')) {
      if (ch === ' ') {
        spaces += 1;
        continue;
      }
      if (railMetricsForChar(charset, sizeId, ch, specialByChar)) paid += 1;
    }
    return { paid, spaces };
  }

  function slidingGlyphUsd(ch, sizeId, prices, specialByChar) {
    if (!prices || ch === ' ') return 0;
    const letters = prices.letters || {};
    const sizeUsd = Number(letters[sizeId]);
    const sp = specialByChar && typeof specialByChar.get === 'function' ? specialByChar.get(ch) : null;
    const stem = sp?.stem || '';
    if (stem === 'KARE_LOGO' || stem === 'YUVARLAK_LOGO') {
      const screen = Number(prices.screens);
      if (Number.isFinite(screen)) return screen;
    }
    return Number.isFinite(sizeUsd) ? sizeUsd : 0;
  }

  function slidingAdapterUsd(adapterKey, prices) {
    if (adapterKey !== '3a' && adapterKey !== '5a') return null;
    const usd = Number(prices?.adapters?.[adapterKey]);
    return Number.isFinite(usd) ? usd : null;
  }

  function railMetricsForChar(charset, sizeId, ch, specialByChar) {
    const entry = sizeEntry(charset, sizeId);
    if (!entry) return null;
      if (isMappedSpecial(ch, specialByChar)) {
      const sp = specialByChar.get(ch);
      const fromStem = entry.metrics?.[sp.stem] || entry.metrics?.[ch];
      if (fromStem) return fromStem;
      return { widthMm: Number(sp.widthMm) || 0, powerW: Number(sp.powerW) || 0 };
    }
    if (entry.metrics?.[ch]) return entry.metrics[ch];
    const stem = stemForChar(charset, sizeId, ch);
    return (stem && entry.metrics?.[stem]) || null;
  }

  function measureRailText(text, charset, sizeId, specialByChar) {
    let widthMm = 0;
    let powerW = 0;
    let count = 0;
    for (const ch of Array.from(text || '')) {
      const met = railMetricsForChar(charset, sizeId, ch, specialByChar);
      if (!met) continue;
      widthMm += Number(met.widthMm) || 0;
      powerW += Number(met.powerW) || 0;
      count += 1;
    }
    return { widthMm, powerW, count };
  }

  /** Magnetic power connector (preview = L25 asset) footprint in mm. */
  function magneticConnectorWidthMm(charset, sizeId) {
    const entry = sizeEntry(charset, sizeId);
    const pick = (m) => {
      if (m == null) return 0;
      if (typeof m === 'number') return Number(m) || 0;
      return Number(m.widthMm) || 0;
    };
    return (
      pick(entry?.metrics?.KONNEKTOR) ||
      pick(entry?.metrics?.connector) ||
      pick(charset?.connectorMetrics) ||
      pick(charset?.baseMetrics?.L25) ||
      pick(charset?.baseMetrics?.controller) ||
      25
    );
  }

  function measureMagneticText(text, charset, sizeId, specialByChar, opts = {}) {
    const base = measureRailText(text, charset, sizeId, specialByChar);
    if (opts.includeConnector) {
      base.widthMm += magneticConnectorWidthMm(charset, sizeId);
    }
    return base;
  }

  /** RGB magnetic base widths (merchant mm table). Display as cm (÷10). */
  function rgbMetricsMap(charset, sizeId) {
    const entry = sizeEntry(charset, sizeId);
    return charset?.baseMetrics || entry?.metrics || {};
  }

  function rgbWidthMmForChar(charset, sizeId, ch, specialByChar) {
    const metrics = rgbMetricsMap(charset, sizeId);
    const pick = (...keys) => {
      for (const k of keys) {
        if (k == null) continue;
        const m = metrics[k];
        if (m && Number(m.widthMm) > 0) return Number(m.widthMm);
        if (typeof m === 'number' && m > 0) return Number(m);
      }
      return 0;
    };
    if (ch === ' ') return pick(' ', 'SPACE') || 35;
    if (isMappedSpecial(ch, specialByChar)) {
      const sp = specialByChar.get(ch);
      return pick(sp?.stem, ch, normalizeRgbStem(sp?.stem)) || Number(sp?.widthMm) || 0;
    }
    const up = toTurkishUpper(ch);
    return pick(ch, up);
  }

  function measureRgbText(text, charset, sizeId, specialByChar, opts = {}) {
    let widthMm = 0;
    let count = 0;
    for (const ch of Array.from(text || '')) {
      const w = rgbWidthMmForChar(charset, sizeId, ch, specialByChar);
      if (!w) continue;
      widthMm += w;
      count += 1;
    }
    if (opts.includeController) {
      const metrics = rgbMetricsMap(charset, sizeId);
      const ctrl =
        Number(metrics.L25?.widthMm || metrics.controller?.widthMm || metrics.L25 || metrics.controller) ||
        25;
      widthMm += ctrl;
    }
    return { widthMm, count, lengthCm: widthMm / 10 };
  }

  const FLOOR_SUPPLY_WIDTH_MM = 220;

  function floorDisplayWidthMm(measure, plan) {
    let width = Number(measure?.widthMm) || 0;
    if (plan?.floor && plan.includeAdapter) {
      const supplies = Array.isArray(plan.floorSupplies) ? plan.floorSupplies.length : 0;
      width += FLOOR_SUPPLY_WIDTH_MM * Math.max(1, supplies);
    }
    return width;
  }

  function formatLengthCm(widthMm) {
    const cm = Number(widthMm || 0) / 10;
    return `${cm.toFixed(1).replace(/\.0$/, '')} cm`;
  }

  function sanitizeRailInput(value, maxChars, charset, sizeId, specialByChar, maxWidthMm) {
    const allowed = allowedCharSet(charset, sizeId);
    const maxW = Number(maxWidthMm) || 2000;
    let out = '';
    let width = 0;
    for (const ch of Array.from(value || '')) {
      let next = null;
      if (isMappedSpecial(ch, specialByChar)) next = ch;
      else if (ch === ' ' && (allowed.has(' ') || isRailSize(charset, sizeId))) next = ' ';
      else if (allowed.has(ch)) next = ch;
      else {
        const up = toTurkishUpper(ch);
        if (allowed.has(up)) next = up;
      }
      if (next == null) continue;
      const met = railMetricsForChar(charset, sizeId, next, specialByChar);
      if (!met) continue;
      const w = Number(met.widthMm) || 0;
      if (width + w > maxW + 0.01) break;
      if (Array.from(out).length >= maxChars) break;
      out += next;
      width += w;
    }
    return out;
  }

  function adapterKeyForPower(powerW, rules) {
    const list = (Array.isArray(rules) && rules.length
      ? rules
      : [
          { maxW: 36, adapter: '3a' },
          { maxW: 60, adapter: '5a' },
          { maxW: 9999, adapter: '60w' },
        ]
    )
      .slice()
      .sort((a, b) => Number(a.maxW) - Number(b.maxW));
    const n = Math.max(0, Number(powerW) || 0);
    for (const rule of list) {
      if (n <= Number(rule.maxW)) return rule.adapter;
    }
    return list[list.length - 1]?.adapter || '60w';
  }

  /** Build theme asset URL; never keep sample file ?v= (causes cross-PC stale/wrong glyphs). */
  function assetUrlFor(sampleUrl, fileName, cacheToken) {
    if (!fileName) return '';
    if (!sampleUrl) return '';
    let url = '';
    if (/letter-[^/?#]+\.png/i.test(sampleUrl)) {
      url = sampleUrl.replace(/letter-[^/?#]+\.png/i, fileName);
    } else {
      try {
        const u = new URL(sampleUrl, window.location.href);
        const parts = u.pathname.split('/');
        parts[parts.length - 1] = fileName;
        u.pathname = parts.join('/');
        url = u.toString();
      } catch (e) {
        return '';
      }
    }
    try {
      const u = new URL(url, window.location.href);
      let shopifyV = '';
      try {
        shopifyV = new URL(sampleUrl, window.location.href).searchParams.get('v') || '';
      } catch (err) {
        shopifyV = '';
      }
      const token = cacheToken != null ? String(cacheToken) : '';
      // Mix charset token + Liquid asset_url v so replacing a PNG busts CDN/browser cache.
      u.searchParams.set('v', `lc${token}-${shopifyV}-${fileName.length}`);
      return u.toString();
    } catch (e) {
      return url;
    }
  }


  /** Magnetic color folders: letter-white-BUYUK_A.png / letter-yellow-A.png. */
  function applyMagneticColorPrefix(fileName, color) {
    if (!fileName || !color?.prefix) return fileName;
    if (fileName.startsWith('letter-rgb-')) return fileName;
    if (fileName.startsWith('letter-rail-') || fileName.startsWith('letter-floor-')) return fileName;
    if (fileName.startsWith('letter-white-') || fileName.startsWith('letter-yellow-') || fileName.startsWith('letter-daylight-') || fileName.startsWith('letter-red-')) {
      return fileName;
    }
    if (fileName.startsWith('letter-')) {
      return String(color.prefix) + fileName.slice('letter-'.length);
    }
    return fileName;
  }


  
  /** Magnetic letters share one PNG set across 95/120/170/220 — strip size from filename. */
  function sharedMagneticFileName(fileName, config) {
    if (!fileName || config?.craftMode === 'rgb') return fileName;
    if (config?.craftMode && config.craftMode !== 'magnetic' && config.craftMode !== 'all') {
      // rail/floor keep size-specific assets
      if (config.craftMode === 'rail' || config.craftMode === 'floor') return fileName;
    }
    if (
      fileName.startsWith('letter-rgb-') ||
      fileName.startsWith('letter-rail-') ||
      fileName.startsWith('letter-floor-')
    ) {
      return fileName;
    }
    // Size-locked specials/logos live only as letter-{mm}-{STEM}.png — never strip.
    // (KALP, YILBASI_AGACI, BARDAK, KARE_LOGO, YUVARLAK_LOGO*, ESKI, …)
    if (
      /letter-(?:white-|yellow-|daylight-|red-)?(?:95|120|135|140|155|170|220)-(?!BUYUK_|KUCUK_|[A-Z0-9]\.png$)[A-Z0-9_]+\.png$/i.test(
        fileName
      )
    ) {
      return fileName;
    }
    // letter-120-BUYUK_A.png → letter-BUYUK_A.png
    // letter-white-95-A.png → letter-white-A.png
    return String(fileName).replace(
      /^(letter-(?:white-|yellow-|daylight-|red-)?)\d+-/,
      '$1'
    );
  }

function isRgbAssetConfig(config) {
    return config?.craftMode === 'rgb' || config?.forceRgbAssets === true;
  }

  function rgbSafeFileName(fileName, config) {
    if (!fileName) return fileName;
    if (!isRgbAssetConfig(config)) return fileName;
    // RGB craft must never load magnetic/rail hearts (e.g. letter-120-KALP.png)
    if (fileName.startsWith('letter-rgb-')) return fileName;
    if (fileName.startsWith('letter-')) {
      return 'letter-rgb-' + fileName.slice('letter-'.length);
    }
    return fileName;
  }

  /** Prefer PNG glyph URL; empty string means caller may use text fallback. */
  function glyphAssetUrl(sampleUrl, charset, sizeId, ch, specialByChar, config) {
    const color = arguments.length > 6 ? arguments[6] : null;
    if (isMappedSpecial(ch, specialByChar)) {
      const sp = specialByChar.get(ch);
      const isMagneticGlyph =
        !isRgbAssetConfig(config) &&
        (config?.craftMode === 'magnetic' ||
          (charset?.sharedMagneticAssets &&
            config?.craftMode !== 'rail' &&
            config?.craftMode !== 'floor'));
      // Magnetic specials: keep size-locked PNGs (letter-120-KALP.png, logos, …).
      // Do not strip mm or force white/yellow prefixes — those files do not exist.
      if (sp?.file && isMagneticGlyph) {
        const file = specialPickerFileName(sp, config) || sp.file;
        return assetUrlFor(sampleUrl, file, charset?.version);
      }
      // Magnetic Unicode symbols (♥) mapped via charset chars → fall through to stem.
      const symbolStem =
        charset?.symbolStemByChar?.[ch] ||
        (sp?.stem && charForSpecialStem(charset, sp.stem) === ch ? sp.stem : null);
      if (symbolStem && isMagneticGlyph) {
        // fall through to stem resolution below
      } else if (sp?.file) {
        let file = sp.file;
        if (isMagneticGlyph) {
          file = sharedMagneticFileName(file, config) || file;
          const colored = applyMagneticColorPrefix(file, color);
          if (colored) file = sharedMagneticFileName(colored, config) || colored;
        } else {
          file = rgbSafeFileName(file, config);
        }
        return assetUrlFor(sampleUrl, file, charset?.version);
      }
    }
    let stem = stemForChar(charset, sizeId, ch);
    if (!stem) {
      const up = toTurkishUpper(ch);
      stem =
        charset?.symbolStemByChar?.[ch] ||
        charset?.symbolStemByChar?.[up] ||
        (stemForChar(charset, sizeId, up) || null);
      if (!stem) {
        const candidate = up || ch;
        if (/^[A-Z0-9]$/.test(candidate)) stem = candidate;
      }
    }
    if (!stem && isMappedSpecial(ch, specialByChar)) {
      stem = specialByChar.get(ch)?.stem || null;
    }
    if (!stem) return '';
    // RGB merchant aliases only (AG_AC_→AGAC, VE→ET on RGB packs). Magnetic keeps VE vs ET separate.
    if (isRgbAssetConfig(config)) stem = normalizeRgbStem(stem);
    else if (usesSharedMagneticAssets(charset, config)) {
      // A / a → same PNG (KUCUK_* folded into BUYUK_*; short S via error fallback)
      stem = normalizeMagneticLetterStem(stem);
    } else stem = String(stem);
    // Shared spacer asset for space glyphs (not size-specific SPACE PNGs)
    if (stem === 'SPACE' || ch === ' ') {
      const shared =
        (charset?.sharedAssets && charset.sharedAssets.spacer) ||
        'letter-rgb-SPACE.png';
      return assetUrlFor(sampleUrl, rgbSafeFileName(shared, config), charset?.version);
    }
    // Magnetic letters: latest shared pack (any size tab). Specials already returned above.
    if (usesSharedMagneticAssets(charset, config)) {
      const sharedFile = magneticSharedLetterFile(stem, color);
      if (sharedFile) return assetUrlFor(sampleUrl, sharedFile, charset?.version);
    }
    let resolved = assetFileFor(charset, sizeId, stem);
    // Legacy stems (AG_AC_/C_I_FT/VE) → also resolve via AGAC/CIFT/ET files
    if (!resolved) {
      for (const [legacy, canon] of Object.entries(RGB_STEM_ALIASES)) {
        if (canon === stem && legacy !== canon) {
          resolved = assetFileFor(charset, sizeId, legacy);
          if (resolved) break;
        }
      }
    }
    if (resolved) {
      resolved = sharedMagneticFileName(resolved, config) || resolved;
      const colored = applyMagneticColorPrefix(resolved, color);
      if (colored && colored !== resolved) {
        const sharedColored = sharedMagneticFileName(colored, config) || colored;
        return assetUrlFor(sampleUrl, sharedColored, charset?.version);
      }
      return assetUrlFor(sampleUrl, rgbSafeFileName(resolved, config), charset?.version);
    }
    const mm = mmForSize(config, sizeId);
    let pattern = charset?.assetPattern || 'letter-{mm}-{stem}.png';
    // Magnetic: one photo set for all sizes (95/120/170/220)
    if (
      !isRgbAssetConfig(config) &&
      (config?.craftMode === 'magnetic' ||
        (charset?.sharedMagneticAssets &&
          config?.craftMode !== 'rail' &&
          config?.craftMode !== 'floor'))
    ) {
      pattern = charset?.magneticAssetPattern || 'letter-{stem}.png';
    }
    // RGB craft must never emit magnetic letter-{mm}-*.png paths
    if (isRgbAssetConfig(config) && !String(pattern).includes('letter-rgb-')) {
      pattern = 'letter-rgb-{mm}-{stem}.png';
    }
    let fileName = pattern.replace('{mm}', mm).replace('{stem}', stem);
    fileName = applyMagneticColorPrefix(fileName, color) || fileName;
    fileName = sharedMagneticFileName(fileName, config) || fileName;
    return assetUrlFor(sampleUrl, rgbSafeFileName(fileName, config), charset?.version);
  }


  const glyphCropCache = new Map();

  const cropOpaqueToSquareDataUrl = (img) => {
    try {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return null;
      const maxScan = 360;
      const scale = Math.min(1, maxScan / Math.max(nw, nh));
      const sw = Math.max(1, Math.round(nw * scale));
      const sh = Math.max(1, Math.round(nh * scale));
      const scan = document.createElement('canvas');
      scan.width = sw;
      scan.height = sh;
      const sctx = scan.getContext('2d', { willReadFrequently: true });
      if (!sctx) return null;
      sctx.drawImage(img, 0, 0, sw, sh);
      const data = sctx.getImageData(0, 0, sw, sh).data;
      let minX = sw;
      let minY = sh;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < sh; y += 1) {
        for (let x = 0; x < sw; x += 1) {
          const i = (y * sw + x) * 4;
          const a = data[i + 3];
          const lum = data[i] + data[i + 1] + data[i + 2];
          // Ignore transparent + near-black stage background
          if (a < 28 || lum < 45) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX <= minX || maxY <= minY) return null;
      const pad = Math.round(Math.max(maxX - minX, maxY - minY) * 0.08);
      const sx = Math.max(0, Math.floor((minX - pad) / scale));
      const sy = Math.max(0, Math.floor((minY - pad) / scale));
      const ex = Math.min(nw, Math.ceil((maxX + pad) / scale));
      const ey = Math.min(nh, Math.ceil((maxY + pad) / scale));
      const cw = Math.max(1, ex - sx);
      const ch = Math.max(1, ey - sy);
      const side = Math.max(cw, ch);
      const out = document.createElement('canvas');
      out.width = side;
      out.height = side;
      const octx = out.getContext('2d');
      if (!octx) return null;
      octx.clearRect(0, 0, side, side);
      octx.drawImage(img, sx, sy, cw, ch, Math.round((side - cw) / 2), Math.round((side - ch) / 2), cw, ch);
      return out.toDataURL('image/png');
    } catch (err) {
      return null;
    }
  };

  const applyCroppedGlyph = (img, sourceUrl) => {
    if (!img || img.dataset.cropped === '1') return;
    const cached = sourceUrl ? glyphCropCache.get(sourceUrl) : null;
    if (cached) {
      img.dataset.cropped = '1';
      img.src = cached;
      return;
    }
    const dataUrl = cropOpaqueToSquareDataUrl(img);
    if (!dataUrl) return;
    if (sourceUrl) glyphCropCache.set(sourceUrl, dataUrl);
    img.dataset.cropped = '1';
    img.src = dataUrl;
  };






  const DEFAULT_USD_TRY_RATE = 48.85;

  function usdTryRate(config) {
    const n = Number(config?.usdTryRate);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_USD_TRY_RATE;
  }

  /** Convert USD list price → Shopify money cents (TRY). */
  function usdToTryCents(usd, config) {
    const n = Number(usd);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * usdTryRate(config) * 100);
  }

  /** P3 floor supply. Spaces do not count. RGB: 1–8 → 600W, 9+ → 1000W. White: 1–12 → 350W, 13–20 → 600W. */
  function floorSupplyKey(colorId, glyphs) {
    const n = Math.max(0, Number(glyphs) || 0);
    if (n <= 0) return null;
    if (colorId === 'rgb') return n >= 9 ? 'rgb1000' : 'rgb600';
    return n >= 13 ? 'white600' : 'white350';
  }

  function floorSupplyWatts(key) {
    return { white350: 350, white600: 600, rgb600: 600, rgb1000: 1000 }[key] || 0;
  }

function floorSupplyLabel(key) {
  const watts = floorSupplyWatts(key);
  return watts ? `${watts}W` : '';
}

/** Per-character floor colors. Spaces are not a color. Missing entries follow fallback. */
function floorGlyphColors(text, charColors, fallback) {
  const chars = Array.from(text || '');
  const cols = Array.from(charColors || []);
  const fb = fallback === 'rgb' ? 'rgb' : 'white';
  return chars.map((ch, i) => {
    if (ch === ' ') return 'white';
    return cols[i] === 'rgb' ? 'rgb' : cols[i] === 'white' ? 'white' : fb;
  });
}

function floorColorCounts(text, charColors, fallback) {
  const chars = Array.from(text || '');
  const cols = floorGlyphColors(text, charColors, fallback);
  let white = 0;
  let rgb = 0;
  chars.forEach((ch, i) => {
    if (ch === ' ') return;
    if (cols[i] === 'rgb') rgb += 1;
    else white += 1;
  });
  return { white, rgb, colors: cols };
}

function sameFloorChar(a, b) {
  if (a === b) return true;
  if (a === ' ' || b === ' ') return false;
  return toTurkishUpper(a) === toTurkishUpper(b);
}

/**
 * Keep each existing floor glyph's color by position.
 * Matching the first identical letter (every E shares one color) repaints the
 * Renkli E when a later Beyaz E is typed. Prefix/suffix alignment leaves
 * untouched glyphs alone and paints only the inserted run with the active swatch.
 * White is capped at 20.
 */
function filterFloorColorInput(prevValue, prevColors, nextValue, activeColorId, maxChars) {
  const active = activeColorId === 'rgb' ? 'rgb' : 'white';
  const prevChars = Array.from(prevValue || '');
  const prevCols = floorGlyphColors(prevValue, prevColors, 'white');
  const nextChars = Array.from(nextValue || '');
  let head = 0;
  while (
    head < prevChars.length &&
    head < nextChars.length &&
    sameFloorChar(prevChars[head], nextChars[head])
  ) {
    head += 1;
  }
  let tail = 0;
  while (
    tail < prevChars.length - head &&
    tail < nextChars.length - head &&
    sameFloorChar(
      prevChars[prevChars.length - 1 - tail],
      nextChars[nextChars.length - 1 - tail]
    )
  ) {
    tail += 1;
  }
  const aligned = nextChars.map((ch, i) => {
    if (i < head) {
      const kept = prevCols[i] === 'rgb' ? 'rgb' : 'white';
      return { ch, color: ch === ' ' ? 'white' : kept };
    }
    if (i >= nextChars.length - tail) {
      const prevIndex = prevChars.length - (nextChars.length - i);
      const kept = prevCols[prevIndex] === 'rgb' ? 'rgb' : 'white';
      return { ch, color: ch === ' ' ? 'white' : kept };
    }
    return { ch, color: ch === ' ' ? 'white' : active };
  });
  const unlimited = !maxChars || maxChars <= 0;
  let glyphs = 0;
  let white = 0;
  let out = '';
  const outColors = [];
  let hitWhiteCap = false;
  aligned.forEach(({ ch, color: glyphColor }) => {
    let color = glyphColor;
    if (ch === ' ') {
      out += ch;
      outColors.push('white');
      return;
    }
    if (!unlimited && glyphs >= maxChars) return;
    if (color !== 'rgb') {
      if (white >= 20) {
        hitWhiteCap = true;
        return;
      }
      white += 1;
      color = 'white';
    }
    glyphs += 1;
    out += ch;
    outColors.push(color);
  });
  return { text: out, colors: outColors, hitWhiteCap };
}

  function magneticPriceTables(config) {
    // Prefer LetterCraftConfig.pricesUsd.magnetic (liquid); fallback magneticPricesUsd
    return config?.pricesUsd?.magnetic || config?.magneticPricesUsd || null;
  }

  function magneticLetterUsd(config, colorId, sizeId) {
    const tables = magneticPriceTables(config);
    if (!tables) return null;
    const size = String(sizeId || '').toLowerCase();
    const letters = tables.letters || {};
    // Shape A: { white: { h95: 12 }, daylight: { h95: 12 } }
    // Shape B: { h95: 12, h120: 20 } (current liquid)
    const color = String(colorId || 'white').toLowerCase();
    if (letters[color] && typeof letters[color] === 'object' && !Array.isArray(letters[color])) {
      const byColor = letters[color] || letters.white || {};
      if (byColor[size] != null) return Number(byColor[size]);
      if (color === 'daylight' && letters.white?.[size] != null) return Number(letters.white[size]);
      return null;
    }
    if (letters[size] != null) return Number(letters[size]);
    // daylight/white share same letter tiers in shape B
    return null;
  }

  function magneticSpecialUsd(config, stem, sizeId) {
    if (!stem) return null;
    const key = String(stem).toUpperCase();
    const tables = magneticPriceTables(config);
    if (!tables) return null;
    const size = String(sizeId || '').toLowerCase();
    // liquid: specialsByStem; legacy: specials / specialsBySize
    const byStem = tables.specialsByStem || tables.specials || {};
    const bySize = tables.specialsBySize || {};
    const entry = byStem[key] != null ? byStem[key] : bySize[key];
    if (entry != null && typeof entry === 'object') {
      if (entry[size] != null) return Number(entry[size]);
      if ((size === 'h220' || size === 'h200') && entry.h220 != null) return Number(entry.h220);
      if (entry.h135 != null) return Number(entry.h135);
      if (entry.default != null) return Number(entry.default);
      return null;
    }
    if (entry != null) return Number(entry);
    if ((key === 'AGAC' || key === 'YILBASI_AGACI') && (byStem.YILBASI_AGACI != null || byStem.AGAC != null)) {
      const v = byStem.YILBASI_AGACI != null ? byStem.YILBASI_AGACI : byStem.AGAC;
      return Number(typeof v === 'object' ? v.default ?? v.h170 ?? Object.values(v)[0] : v);
    }
    return null;
  }

  function magneticAddonUsd(config, kind, adapterKey) {
    const tables = magneticPriceTables(config) || {};
    const addons = tables.addons || tables;
    if (kind === 'connector') return addons.connector != null ? Number(addons.connector) : null;
    if (kind === 'spacer') return addons.spacer != null ? Number(addons.spacer) : null;
    if (kind === 'adapter') {
      const map = addons.adapters || {};
      const k = String(adapterKey || '').toLowerCase();
      return map[k] != null ? Number(map[k]) : null;
    }
    if (kind === 'shelf') {
      const map = addons.shelves || {};
      return map[adapterKey] != null ? Number(map[adapterKey]) : null;
    }
    return null;
  }

  const MAGNETIC_SHELF_IDS = ['clearWall', 'clearGlass', 'lip415', 'lip525'];
  const MAGNETIC_SHELF_INFO = {
    clearWall: {
      title: 'Şeffaf raf (duvar)',
      body: 'Duvara vida ile monte edilen şeffaf akrilik raf. Manyetik harfler rafa oturur; duvar yüzeyleri için uygundur.',
    },
    clearGlass: {
      title: 'Şeffaf raf (cam)',
      body: 'Cam, vitrin veya cam bölme üzerine uygulanan şeffaf akrilik raf. Pencere ve cam yüzeyler için uygundur.',
    },
    lip: {
      title: 'Çıkıntılı raf',
      body: 'Harflerin öne çıkması için çıkıntılı raf. 41,5 cm ve 52,5 cm uzunluk seçenekleri vardır. Duvara monte edilir; harfler rafın üzerinde durur.',
    },
  };
  const MAGNETIC_SHELF_LABELS = {
    clearWall: {
      off: 'Şeffaf Raf (50 Cm) Duvar İçin',
      on: 'Şeffaf Raf (50 Cm) Duvar İçin — Kaldırmak İçin Tıklayın',
      cart: 'Şeffaf Raf (Duvar)',
      cm: '50 Cm',
    },
    clearGlass: {
      off: 'Şeffaf Raf (50 Cm) Cam İçin',
      on: 'Şeffaf Raf (50 Cm) Cam İçin — Kaldırmak İçin Tıklayın',
      cart: 'Şeffaf Raf (Cam)',
      cm: '50 Cm',
    },
    lip415: {
      off: 'Çıkıntılı Raf (41,5 Cm)',
      on: 'Çıkıntılı Raf (41,5 Cm) — Kaldırmak İçin Tıklayın',
      cart: 'Çıkıntılı Raf 41,5 Cm',
      cm: '41,5 Cm',
    },
    lip525: {
      off: 'Çıkıntılı Raf (52,5 Cm)',
      on: 'Çıkıntılı Raf (52,5 Cm) — Kaldırmak İçin Tıklayın',
      cart: 'Çıkıntılı Raf 52,5 Cm',
      cm: '52,5 Cm',
    },
  };

  function paintShelfLabel(el, text, cm) {
    if (!el) return;
    if (!cm || !String(text).includes(cm)) {
      el.textContent = text || '';
      return;
    }
    const i = String(text).indexOf(cm);
    const strong = document.createElement('strong');
    strong.className = 'letter-craft__shelf-cm';
    strong.textContent = cm;
    el.replaceChildren(
      document.createTextNode(String(text).slice(0, i)),
      strong,
      document.createTextNode(String(text).slice(i + cm.length))
    );
  }

  function emptyMagneticShelves() {
    return { clearWall: false, clearGlass: false, lip415: false, lip525: false };
  }

  /** Per-glyph magnetic total in TRY cents from USD tables (spaces use spacer USD). */
  function magneticTextPriceCents(text, config, colorId, sizeId, specialByChar, charset) {
    const letterUsd = magneticLetterUsd(config, colorId, sizeId);
    let total = 0;
    let letters = 0;
    let specials = 0;
    let spaces = 0;
    for (const ch of Array.from(text || '')) {
      if (ch === ' ') {
        spaces += 1;
        continue;
      }
      let priced = false;
      if (isMappedSpecial(ch, specialByChar)) {
        const sp = specialByChar.get(ch);
        const stem = String(sp?.stem || '').toUpperCase();
        const usd = magneticSpecialUsd(config, stem, sp?.sizeId || sizeId);
        if (usd != null) {
          total += usdToTryCents(usd, config);
          specials += 1;
          priced = true;
        }
      }
      if (!priced && typeof stemForChar === 'function' && charset) {
        const stem = stemForChar(charset, sizeId, ch);
        if (stem) {
          const usd = magneticSpecialUsd(config, stem, sizeId);
          // Only when stem has an explicit specials table entry (not letter glyph stems)
          if (usd != null) {
            total += usdToTryCents(usd, config);
            specials += 1;
            priced = true;
          }
        }
      }
      if (!priced && letterUsd != null) {
        total += usdToTryCents(letterUsd, config);
        letters += 1;
      }
    }
    const spacerUsd = magneticAddonUsd(config, 'spacer');
    if (spaces > 0 && spacerUsd != null) {
      total += spaces * usdToTryCents(spacerUsd, config);
    } else if (spaces > 0 && letterUsd != null) {
      total += spaces * usdToTryCents(letterUsd, config);
    }
    return { total, letters, specials, spaces, letterUsd };
  }


  function formatMoney(cents, moneyFormat) {
    if (cents == null || cents === '') return '—';
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(n, moneyFormat);
      } catch (err) {
        /* fall through */
      }
    }
    return `${Math.round(n / 100).toLocaleString('tr-TR')} ₺`;
  }

  /**
   * Listing / vitrin photo — alt contains "ana resim" (or main image).
   * Stays as Shopify featured/card image; never used as a DIY scene background.
   */
  function isMainListingImageAlt(alt) {
    const s = String(alt || '').toLowerCase();
    return /ana\s*resim|anaresim|main\s*image|featured\s*image|listing\s*image/.test(s);
  }

  function parseAlt(alt) {
    const raw = String(alt || '');
    const parts = raw.split('|').map((p) => p.trim()).filter(Boolean);
    const map = {};
    parts.forEach((part) => {
      const idx = part.indexOf(':');
      if (idx < 0) return;
      const key = part.slice(0, idx).trim().toLowerCase();
      const val = part.slice(idx + 1).trim();
      map[key] = val;
    });

    const applyRaw = (map.uygulama || map.apply || '').toLowerCase();
    let apply = null;
    if (/masa|desk/.test(applyRaw)) apply = 'desktop';
    else if (/duvar|wall/.test(applyRaw)) apply = 'wall';
    else if (/aski|hang/.test(applyRaw)) apply = 'hanging';

    const tipRaw = (map.tip || map.type || map.ürün || map.urun || '').toLowerCase();
    const types = [];
    if (/rgb/.test(tipRaw)) types.push('rgb');
    if (/ray|slid|rail/.test(tipRaw)) types.push('sliding');
    if (/zemin|floor|p3/.test(tipRaw)) types.push('floor');
    if (/manyet|magnet/.test(tipRaw) && !/rgb/.test(tipRaw)) types.push('magnetic');

    const sizeRaw = map['ölçü'] || map.olcu || map.size || map.sizes || '';
    const sizes = [];
    String(sizeRaw)
      .split(/[,;/+\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((token) => {
        const m = token.match(/h?\s*(\d{2,3})/i);
        if (m) sizes.push(`h${m[1]}`);
      });

    const pcTop = map['üst'] || map.ust || map.top || map.pctop || null;
    const mbTop = map['mobil üst'] || map['mobil ust'] || map.topmobile || map.mbtop || null;
    const pcLeft =
      map.soldan ||
      map['sol başlangıç'] ||
      map['sol baslangic'] ||
      map.baslangic ||
      map['başlangıç'] ||
      map.sol ||
      map.left ||
      map.yatay ||
      map.x ||
      null;
    const mbLeft =
      map['mobil soldan'] ||
      map['mobil sol başlangıç'] ||
      map['mobil sol baslangic'] ||
      map['mobil başlangıç'] ||
      map['mobil baslangic'] ||
      map['mobil sol'] ||
      map['mobil yatay'] ||
      map.leftmobile ||
      map.mbleft ||
      null;

    // Letter visual scale — shared + optional desktop/mobile overrides.
    // e.g. Boyut:0.9 | Mobil Boyut:0.7   or   Masaüstü Boyut:1.1 | Mobil Boyut:0.85
    const letterScale = parseScaleRaw(
      map.boyut || map['ölçek'] || map.olcek || map.scale || map.letterscale || map['harf boyutu'] || null
    );
    const pcLetterScale = parseScaleRaw(
      map['masaüstü boyut'] ||
        map['masaustu boyut'] ||
        map['pc boyut'] ||
        map['desktop boyut'] ||
        map['masaüstü ölçek'] ||
        map['masaustu olcek'] ||
        map['pc scale'] ||
        map['desktop scale'] ||
        map.pcboyut ||
        map.pcscale ||
        null
    );
    const mbLetterScale = parseScaleRaw(
      map['mobil boyut'] ||
        map['mobile boyut'] ||
        map['mb boyut'] ||
        map['mobil ölçek'] ||
        map['mobil olcek'] ||
        map['mobile scale'] ||
        map.mboyut ||
        map.mbscale ||
        null
    );

    return { apply, types, sizes, pcTop, mbTop, pcLeft, mbLeft, letterScale, pcLetterScale, mbLetterScale };
  }

  /** Parse Alt Boyut / Ölçek value: 1.2, 120%, 0,8 → number or null. */
  function parseScaleRaw(raw) {
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw).trim().replace(',', '.');
    if (s.endsWith('%')) {
      const n = Number(s.slice(0, -1));
      return Number.isFinite(n) && n > 0 ? n / 100 : null;
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Scene Alt Boyut / Ölçek / scale → CSS transform scale (exact number).
   * Magnetic: never invent size-tier steps (0.8/0.9/…) and never multiply size.scale.
   * Missing Alt Boyut → null (caller uses 1 for magnetic identity).
   */
  function effectiveSceneLetterScale(sceneScale) {
    const n = Number(sceneScale);
    if (Number.isFinite(n) && n > 0) return n;
    return null;
  }

  /**
   * Viewport-aware Alt Boyut for magnetic + RGB.
   * Mobil: Mobil Boyut → Boyut. Masaüstü: Masaüstü Boyut → Boyut.
   */
  function sceneLetterScaleForViewport(scene, isMobile) {
    if (!scene) return null;
    if (isMobile) {
      return scene.mbLetterScale != null ? scene.mbLetterScale : scene.letterScale;
    }
    return scene.pcLetterScale != null ? scene.pcLetterScale : scene.letterScale;
  }

  /** Magnetic preview scale: selected scene Alt Boyut only; else 1 (no size.scale). */
  function magneticPreviewScale(sceneScale) {
    const fromAlt = effectiveSceneLetterScale(sceneScale);
    return fromAlt != null ? fromAlt : 1;
  }

  /**
   * Üstten askılı preview. 15/20 cm follow the scene Boyut as authored.
   * 10 cm reuses the 15 cm photos, so letters shrink by 10/15.
   */
  function slidingPreviewScale(sceneScale, size) {
    const scene = magneticPreviewScale(sceneScale);
    if (size?.id === 'h100') return scene * (100 / 150);
    return scene;
  }

  /**
   * Mixed RGB preview scale — never follow selected size tab (h120/h170/h200).
   * Row scale stays at the 12 cm letter base (× scene Alt Boyut).
   * CSS then enlarges 17cm symbols (17/12) and 20cm digits (20/12) from that base.
   */
  /** P3: alt Boyut is the preview scale. Missing Boyut keeps the 68 cm default. */
  function floorPreviewScale(sceneScale, size) {
    const fromAlt = effectiveSceneLetterScale(sceneScale);
    if (fromAlt != null) return fromAlt;
    return Number(size?.scale) || 1;
  }

  function rgbPreviewScale(sceneScale, config) {
    const sizes = config?.sizes || [];
    const rgbBase =
      Number(sizes.find((s) => s.id === 'h120' && (s.mode === 'rgb' || s.type === 'rgb'))?.scale) ||
      Number(sizes.find((s) => s.id === 'h120')?.scale) ||
      0.55;
    const fromAlt = effectiveSceneLetterScale(sceneScale);
    return rgbBase * (fromAlt != null ? fromAlt : 1);
  }

  /**
   * Magnetic specials are size-locked PNGs (letter-120-KALP, letter-170-YILBASI_AGACI, …).
   * Never strip the mm segment — sharedMagneticFileName would break picker icons.
   */
  function specialPickerFileName(sp, config) {
    if (!sp?.file) return '';
    let file = String(sp.file);
    if (isRgbAssetConfig(config)) return rgbSafeFileName(file, config) || file;
    // Legacy shared alias without mm
    if (/^letter-KALP\.png$/i.test(file)) file = 'letter-120-KALP.png';
    if (
      sp.mm &&
      /^letter-(?:white-|yellow-|daylight-)?[A-Z0-9_]+\.png$/i.test(file) &&
      !/^letter-(?:white-|yellow-|daylight-|rgb-|rail-|floor-)?\d+-/.test(file)
    ) {
      file = file.replace(
        /^(letter-(?:white-|yellow-|daylight-)?)/i,
        `$1${sp.mm}-`
      );
    }
    return file;
  }

  function specialPickerCandidateFiles(sp, config) {
    const primary = specialPickerFileName(sp, config);
    const out = [];
    const push = (f) => {
      if (f && !out.includes(f)) out.push(f);
    };
    // Button-only art (ara boşluk / coming-soon logos) — keep preview glyph on sp.file
    if (sp?.buttonFile) push(String(sp.buttonFile));
    push(primary);
    if (sp?.file) push(String(sp.file));
    if (sp?.mm && primary) {
      push(
        primary.replace(
          /^(letter-(?:white-|yellow-|daylight-|rgb-|rail-|floor-)?)/i,
          (m) => (/^\d+-/.test(primary.slice(m.length)) ? m : `${m}${sp.mm}-`)
        )
      );
    }
    if (sp?.stem) {
      const stem = String(sp.stem).toUpperCase();
      const mm = sp.mm || '120';
      push(`letter-${mm}-${stem}.png`);
      push(`letter-${stem}.png`);
    }
    return out;
  }

  function resolveTopPercent(raw, stage, isMobile) {
    if (raw == null || raw === '') return isMobile ? '42%' : '51%';
    return resolveAxisPercent(raw, stage, stage && stage.clientHeight) || (isMobile ? '42%' : '51%');
  }

  /** Horizontal percent of the scene photo. Bare numbers are percents; px uses photo width. */
  function resolveLeftPercent(raw, stage) {
    if (raw == null || raw === '') return null;
    return resolveAxisPercent(raw, stage, stage && stage.clientWidth);
  }

  function resolveAxisPercent(raw, stage, axisPx) {
    const s = String(raw).trim();
    if (!s) return null;
    if (s.endsWith('%')) return s;
    const vw = s.match(/^([\d.]+)\s*vw$/i);
    if (vw) return `${vw[1]}%`;
    const px = s.match(/^([\d.]+)\s*px$/i);
    if (px && axisPx) {
      const pct = (Number(px[1]) / axisPx) * 100;
      return `${Math.max(0, Math.min(100, pct)).toFixed(2)}%`;
    }
    if (/^[\d.]+$/.test(s)) return `${s}%`;
    return s;
  }

  function typeFromSizeId(sizeId) {
    if (['h100', 'h150', 'h200'].includes(sizeId)) return 'sliding';
    if (sizeId === 'h680') return 'floor';
    if (['h95', 'h120', 'h170', 'h220', 'symbols'].includes(sizeId)) return 'magnetic';
    return null;
  }

  function magneticSharedSpecialsList(charset) {
    return (charset?.sharedSpecials || []).filter((sp) => !sp?.mode || sp.mode === 'magnetic');
  }

  /** Magnetic Semboller catalog order (merchant list). */
  function sortMagneticSymbolsCatalog(list) {
    const rank = (sp) => {
      const id = String(sp?.id || '');
      const byId = {
        '120:KALP': 0,
        '135:YUVARLAK_LOGO': 1,
        '220:YUVARLAK_LOGO': 2,
        '135:KARE_LOGO': 3,
        '220:KARE_LOGO': 4,
        '140:BARDAK': 5,
        '155:KAHVE_FINCANI': 6,
        '155:BEGENI': 7,
        '150:CIFT': 8,
        '170:YILBASI_AGACI': 9,
        '178:KEDI': 10,
        '120:YUKARI_OK': 11,
        '120:ASAGI_OK': 12,
        '120:SOL_OK': 13,
        '120:SAG_OK': 14,
        '120:OFF': 15,
      };
      if (byId[id] != null) return byId[id];
      return 100;
    };
    return [...(list || [])].sort((a, b) => {
      const d = rank(a) - rank(b);
      if (d !== 0) return d;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
  }

  function isRgbCraft(config, typeConfig, sizeConfig) {
    return (
      config?.craftMode === 'rgb' ||
      typeConfig?.mode === 'rgb' ||
      typeConfig?.id === 'rgb' ||
      sizeConfig?.mode === 'rgb' ||
      sizeConfig?.type === 'rgb'
    );
  }

  function spacerVariantIdForSize(addons, sizeId) {
    const map = addons?.spacerVariants || {};
    const key = String(sizeId || '').toLowerCase();
    const id = map[key] || addons?.spacerVariantId;
    return id ? Number(id) : null;
  }

  function variantOptionBlob(v) {
    const opts = Array.isArray(v.options) ? v.options.join(' ') : '';
    return `${v.title || ''} ${v.option1 || ''} ${v.option2 || ''} ${v.option3 || ''} ${opts}`;
  }

  function isKalpVariant(v) {
    return /kalp/i.test(variantOptionBlob(v));
  }

  /** RGB H12 Kalp special variant ($22) — separate from letter H12 ($20). */
  function findKalpVariant(variants, preferredType) {
    const list = (variants || []).filter((v) => isKalpVariant(v));
    if (!list.length) return null;
    if (preferredType) {
      const typed = list.find((v) => String(v.type || '') === String(preferredType));
      if (typed) return typed;
    }
    return list.find((v) => v.available) || list[0];
  }

  function isHeartSpecialChar(ch, specialByChar) {
    if (!ch) return false;
    if (ch === '♥') return true;
    const sp = specialByChar?.get(ch);
    if (!sp) return false;
    const stem = String(sp.stem || '').toUpperCase();
    const id = String(sp.id || '').toUpperCase();
    return stem === 'KALP' || id.includes('KALP') || sp.char === '♥';
  }

  /** Split RGB text into letter qty vs heart (kalp) qty for pricing/cart. */
  function rgbPriceBreakdown(text, specialByChar) {
    let letters = 0;
    let hearts = 0;
    let spaces = 0;
    for (const ch of Array.from(text || '')) {
      if (ch === ' ') {
        spaces += 1;
        continue;
      }
      if (isHeartSpecialChar(ch, specialByChar)) hearts += 1;
      else letters += 1;
    }
    return { letters, hearts, spaces, glyphs: letters + hearts };
  }

  /** Extract size id from variant title/options (H120mm or H12 cm → h120). */
  function variantSizeCode(v) {
    const blob = variantOptionBlob(v).toLowerCase().replace(/\s+/g, '');
    const cmMap = {
      '9': 'h95',
      '9.5': 'h95',
      '9,5': 'h95',
      '10': 'h100',
      '12': 'h120',
      '15': 'h150',
      '17': 'h170',
      '20': 'h200',
      '22': 'h220',
      '68': 'h680',
    };
    let m = blob.match(/h(\d{2,3})mm/);
    if (m) return `h${m[1]}`;
    m = blob.match(/h(\d{1,2}(?:[.,]\d)?)cm/);
    if (m && cmMap[m[1]]) return cmMap[m[1]];
    m = blob.match(/(?:^|[^a-z0-9])(\d{1,2}(?:[.,]\d)?)cm/);
    if (m && cmMap[m[1]]) return cmMap[m[1]];
    m = blob.match(/h(\d{2,3})(?!\d)/);
    if (m) return `h${m[1]}`;
    return null;
  }

  function scoreVariantForSize(v, size, preferredType) {
    if (!size) return 0;
    // Never pick "H12 cm Kalp" for normal letter sizing
    if (isKalpVariant(v)) return 0;
    const blob = variantOptionBlob(v).toLowerCase();
    const compact = blob.replace(/\s+/g, '');
    const code = variantSizeCode(v);
    let score = 0;

    // Strongest: parsed H-code matches size id (h95, h150, …)
    if (code && size.id && code === String(size.id).toLowerCase()) score += 1000;

    const tokens = (size.match || []).map((m) => String(m).toLowerCase());
    tokens.forEach((token) => {
      if (!token) return;
      const tCompact = token.replace(/\s+/g, '');
      // Exact option equality
      for (const opt of [v.option1, v.option2, v.option3, ...(v.options || [])]) {
        if (!opt) continue;
        const o = String(opt).toLowerCase().replace(/\s+/g, '');
        if (o === tCompact || o.startsWith(tCompact) || o.includes(tCompact)) {
          score += Math.min(80, 20 + tCompact.length);
        }
      }
      if (compact.includes(tCompact)) score += Math.min(40, 10 + tCompact.length);
    });

    const typeHint = preferredType || size.type || size.mode || null;
    if (typeHint && v.type) {
      if (String(v.type) === String(typeHint)) score += 500;
      else if (typeHint === 'rail' && v.type === 'sliding') score += 500;
      else score -= 200;
    }

    return score;
  }

  function findVariantForSize(variants, size, preferredType) {
    if (!variants?.length || !size) return null;
    let best = null;
    let bestScore = 0;
    variants.forEach((v) => {
      const score = scoreVariantForSize(v, size, preferredType);
      if (score > bestScore) {
        bestScore = score;
        best = v;
      } else if (score === bestScore && score > 0 && v.available && best && !best.available) {
        best = v;
      }
    });
    // Require a real match — don't silently fall back to an unrelated variant
    if (bestScore < 20) return null;
    // Prefer available among equally scored
    if (best && !best.available) {
      const avail = variants.find(
        (v) => scoreVariantForSize(v, size, preferredType) === bestScore && v.available
      );
      if (avail) return avail;
    }
    return best;
  }

  class LetterCraft extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const cfgEl = document.getElementById(this.dataset.configId);
      try {
        this.config = cfgEl ? JSON.parse(cfgEl.textContent) : {};
      } catch (err) {
        console.error('[letter-craft] config JSON parse failed', err);
        this.config = {};
      }
      this.i18n = this.config.i18n || {};
      this.moneyFormat = this.config.moneyFormat || '{{amount}}';
      this.assetSample = this.config.assetSample || '';
      this.baseCharset = this.config.charset || { sizes: {}, sharedSpecials: [], railSharedSpecials: [], floorSharedSpecials: [] };
      this.rgbCharset = null;
      this._connectorOptOut = false;
      this._adapterOptOut = false;
      this._rodOptOut = false;
      this._controllerOptOut = false;
      this._shelves = emptyMagneticShelves();
      this.charset = this.baseCharset;

      const firstType = (this.config.productTypes && this.config.productTypes[0]) || null;
      const preferredSizeId =
        (firstType && Array.isArray(firstType.sizes) && firstType.sizes[0]) ||
        (this.config.sizes && this.config.sizes[0]?.id) ||
        'h95';
      this.state = {
        typeId: firstType?.id || 'magnetic',
        sizeId: preferredSizeId,
        applyId: 'desktop',
        colorId: this.config.craftMode === 'rgb' ? 'rgb' : 'white',
        charColors: [],
        sceneId: null,
        text: '',
        catalogTab: 'letters',
        // RGB craftMode: live rainbow always on (no user toggle)
        rgbLive: this.config.craftMode === 'rgb',
      };
      // Last real magnetic letter size — used for glyphs/pricing while on "Semboller".
      this._magneticLetterSizeId = MAGNETIC_LETTER_SIZE_IDS.includes(preferredSizeId)
        ? preferredSizeId
        : 'h120';
      this._rebuildSpecials();

      this.maxChars = this.config.maxChars || 20;
      this.notes = this.config.notes || {};

      this.input = this.querySelector('[data-lc-input]');
      this.inputMirror = this.querySelector('[data-lc-input-mirror]');
      if (this.input) {
        if (this.config.craftMode === 'rgb') {
          this.input.removeAttribute('maxlength');
        } else {
          this.input.maxLength = this.maxChars;
        }
      }
      this.clearBtn = this.querySelector('[data-lc-clear]');
      this.backspaceBtn = this.querySelector('[data-lc-backspace]');
      this.preview = this.querySelector('[data-lc-preview]');
      this.placeholder = this.querySelector('[data-lc-placeholder]');
      this.card = this.querySelector('[data-lc-card]');
      this.bg = this.querySelector('[data-lc-bg]');
      this.thumbs = this.querySelector('[data-lc-thumbs]');
      this.sizesEl = this.querySelector('[data-lc-sizes]');
      this.appliesEl = this.querySelector('[data-lc-applies]');
      this.applyField = this.querySelector('[data-lc-apply-field]');
      this.colorsEl = this.querySelector('[data-lc-colors]');
      this.typesEl = this.querySelector('[data-lc-types]');
      this.symbolsEl = this.querySelector('[data-lc-symbols]');
      this.specialsEl = this.querySelector('[data-lc-specials]');
      this.specialsNoteEl = this.querySelector('[data-lc-specials-note]');
      this.specialsLabelEl = this.querySelector('[data-lc-specials-label]');
      this.specialsPanelEl = this.querySelector('[data-lc-specials-panel]');
      this.unitLabelEl = this.querySelector('[data-lc-unit-label]');
      this.catalogEl = this.querySelector('[data-lc-catalog]');
      this.catalogTabsEl = this.querySelector('[data-lc-catalog-tabs]');
      this.resetBtn = this.querySelector('[data-lc-reset]');
      this.colorField = this.querySelector('[data-lc-color-field]');
      this.railLengthRow = this.querySelector('[data-lc-rail-length-row]');
      this.railPowerRow = this.querySelector('[data-lc-rail-power-row]');
      this.railLengthEl = this.querySelector('[data-lc-rail-length]');
      this.railRodRow = this.querySelector('[data-lc-rail-rod-row]');
      this.railRodLabel = this.querySelector('[data-lc-rail-rod-label]');
      this.railRodPrice = this.querySelector('[data-lc-rail-rod-price]');
      this.railPowerEl = this.querySelector('[data-lc-rail-power]');
      this.countEl = this.querySelector('[data-lc-count]');
      this.unitEl = this.querySelector('[data-lc-unit]');
      this.totalEl = this.querySelector('[data-lc-total]');
      this.spaceRow = this.querySelector('[data-lc-space-row]');
      this.spaceCountEl = this.querySelector('[data-lc-space-count]');
      this.spacePriceEl = this.querySelector('[data-lc-space-price]');
      this.noteEl = this.querySelector('[data-lc-note]');
      this.warningEl = this.querySelector('[data-lc-warning]');
      this.statusEl = this.querySelector('[data-lc-status]');
      this.connectorOptional = this.querySelector('[data-lc-connector-optional]');
      this.connectorCheck = this.querySelector('[data-lc-connector-check]');
      this.adapterOptional = this.querySelector('[data-lc-adapter-optional]');
      this.adapterCheck = this.querySelector('[data-lc-adapter-check]');
      this.adapterBtnLabel = this.querySelector('[data-lc-adapter-btn-label]');
      this.rodOptional = this.querySelector('[data-lc-rod-optional]');
      this.rodBtnLabel = this.querySelector('[data-lc-rod-btn-label]');
      this.controllerOptional = this.querySelector('[data-lc-controller-optional]');
      this.controllerBtnLabel = this.querySelector('[data-lc-controller-btn-label]');
      this.dimmerBtnLabel = this.querySelector('[data-lc-dimmer-btn-label]');
      this.addonNoteEl = this.querySelector('[data-lc-addon-note]');
      this.shelvesWrap = this.querySelector('[data-lc-shelves]');
      this.shelfDialog = this.querySelector('[data-lc-shelf-dialog]');
      this.shelfDialogTitle = this.querySelector('[data-lc-shelf-dialog-title]');
      this.shelfDialogBody = this.querySelector('[data-lc-shelf-dialog-body]');
      this.shelfDialogImg = this.querySelector('[data-lc-shelf-dialog-img]');
      this.dimmerAsset = this.config.dimmerAsset || '';

      this._failedAssets = new Set();
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.stageEl = this.querySelector('[data-lc-stage]');

      this.scenes = this._buildScenes(this.config.media || []);
      this._markProductForm();
      const boot = () => {
        this._selectType(this.state.typeId, true);
        this._renderTypeControls();
        this._renderSymbolControls();
        this._bind();
        this._observeStage();
        this._relocateBuyButtons();
        requestAnimationFrame(() => this._relocateBuyButtons());
      };
      const fetchJson = (url) => {
        if (!url) return Promise.resolve(null);
        return fetch(url)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
      };
      // Base = magnetic/rail/floor charset. RGB loaded separately for craft_mode=all|rgb.
      const baseUrl = this.config.charsetUrl || null;
      const rgbUrl =
        this.config.rgbCharsetUrl ||
        (this.config.craftMode === 'rgb' ? this.config.charsetUrl : null);
      Promise.all([fetchJson(baseUrl), fetchJson(rgbUrl)])
        .then(([baseCs, rgbCs]) => {
          if (this.config.craftMode === 'rgb') {
            const cs = rgbCs || baseCs;
            if (cs) {
              this.rgbCharset = cs;
              this.charset = cs;
            }
          } else {
            if (baseCs) {
              this.baseCharset = baseCs;
              this.charset = baseCs;
            }
            if (rgbCs) this.rgbCharset = rgbCs;
          }
          boot();
        })
        .catch(() => boot());
    }

    _observeStage() {
      if (!this.stageEl || typeof ResizeObserver === 'undefined') return;
      this._ro = new ResizeObserver(() => {
        this.isMobile = window.matchMedia('(max-width: 749px)').matches;
        this._applyScenePosition();
        this._fitPreview();
      });
      this._ro.observe(this.stageEl);
    }

    _buildScenes(media) {
      const applies = (this.config.applies || []).map((a) => a.id);
      const sizeIds = (this.config.sizes || []).map((s) => s.id);
      const typeIds = (this.config.productTypes || []).map((t) => t.id);
      const mode = this.config.craftMode || 'magnetic';
      // Skip "ana resim" listing photos — they must not appear in product DIY scenes.
      const sceneMedia = (media || []).filter((m) => !isMainListingImageAlt(m?.alt));

      return sceneMedia.map((m, index) => {
        const meta = parseAlt(m.alt);
        let apply = meta.apply;
        let sizes = meta.sizes || [];
        let types = meta.types || [];

        // Magnetic: Üst / Mobil Üst / Boyut apply on every size tab.
        // Alt no longer needs Uygulama or Ölçü (those tags used to lock H95-only).
        if (mode === 'magnetic') {
          sizes = ['h95', 'h120', 'h170', 'h220'];
          types = ['magnetic'];
          apply = null;
        }

        // Dedicated RGB page: Liquid already sent product-only media — trust it,
        // ignore leftover tip:manyet in copied alts so magnetic scenes never win.
        if (mode === 'rgb') {
          types = ['rgb'];
          if (!sizes.length) sizes = ['h120', 'h170', 'h200'];
          apply = null;
        } else if (!sizes.length && !meta.types.length) {
          if (mode === 'sliding') {
            sizes = ['h100', 'h150', 'h200'];
            types = ['sliding'];
          } else if (mode === 'floor') {
            sizes = ['h680'];
            types = ['floor'];
          } else if (mode === 'all') {
            sizes = [...sizeIds];
            types = [...typeIds];
          } else {
            sizes = ['h95', 'h120', 'h170', 'h220'];
            types = ['magnetic'];
          }
        } else if (!sizes.length) {
          if (types.includes('rgb') && !types.includes('magnetic') && !types.includes('sliding')) {
            sizes = ['h120', 'h170', 'h200'];
          } else if (types.includes('floor') && types.length === 1) {
            sizes = ['h680'];
          } else if (types.includes('sliding') && types.length === 1) {
            sizes = ['h100', 'h150', 'h200'];
          } else {
            sizes = [...sizeIds];
          }
        }
        if (!types.length) {
          types = [...new Set(sizes.map(typeFromSizeId).filter(Boolean))];
        }
        if (!types.length) types = ['magnetic'];

        // Untagged apply: only when the product actually offers uygulama options
        if (!apply && types.includes('magnetic') && applies.length && mode !== 'magnetic') {
          apply = applies[index % applies.length] || 'desktop';
        }

        return {
          id: String(m.id || index),
          src: m.src,
          thumb: m.thumb || m.src,
          alt: m.alt || '',
          apply,
          sizes,
          types,
          pcTop: meta.pcTop || '51%',
          mbTop: meta.mbTop || '42%',
          pcLeft: meta.pcLeft || null,
          mbLeft: meta.mbLeft || null,
          letterScale: meta.letterScale != null ? meta.letterScale : null,
          pcLetterScale: meta.pcLetterScale != null ? meta.pcLetterScale : null,
          mbLetterScale: meta.mbLetterScale != null ? meta.mbLetterScale : null,
        };
      });
    }

    /** Active scene Alt Boyut for current desktop/mobile viewport (magnetic / RGB / sliding). */
    _sceneLetterScale() {
      return sceneLetterScaleForViewport(this._currentScene(), this.isMobile);
    }

    _markProductForm() {
      const hosts = [
        this.closest('product-info'),
        this.closest('.product'),
        this.closest('.product-information'),
        this.closest('.product-details'),
        this.closest('.product-information__grid'),
      ].filter(Boolean);

      hosts.forEach((el) => el.classList.add('product--lettercraft'));

      // Horizon: media gizlenince details dar sidebar'da kalmasın
      const grid =
        this.closest('.product-information__grid') ||
        document.querySelector('.product-information__grid');
      if (grid) {
        grid.classList.add('product--lettercraft');
        grid.classList.add('product-information--media-none');
        grid.classList.remove('product-information--media-left');
        grid.classList.remove('product-information--media-right');
      }

      const details = this.closest('.product-details') || document.querySelector('.product-details');
      if (details) {
        details.classList.add('product--lettercraft');
        details.style.setProperty('--details-max-width', '100%');
        details.style.setProperty('--sidebar-width', '100%');
        details.style.maxWidth = 'none';
        details.style.width = '100%';
      }

      // Limit eden üst group-block / section sarmalayıcıları
      let node = this.parentElement;
      for (let i = 0; i < 8 && node; i += 1) {
        if (
          node.classList?.contains('group-block') ||
          node.classList?.contains('product-information') ||
          node.matches?.('[class*="product-details"]')
        ) {
          node.style.setProperty('--details-max-width', '100%');
          node.style.maxWidth = 'none';
          node.style.width = '100%';
        }
        node = node.parentElement;
      }

      const media =
        document.querySelector('.product-information__media') ||
        document.querySelector('media-gallery');
      if (media) media.setAttribute('hidden', '');
    }

    _typeConfig() {
      const typeId = this.state?.typeId;
      return (
        (this.config.productTypes || []).find((t) => t.id === typeId) ||
        this.config.productTypes?.[0] ||
        null
      );
    }

    _sizesForType() {
      const type = this._typeConfig();
      const allowed = type?.sizes || [];
      const all = this.config.sizes || [];
      let list = all.filter((s) => {
        if (allowed.length && !allowed.includes(s.id)) return false;
        if (type?.id && s.type && s.type !== type.id) return false;
        return true;
      });
      if (!list.length && !allowed.length) list = [...all];

      // Magnetic: filtre uygulama tipine göre (Askılı → 10/15, Masaüstü/Duvar → diğerleri)
      if (type?.showApply && this.state.applyId) {
        const byApply = list.filter(
          (s) => Array.isArray(s.applies) && s.applies.includes(this.state.applyId)
        );
        if (byApply.length) list = byApply;
      }
      return list;
    }

    _sizeConfig() {
      const list = this._sizesForType();
      return list.find((s) => s.id === this.state.sizeId) || list[0] || this.config.sizes?.[0];
    }

    _colorConfig() {
      return (this.config.colors || []).find((c) => c.id === this.state.colorId) || this.config.colors?.[0];
    }

    _allowedApplies() {
      const type = this._typeConfig();
      if (type && type.showApply === false) return [];
      const typeSizeIds = type?.sizes || [];
      const sizeId = this.state?.sizeId;
      const current = (this.config.sizes || []).find((s) => s.id === sizeId);
      // Selected size wins: h95 → Masaüstü only (hide Duvar/Cam)
      if (
        current &&
        Array.isArray(current.applies) &&
        (!typeSizeIds.length || typeSizeIds.includes(current.id)) &&
        (!type?.id || !current.type || current.type === type.id)
      ) {
        return (this.config.applies || [])
          .map((a) => a.id)
          .filter((id) => current.applies.includes(id));
      }
      const set = new Set();
      (this.config.sizes || []).forEach((s) => {
        if (typeSizeIds.length && !typeSizeIds.includes(s.id)) return;
        if (type?.id && s.type && s.type !== type.id) return;
        (s.applies || []).forEach((a) => set.add(a));
      });
      return (this.config.applies || []).map((a) => a.id).filter((id) => set.has(id));
    }

    _allowedColors() {
      return this._sizeConfig()?.colors || ['white'];
    }

    _constrainOptions(resetSize) {
      const sizes = this._sizesForType();
      if (!sizes.length) return;
      // Only reset size when switching product type (or size invalid for type)
      if (resetSize || !sizes.some((s) => s.id === this.state.sizeId)) {
        this.state.sizeId = sizes[0].id;
      }
      if (MAGNETIC_LETTER_SIZE_IDS.includes(this.state.sizeId)) {
        this._magneticLetterSizeId = this.state.sizeId;
      }

      const applies = this._allowedApplies();
      if (!applies.length) {
        this.state.applyId = null;
      } else if (resetSize || !applies.includes(this.state.applyId)) {
        this.state.applyId = applies[0];
      }

      const colors = this._allowedColors();
      if (resetSize || !colors.includes(this.state.colorId)) {
        // Prefer white when daylight (etc.) drops out for the new size
        this.state.colorId = colors.includes('white') ? 'white' : colors[0];
      }
    }

    _selectType(typeId, isInit) {
      this.state.typeId = typeId;
      if (!isInit) {
        this._connectorOptOut = false;
        this._adapterOptOut = false;
        this._rodOptOut = false;
        this._controllerOptOut = false;
        this._shelves = emptyMagneticShelves();
      }
      if (this._isRgbMode() && this.rgbCharset) this.charset = this.rgbCharset;
      else this.charset = this.baseCharset || this.charset;
      this._constrainOptions(true);
      this._rebuildSpecials();
      // RGB: never wipe shared textarea on type/size constrain — only magnetic/rail sanitize.
      if (this.input && !isInit) {
        if (this._isRgbMode()) this._rgbPrevValue = this.input.value || '';
        else this.input.value = this._sanitizeCurrent(this.input.value);
      }
      this._renderSizeControls();
      this._renderApplyControls();
      this._renderColorControls();
      this._renderSymbolControls();
      this._syncVariant();
      this._renderScenes(true);
      this._render();
      this._applyScenePosition();
      if (!isInit) this._renderTypeControls();
      this._relocateBuyButtons();
      this._syncTypeChrome();
    }

    /**
     * Homepage (craft_mode=all): match RGB/magnetic PDP chrome —
     * total under specials + larger text input. Rail/floor keep total on top.
     */
    _syncTypeChrome() {
      const emphasis = this._isRgbMode() || this._isMagneticMode() || this._isSlidingMode();
      const afterSpecials = this._isRgbMode() || this._isMagneticMode();
      const topSlot = this.querySelector('[data-lc-total-slot="top"]');
      const afterSlot = this.querySelector('[data-lc-total-slot="after-specials"]');
      let hero = this.querySelector('[data-lc-total-hero]');
      if (!hero && this.totalEl) hero = this.totalEl.closest('.letter-craft__total-hero');

      if (hero && topSlot && afterSlot) {
        const target = afterSpecials ? afterSlot : topSlot;
        if (hero.parentElement !== target) target.appendChild(hero);
        hero.classList.toggle('letter-craft__total-hero--after-specials', afterSpecials);
        topSlot.hidden = afterSpecials;
        afterSlot.hidden = !afterSpecials;
      }

      const magnetic = this._isMagneticMode();
      if (this.input) {
        this.input.classList.toggle('letter-craft__input--emphasis', emphasis && !magnetic);
        this.input.classList.toggle('letter-craft__input--boxed', magnetic);
        const cue = t(this.i18n, 'textPlaceholder', 'Metninizi buraya yazın');
        this.input.placeholder = magnetic ? '' : cue;
      }
      const textLabel = this.querySelector('[data-lc-text-label]');
      if (textLabel) {
        textLabel.hidden = !magnetic;
        if (magnetic) {
          textLabel.textContent = t(this.i18n, 'textPlaceholder', 'Metninizi buraya yazın');
        }
      }
      const aria = this.querySelector('[data-lc-text-aria]');
      if (aria) {
        const i18n = this.i18n || {};
        aria.textContent = t(i18n, 'textPlaceholder', 'Metninizi buraya yazın');
      }
      this._syncBackspaceMount();
      this.classList.toggle('letter-craft--rgb', this._isRgbMode());
      this.classList.toggle('letter-craft--magnetic', this._isMagneticMode());
      this.classList.toggle('letter-craft--floor', this._isFloorMode());
      this.classList.toggle('letter-craft--sliding', this._isSlidingMode());
      if (this._isSlidingMode()) {
        this.style.setProperty('--lc-sym', '1');
      } else {
        this.style.removeProperty('--lc-sym');
        this.preview?.querySelector('[data-lc-rail-track]')?.remove();
        this.preview?.querySelector('[data-lc-rail-line]')?.remove();
      }
    }

    /** Sil sits inside the text input (replaces the old × clear control). */
    _syncBackspaceMount() {
      const btn = this.backspaceBtn || this.querySelector('[data-lc-backspace]');
      if (!btn) return;
      this.backspaceBtn = btn;
      const wrap =
        this.querySelector('[data-lc-input-wrap]') ||
        this.input?.closest('.letter-craft__input-wrap');
      if (wrap && btn.parentElement !== wrap) wrap.appendChild(btn);
      this._syncBackspaceVisibility();
    }

    _syncBackspaceVisibility() {
      const btn = this.backspaceBtn || this.querySelector('[data-lc-backspace]');
      if (!btn) return;
      this.backspaceBtn = btn;
      const hasText = Array.from(this.input?.value || '').length > 0;
      btn.hidden = !hasText;
      btn.disabled = !hasText;
      // Legacy × clear control — keep hidden; Sil replaces it.
      if (this.clearBtn) this.clearBtn.hidden = true;
    }

    /**
     * Prefer theme Sepete ekle / Hemen al (PDP). On homepage / sections without a
     * product form, show built-in CTAs so all letter types can still be purchased.
     */
    _relocateBuyButtons() {
      const mount = this.querySelector('[data-lc-buy-mount]');
      if (!mount) return;
      const standalone = mount.querySelector('[data-lc-buy-standalone]');
      const onProductPage = Boolean(
        this.closest('.product-details') ||
          this.closest('.product-information') ||
          this.closest('product-info') ||
          this.closest('[data-product-id]')
      );
      const root =
        this.closest('.product-details') ||
        this.closest('.product-information') ||
        this.closest('product-info') ||
        document;

      if (!this._buyButtonsEl && onProductPage) {
        const formButtons = root.querySelector('.product-form-buttons');
        const el =
          formButtons?.closest('.shopify-block') ||
          formButtons?.closest('[class*="buy-buttons"]') ||
          root.querySelector('buy-buttons-component') ||
          formButtons?.parentElement;
        if (el && !this.contains(el)) {
          this._buyButtonsEl = el;
          this._buyButtonsHome = el.parentElement;
        }
      }

      // PDP: always mount theme ATC under craft note (matches hangbox/standbox placement).
      if (this._buyButtonsEl) {
        if (standalone) standalone.hidden = true;
        mount.hidden = false;
        if (!mount.contains(this._buyButtonsEl)) mount.appendChild(this._buyButtonsEl);
        this._bindStandaloneBuyButtons();
        this._syncStandaloneBuyButtons();
        return;
      }

      // No theme ATC (homepage letter-craft): always show standalone CTAs.
      mount.hidden = false;
      if (standalone) standalone.hidden = false;
      this._bindStandaloneBuyButtons();
      this._syncStandaloneBuyButtons();
    }

    _bindStandaloneBuyButtons() {
      if (this._standaloneBuyBound) return;
      const addBtn = this.querySelector('[data-lc-add-cart]');
      const buyBtn = this.querySelector('[data-lc-buy-now]');
      if (!addBtn && !buyBtn) return;
      this._standaloneBuyBound = true;
      addBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this._addWithAddons({ checkout: false });
      });
      buyBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this._addWithAddons({ checkout: true });
      });
    }

    _syncStandaloneBuyButtons() {
      const addBtn = this.querySelector('[data-lc-add-cart]');
      const buyBtn = this.querySelector('[data-lc-buy-now]');
      if (!addBtn && !buyBtn) return;
      const plan = this._addonPlan();
      const count = this._mainLineQty(plan);
      const canAdd = this.available && count > 0 && Boolean(this.variant?.id);
      [addBtn, buyBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !canAdd;
        if (canAdd) btn.removeAttribute('aria-disabled');
        else btn.setAttribute('aria-disabled', 'true');
      });
    }

    _renderTypeControls() {
      if (!this.typesEl) return;
      this.typesEl.innerHTML = '';
      const types = this.config.productTypes || [];
      this.typesEl.hidden = types.length <= 1;
      types.forEach((type) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__type';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', type.id === this.state.typeId ? 'true' : 'false');
        btn.textContent = type.label;
        btn.dataset.typeId = type.id;
        if (type.id === this.state.typeId) btn.classList.add('is-active');
        btn.addEventListener('click', () => {
          if (type.id === this.state.typeId) return;
          this._selectType(type.id, false);
        });
        this.typesEl.appendChild(btn);
      });
    }

    _renderSizeControls() {
      this.sizesEl.innerHTML = '';
      this._sizesForType().forEach((size) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__size-box';
        // Keep RGB labels like "12 cm Harf"; only strip H-prefix / inch dual units.
        const cleaned =
          (size.label || '')
            .replace(/^H/i, '')
            .replace(/\s*mm\s*/gi, ' ')
            .replace(/\s*\/\s*[\d.,]+\s*(in|inç|cm).*$/i, '')
            .replace(/\s+/g, ' ')
            .trim() || size.label;
        // RGB: "12 cm Harf" → 12 cm / Harf on two lines (Harf·Sembol·Rakam).
        const stacked =
          this._isRgbMode() &&
          cleaned &&
          cleaned.match(
            /^(.+?\bcm)\s+(Harf|Sembol|Rakam|Letter|Symbol|Digit|Number)s?$/i
          );
        if (stacked) {
          btn.classList.add('letter-craft__size-box--stacked');
          const main = document.createElement('span');
          main.className = 'letter-craft__size-box-main';
          main.textContent = stacked[1];
          const kind = document.createElement('span');
          kind.className = 'letter-craft__size-box-kind';
          kind.textContent = stacked[2];
          btn.appendChild(main);
          btn.appendChild(kind);
        } else {
          btn.textContent = cleaned;
        }
        btn.title = size.label;
        btn.dataset.sizeId = size.id;
        if (size.id === this.state.sizeId) {
          btn.classList.add('is-active');
          this.dataset.sizeId = size.id;
        }
        btn.addEventListener('click', () => {
          // Semboller is a catalog picker, not a size — keep prior letter size for
          // scene photo, scale, and variant until a real cm size is chosen again.
          const prevSceneSizeId = this._activeCharsetSizeId();
          this.state.sizeId = size.id;
          this.dataset.sizeId = size.id;
          if (MAGNETIC_LETTER_SIZE_IDS.includes(size.id)) {
            this._magneticLetterSizeId = size.id;
          }
          this._setActive(this.sizesEl, btn);
          // Do NOT reset size to first — only refresh apply/color for this size
          this._constrainOptions(false);
          this._rebuildSpecials();
          // RGB: keep shared textarea across 12/17/20 — do not wipe on size switch.
          // Magnetic: keep text when jumping to/from Semboller (sanitize allows union there).
          if (this.input && !this._isRgbMode()) {
            this.input.value = this._sanitizeCurrent(this.input.value);
          }
          if (this._isRgbMode()) this._rgbPrevValue = this.input?.value || '';
          this._renderApplyControls();
          this._renderColorControls();
          this._renderSymbolControls();
          this._syncVariant();
          const nextSceneSizeId = this._activeCharsetSizeId();
          this._renderScenes(prevSceneSizeId !== nextSceneSizeId);
          this._render();
          // After glyphs+Boyut update, re-pin to THIS scene's Üst (no stale top).
          this._applyScenePosition();
        });
        this.sizesEl.appendChild(btn);
      });
    }

    _renderApplyControls() {
      const allowed = this._allowedApplies();
      if (this.applyField) this.applyField.hidden = allowed.length === 0;
      this.appliesEl.innerHTML = '';
      if (!allowed.length) return;

      (this.config.applies || [])
        .filter((apply) => allowed.includes(apply.id))
        .forEach((apply) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'letter-craft__pill';
          btn.textContent = apply.label;
          btn.dataset.applyId = apply.id;
          if (apply.id === this.state.applyId) btn.classList.add('is-active');
          btn.addEventListener('click', () => {
            this.state.applyId = apply.id;
            this._setActive(this.appliesEl, btn);
            const prevSizeId = this.state.sizeId;
            const sizes = this._sizesForType();
            if (!sizes.some((s) => s.id === this.state.sizeId) && sizes[0]) {
              this.state.sizeId = sizes[0].id;
            }
            const colors = this._allowedColors();
            if (!colors.includes(this.state.colorId)) {
              this.state.colorId = colors.includes('white') ? 'white' : colors[0];
            }
            if (prevSizeId !== this.state.sizeId) {
              this._rebuildSpecials();
              if (this.input) {
                if (this._isRgbMode()) this._rgbPrevValue = this.input.value || '';
                else this.input.value = this._sanitizeCurrent(this.input.value);
              }
            }
            this._renderSizeControls();
            this._renderColorControls();
            if (prevSizeId !== this.state.sizeId) this._renderSymbolControls();
            this._syncVariant();
            this._renderScenes(true);
            this._render();
            this._applyScenePosition();
          });
          this.appliesEl.appendChild(btn);
        });
    }

    _renderColorControls() {
      const allowed = this._allowedColors();
      this.colorsEl.innerHTML = '';
      // RGB has a single fixed look — no color picker
      if (this._isRgbMode()) {
        if (this.colorField) this.colorField.hidden = true;
        this.state.colorId = 'rgb';
        this._syncRgbLiveToggle();
        return;
      }
      if (this.colorField) this.colorField.hidden = allowed.length === 0;
      // Product page (craftMode=magnetic) and homepage (craftMode=all + type=magnetic)
      // both show the color name inside the swatch.
      const typeId = this.state?.typeId || this._typeConfig()?.id;
      const floorLabeled = this._isFloorMode() || typeId === 'floor';
      const labeled =
        floorLabeled ||
        this.config.craftMode === 'magnetic' ||
        this.getAttribute('data-craft-mode') === 'magnetic' ||
        typeId === 'magnetic';
      if (this.colorsEl) {
        this.colorsEl.classList.toggle('letter-craft__swatches--labeled', labeled);
      }
      (this.config.colors || [])
        .filter((color) => allowed.includes(color.id))
        .forEach((color) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = labeled
            ? 'letter-craft__swatch letter-craft__swatch--labeled'
            : 'letter-craft__swatch';
          btn.style.background = color.swatch;
          btn.title = color.label;
          btn.setAttribute('aria-label', color.label);
          btn.dataset.colorId = color.id;
          if (labeled) {
            const name = document.createElement('span');
            name.className = 'letter-craft__swatch-label';
            name.textContent = floorLabeled
              ? color.id === 'rgb'
                ? 'Renkli'
                : color.id === 'white'
                  ? 'Beyaz'
                  : color.label
              : color.label;
            btn.appendChild(name);
          }
          if (color.id === this.state.colorId) btn.classList.add('is-active');
          btn.addEventListener('click', () => {
            this.state.colorId = color.id;
            this._setActive(this.colorsEl, btn);
            // Mixed h170: keep existing glyphs; active color only paints newly typed chars.
            if (
              this._isMagneticMode() &&
              magneticSupportsMixedColors(this.config, this.state.sizeId) &&
              this.input
            ) {
              // Re-gate only if active color cannot keep typing (no wipe of prior text).
              const filtered = filterMagneticColorInput(
                this.state.text || this.input.value || '',
                this.state.charColors || [],
                this.input.value || '',
                this.state.colorId || 'white',
                this.maxChars,
                this.charset,
                this.state.sizeId,
                this.specialByChar
              );
              if (filtered.text !== this.input.value) this.input.value = filtered.text;
              this.state.text = filtered.text;
              this.state.charColors = filtered.colors;
            } else if (this._isMagneticMode() && this.input?.value) {
              // Uniform color: repaint all glyphs to the selected swatch.
              this.state.charColors = Array.from(this.input.value).map(
                () => this.state.colorId || 'white'
              );
            } else if (this._isFloorMode() && this.input) {
              const cleaned = this._sanitizeCurrent(this.input.value);
              if (cleaned !== this.input.value) this.input.value = cleaned;
              this.state.text = cleaned;
            }
            this._syncRgbLiveToggle();
            this._renderSymbolControls();
            this._render();
          });
          this.colorsEl.appendChild(btn);
        });
      this._syncRgbLiveToggle();
    }

    /** Floor kaleidoscope runs on RGB glyphs only, including mixed Beyaz + RGB text. */
    _floorRgbLive() {
      if (!this._isFloorMode()) return false;
      return floorColorCounts(this.input?.value || this.state?.text || '', this.state?.charColors, 'white').rgb > 0;
    }

    _syncRgbLiveToggle() {
      const rgb = this._isRgbMode();
      const floorRgb = this._floorRgbLive();
      if (!rgb && !floorRgb) {
        this.state.rgbLive = false;
        delete this.dataset.rgbLive;
        delete this.dataset.live;
        this.classList.remove('is-rgb-live');
        this.preview?.classList.remove('letter-craft__preview--rgb-live');
        this._stopRgbLiveAnim();
        return;
      }
      this.state.rgbLive = true;
      this.dataset.rgbLive = '1';
      this.dataset.live = '1';
      this.classList.add('is-rgb-live');
      this.preview?.classList.add('letter-craft__preview--rgb-live');
      // Floor keeps the white layout size. data-color=rgb would switch on magnetic RGB heights.
      if (!this._isFloorMode()) this.dataset.color = 'rgb';
    }

    /**
     * Live RGB overlay — port of harfmix-rainbow-2.html.
     * Shared field across the word (--lc-band-x / --lc-band-span).
     * Coded letters use background-clip; special/connector PNGs keep photo masks.
     */
    _applyRgbLiveBand() {
      this._syncRgbLiveToggle();
      if (!this.preview) return;
      const on = (this._isRgbMode() || this._floorRgbLive()) && !!this.state.rgbLive;
      const nodes = this.preview.querySelectorAll(
        '[data-lc-glyph], .letter-craft__space--glyph, .letter-craft__glyph--rgb-text, .letter-craft__glyph--fallback'
      );
      if (!on) {
        nodes.forEach((el) => {
          el.classList.remove('letter-craft__glyph--live-band');
          el.style.removeProperty('--lc-live-mask');
          el.style.removeProperty('--lc-band-x');
          el.style.removeProperty('--lc-band-span');
        });
        this.preview.style.removeProperty('--lc-band-span');
        this._stopRgbLiveAnim();
        return;
      }
      if (!this._isFloorMode()) this.dataset.color = 'rgb';
      const span = Math.max(1, this.preview.scrollWidth || this.preview.clientWidth || 1);
      this.preview.style.setProperty('--lc-band-span', `${span}px`);
      nodes.forEach((el) => {
        // Never paint live RGB on connector / dimmer / power strips (L25 PNG stays).
        // Floor spacers stay the physical gap photo.
        if (
          el.classList.contains('letter-craft__controller') ||
          el.hasAttribute('data-lc-controller') ||
          el.hasAttribute('data-lc-dimmer') ||
          (this._isFloorMode() &&
            (el.hasAttribute('data-lc-space') ||
              el.classList.contains('letter-craft__space') ||
              el.dataset.lcColor !== 'rgb'))
        ) {
          el.classList.remove('letter-craft__glyph--live-band');
          el.style.removeProperty('--lc-live-mask');
          el.style.removeProperty('--lc-band-x');
          el.style.removeProperty('--lc-band-span');
          return;
        }
        const img = el.querySelector?.('img');
        const src = img?.currentSrc || img?.src;
        el.style.setProperty('--lc-band-x', `${-el.offsetLeft}px`);
        el.style.setProperty('--lc-band-span', `${span}px`);
        // PNG letters/specials/spacers: RGB field masked to photo alpha (face only).
        if (src) {
          el.classList.add('letter-craft__glyph--live-band');
          el.classList.remove('letter-craft__glyph--rgb-text');
          const safe = String(src).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
          el.style.setProperty('--lc-live-mask', `url("${safe}")`);
          if (img && !img.complete) {
            img.addEventListener(
              'load',
              () => {
                const s2 = img.currentSrc || img.src;
                if (!s2) return;
                const safe2 = String(s2).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
                el.style.setProperty('--lc-live-mask', `url("${safe2}")`);
              },
              { once: true }
            );
          }
        } else if (
          el.classList.contains('letter-craft__glyph--rgb-text') ||
          el.classList.contains('letter-craft__glyph--fallback')
        ) {
          el.classList.add('letter-craft__glyph--live-band');
          el.style.removeProperty('--lc-live-mask');
        }
      });
      this._startRgbLiveAnim();
    }

    /** Safari flickers when CSS background-image URLs change every frame. */
    _isSafariRgb() {
      if (this._safariRgb != null) return this._safariRgb;
      const ua = String(navigator.userAgent || '');
      this._safariRgb = /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|Android/i.test(ua);
      return this._safariRgb;
    }

    /** Ensure each live-band glyph has a face canvas (avoids toDataURL → CSS on Safari). */
    _syncRgbFaceLayers() {
      if (!this.preview) return;
      const live = this.preview.querySelectorAll('.letter-craft__glyph--live-band');
      const keep = new Set();
      live.forEach((el) => {
        if (
          el.classList.contains('letter-craft__controller') ||
          el.hasAttribute('data-lc-controller') ||
          el.hasAttribute('data-lc-dimmer')
        ) {
          return;
        }
        const img = el.querySelector(':scope > img');
        if (!img) return;
        let face = el.querySelector(':scope > canvas.letter-craft__rgb-face');
        if (!face) {
          face = document.createElement('canvas');
          face.className = 'letter-craft__rgb-face';
          face.setAttribute('aria-hidden', 'true');
          el.appendChild(face);
        }
        el.classList.add('letter-craft__glyph--has-rgb-face');
        keep.add(face);
      });
      this.preview.querySelectorAll('canvas.letter-craft__rgb-face').forEach((face) => {
        if (keep.has(face)) return;
        face.parentElement?.classList.remove('letter-craft__glyph--has-rgb-face');
        face.remove();
      });
    }

    /** Paint shared field into per-glyph canvases (masked to letter PNG alpha). */
    _paintRgbFaces(field) {
      if (!this.preview || !field) return;
      const span = Math.max(1, this.preview.scrollWidth || this.preview.clientWidth || 1);
      this.preview.querySelectorAll('.letter-craft__glyph--has-rgb-face').forEach((el) => {
        const face = el.querySelector(':scope > canvas.letter-craft__rgb-face');
        const img = el.querySelector(':scope > img');
        if (!face || !img?.complete || !img.naturalWidth) return;
        const w = Math.max(1, Math.round(el.clientWidth || img.clientWidth || 1));
        const h = Math.max(1, Math.round(el.clientHeight || img.clientHeight || 1));
        if (face.width !== w || face.height !== h) {
          face.width = w;
          face.height = h;
        }
        const ctx = face.getContext('2d', { alpha: true });
        if (!ctx) return;
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
        // Match CSS: background-size span×100%, background-position -offsetLeft
        ctx.drawImage(field, 0, 0, field.width, field.height, -el.offsetLeft, 0, span, h);
        // Letter alpha (contain, bottom-centered — same as CSS mask)
        const ir = img.naturalWidth / Math.max(1, img.naturalHeight);
        let dw = w;
        let dh = w / ir;
        if (dh > h) {
          dh = h;
          dw = h * ir;
        }
        const dx = (w - dw) / 2;
        const dy = h - dh;
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(img, dx, dy, dw, dh);
        // Cut acrylic base (~bottom 12%), matching the CSS linear-gradient mask
        const fade = ctx.createLinearGradient(0, h, 0, 0);
        fade.addColorStop(0, 'rgba(0,0,0,0)');
        fade.addColorStop(0.11, 'rgba(0,0,0,0)');
        fade.addColorStop(0.135, 'rgba(0,0,0,1)');
        fade.addColorStop(1, 'rgba(0,0,0,1)');
        ctx.fillStyle = fade;
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
      });
    }

    /** P3 RGB only: mirrored kaleidoscope inside each letter, not the rainbow field. */
    _paintFloorKaleidoFaces(t) {
      const rainbow = this._ensureRgbLiveField()?.rainbow;
      if (!this.preview || typeof rainbow !== 'function') return;
      const slices = 12;
      const slice = (Math.PI * 2) / slices;
      this.preview.querySelectorAll('.letter-craft__glyph--has-rgb-face').forEach((el) => {
        if (
          el.hasAttribute('data-lc-space') ||
          el.classList.contains('letter-craft__space') ||
          el.hasAttribute('data-lc-dimmer')
        ) {
          return;
        }
        const face = el.querySelector(':scope > canvas.letter-craft__rgb-face');
        const img = el.querySelector(':scope > img');
        if (!face || !img?.complete || !img.naturalWidth) return;
        const w = Math.max(1, Math.round(el.clientWidth || img.clientWidth || 1));
        const h = Math.max(1, Math.round(el.clientHeight || img.clientHeight || 1));
        if (face.width !== w || face.height !== h) {
          face.width = w;
          face.height = h;
        }
        const ctx = face.getContext('2d', { alpha: true });
        if (!ctx) return;
        const imgData = ctx.createImageData(w, h);
        const data = imgData.data;
        const cx = (w - 1) / 2;
        const cy = (h - 1) / 2;
        const maxR = Math.hypot(cx, cy) || 1;
        for (let y = 0; y < h; y += 1) {
          for (let x = 0; x < w; x += 1) {
            const dx = x - cx;
            const dy = y - cy;
            let ang = Math.atan2(dy, dx);
            ang = ((ang % slice) + slice) % slice;
            if (ang > slice / 2) ang = slice - ang;
            const r = Math.hypot(dx, dy) / maxR;
            const rgb = rainbow((ang / (slice / 2)) * 1.6 + r * 2.2 + t * 1.15);
            const o = (y * w + x) * 4;
            data[o] = rgb[0];
            data[o + 1] = rgb[1];
            data[o + 2] = rgb[2];
            data[o + 3] = 255;
          }
        }
        ctx.clearRect(0, 0, w, h);
        ctx.putImageData(imgData, 0, 0);
        const ir = img.naturalWidth / Math.max(1, img.naturalHeight);
        let dw = w;
        let dh = w / ir;
        if (dh > h) {
          dh = h;
          dw = h * ir;
        }
        const dx = (w - dw) / 2;
        const dy = h - dh;
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(img, dx, dy, dw, dh);
        // The PNG's white acrylic plinth is the bottom quarter. Cut the pattern
        // there so the kaleidoscope stops at the letter face.
        const faceBottom = dy + dh * 0.74;
        const fadePx = Math.max(2, dh * 0.018);
        const fade = ctx.createLinearGradient(0, faceBottom - fadePx, 0, faceBottom);
        fade.addColorStop(0, 'rgba(0,0,0,1)');
        fade.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = fade;
        ctx.fillRect(0, 0, w, h);
        // Drop the PNG's soft fringe so the animation cannot tint the photo around the letter.
        const masked = ctx.getImageData(0, 0, w, h);
        const px = masked.data;
        for (let i = 3; i < px.length; i += 4) {
          if (px[i] < 200) px[i] = 0;
        }
        ctx.globalCompositeOperation = 'source-over';
        ctx.putImageData(masked, 0, 0);
      });
    }

    _clearRgbFaceLayers() {
      this.querySelectorAll?.('canvas.letter-craft__rgb-face').forEach((face) => {
        face.parentElement?.classList.remove('letter-craft__glyph--has-rgb-face');
        face.remove();
      });
      this.preview?.querySelectorAll('canvas.letter-craft__rgb-face').forEach((face) => {
        face.parentElement?.classList.remove('letter-craft__glyph--has-rgb-face');
        face.remove();
      });
    }

    /** Publish field for background-clip text fallbacks only (rare). */
    _publishRgbFieldCss(field) {
      if (!field) return;
      const hasText = !!this.preview?.querySelector(
        '.letter-craft__glyph--rgb-text.letter-craft__glyph--live-band, .letter-craft__glyph--fallback.letter-craft__glyph--live-band'
      );
      if (!hasText) {
        // Canvas faces cover PNG glyphs — avoid CSS url churn (Safari flicker).
        return;
      }
      try {
        // JPEG is cheaper than PNG for Safari decode; still only when text glyphs exist.
        this.style.setProperty('--lc-rgb-field', `url("${field.toDataURL('image/jpeg', 0.82)}")`);
      } catch (err) {
        this.style.setProperty('--lc-rgb-field', `url("${field.toDataURL('image/png')}")`);
      }
    }

    /** Offscreen field — palette / solid cycle / SPEED from harfmix-rainbow-2.html */
    _ensureRgbLiveField() {
      if (this._rgbField?.flow) return this._rgbField;
      const FIELD_W = 360;
      const FIELD_H = 90;
      const field = document.createElement('canvas');
      field.width = FIELD_W;
      field.height = FIELD_H;
      // desynchronized can tear/flicker on Safari — only enable elsewhere.
      const ctxOpts = this._isSafariRgb()
        ? { alpha: false }
        : { alpha: false, desynchronized: true };
      const f = field.getContext('2d', ctxOpts);
      if (f) {
        f.imageSmoothingEnabled = true;
        f.imageSmoothingQuality = 'high';
      }
      const pixels = f.createImageData(FIELD_W, FIELD_H);
      const parseHex = (hex) => hex.match(/[a-f0-9]{2}/gi).map((v) => parseInt(v, 16));
      // Turkuaz, macenta, sarı, yeşil.
      const colors = ['#00e5df', '#f000c8', '#ffe600', '#33ef58'].map(parseHex);
      // Sabit anlarda bütün yazı aynı renk olur.
      const solidColors = ['#ffe600', '#168bff', '#ff55b7', '#122980', '#33ef58', '#ff2424'].map(
        parseHex
      );
      const mod = (n, v) => ((n % v) + v) % v;
      const smooth = (t) => {
        t = Math.max(0, Math.min(1, t));
        return t * t * (3 - 2 * t);
      };
      const mix = (a, b, t) => [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
      ];
      // Cosine blend between stops (rainbow-2)
      const rainbow = (p) => {
        p = mod(p, colors.length);
        const i = Math.floor(p);
        return mix(
          colors[i],
          colors[(i + 1) % colors.length],
          (1 - Math.cos(Math.PI * (p - i))) / 2
        );
      };
      // Generalized stroke flow (arbitrary text — not HARFMIX-only routes).
      // Per-column vertical + midline paths mimic letter light channels.
      const COLS = 8;
      const flow = new Float32Array(FIELD_W * FIELD_H);
      for (let y = 0; y < FIELD_H; y++) {
        for (let x = 0; x < FIELD_W; x++) {
          const nx = (x + 0.5) / FIELD_W;
          const ny = (y + 0.5) / FIELD_H;
          const ri = Math.min(COLS - 1, Math.floor(nx * COLS));
          const colX = (ri + 0.5) / COLS;
          const dx = nx - colX;
          // Distance to vertical stem + horizontal bar (H-like channel)
          const dStem = Math.abs(dx);
          const dBar = Math.abs(ny - 0.5) * 0.55 + Math.abs(dx) * 0.35;
          const along = ny * 0.62 + (1 - Math.min(1, dStem * 14)) * 0.28 + ri * 0.16;
          const weight = Math.exp(-(dStem * dStem) / (2 * 0.045 ** 2)) +
            Math.exp(-(dBar * dBar) / (2 * 0.08 ** 2)) * 0.65;
          flow[y * FIELD_W + x] = along * 1.65 + (1 - Math.min(1, weight)) * 0.2;
        }
      }
      this._rgbField = {
        field,
        f,
        pixels,
        flow,
        rainbow,
        smooth,
        mix,
        solidColors,
        FIELD_W,
        FIELD_H,
        SPEED: 1.8,
      };
      return this._rgbField;
    }

    _stopRgbLiveAnim() {
      if (this._rgbRaf) {
        cancelAnimationFrame(this._rgbRaf);
        this._rgbRaf = 0;
      }
      this._rgbAnimStart = undefined;
      this._rgbLastPublish = 0;
      delete this.dataset.rgbPhase;
      this.style.removeProperty('--lc-rgb-field');
      this.style.removeProperty('--lc-rgb-intensity');
      if (this._rgbPhaseTimer) {
        clearTimeout(this._rgbPhaseTimer);
        this._rgbPhaseTimer = null;
      }
      this.style.removeProperty('--lc-rgb-flow');
      this.style.removeProperty('--lc-rgb-live-duration');
      this._clearRgbFaceLayers();
    }

    /**
     * Port of harfmix-rainbow-2.html draw loop.
     * PNG glyphs: paint into per-letter canvases (Safari-safe).
     * Text fallbacks only: publish --lc-rgb-field (rare).
     */
    _startRgbLiveAnim() {
      const rgbOn = () => this._isRgbMode() && !!this.state?.rgbLive;
      const floorOn = () => this._floorRgbLive() && !!this.state?.rgbLive;
      if (!rgbOn() && !floorOn()) {
        this._stopRgbLiveAnim();
        return;
      }
      if (this._rgbRaf) return;
      const reducedMq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      // Safari: slightly lower publish rate; Chrome keeps ~30fps.
      const PUBLISH_MS = this._isSafariRgb() ? 1000 / 20 : 1000 / 30;
      this._syncRgbFaceLayers();
      const tick = (now) => {
        const floor = floorOn();
        if (!rgbOn() && !floor) {
          this._stopRgbLiveAnim();
          return;
        }
        this._rgbAnimStart ??= now;
        const reduced = !!reducedMq?.matches;
        const due =
          reduced || !this._rgbLastPublish || now - this._rgbLastPublish >= PUBLISH_MS;
        if (!due) {
          this._rgbRaf = requestAnimationFrame(tick);
          return;
        }
        this._rgbLastPublish = now;
        const t = reduced ? 0 : (now - this._rgbAnimStart) / 1000;
        if (floor) {
          this._syncRgbFaceLayers();
          this._paintFloorKaleidoFaces(t);
          if (!reduced) this._rgbRaf = requestAnimationFrame(tick);
          else this._rgbRaf = 0;
          return;
        }
        const {
          field,
          f,
          pixels,
          flow,
          rainbow,
          smooth,
          mix,
          solidColors,
          SPEED,
        } = this._ensureRgbLiveField();
        // Her 8 saniyede farklı hareket ve farklı bir düz renk (rainbow-2).
        const cycle = Math.floor(t / 8);
        const local = t % 8;
        const solidAmount = smooth((local - 4.7) / 0.8) * (1 - smooth((local - 6.8) / 1.2));
        const solid = solidColors[cycle % solidColors.length];
        const data = pixels.data;
        for (let i = 0; i < flow.length; i++) {
          const rgb = mix(rainbow(flow[i] - t * SPEED * 0.72), solid, solidAmount);
          const o = i * 4;
          data[o] = (rgb[0] + 0.5) | 0;
          data[o + 1] = (rgb[1] + 0.5) | 0;
          data[o + 2] = (rgb[2] + 0.5) | 0;
          data[o + 3] = 255;
        }
        f.putImageData(pixels, 0, 0);
        this._syncRgbFaceLayers();
        this._paintRgbFaces(field);
        this._publishRgbFieldCss(field);
        this.dataset.rgbPhase = String(cycle % solidColors.length);
        if (!reduced) {
          this._rgbRaf = requestAnimationFrame(tick);
        } else {
          this._rgbRaf = 0;
        }
      };
      this._rgbRaf = requestAnimationFrame(tick);
    }


    _rebuildSpecials() {
      if (!this.state) {
        this.specialMap = createSpecialCharMap([], this.charset);
        this.specialByChar = this.specialMap.byChar;
        return;
      }
      const type = this._typeConfig();
      const mode = type?.mode || this._sizeConfig()?.mode;
      const railOrFloor = mode === 'rail' || mode === 'floor' || type?.id === 'sliding' || type?.id === 'floor';
      let shared = this.charset.sharedSpecials || [];
      if (mode === 'rail' || type?.id === 'sliding') {
        shared = this.charset.railSharedSpecials || [];
      } else if (mode === 'floor' || type?.id === 'floor') {
        shared = this.charset.floorSharedSpecials || [];
      }
      const sizeSp = sizeEntry(this.charset, this.state.sizeId)?.specials || [];
      const seen = new Set();
      const uniq = [];
      const rgbMode = mode === 'rgb' || this._isRgbMode();
      const magneticSymbolsSize = this._isMagneticMode() && this.state.sizeId === 'symbols';
      [...shared, ...sizeSp].forEach((sp) => {
        if (!sp?.id || seen.has(sp.id)) return;
        if (mode === 'floor' || type?.id === 'floor') {
          if (sp.sizeId && sp.sizeId !== this.state.sizeId) return;
        } else if (mode === 'rail' || type?.id === 'sliding') {
          if (!isSpecialAllowedForMagnetic(sp, this.state.sizeId)) return;
        } else if (!railOrFloor && rgbMode) {
          // RGB catalog: size-locked specials + all-size (ara boşluk). Coming-soon logos also size-bound.
          if (!sp.allRgbSizes && !isSpecialAllowedForMagnetic(sp, this.state.sizeId)) return;
        } else if (this._isMagneticMode()) {
          // Magnetic: catalog only on "Semboller" — keep both 13.5cm and 22cm logos (no stem dedupe).
          if (!magneticSymbolsSize) return;
        }
        seen.add(sp.id);
        uniq.push(sp);
      });
      if (rgbMode) {
        // Full map so mixed text (digits + couple + letters) stays recognized across size switches.
        const full = createSpecialCharMap(rgbAllSpecials(this.charset), this.charset);
        const catalogIds = new Set(uniq.map((sp) => sp.id));
        this.specialByChar = full.byChar;
        this.specialMap = {
          byChar: full.byChar,
          list: full.list.filter((sp) => catalogIds.has(sp.id)),
        };
      } else if (this._isMagneticMode()) {
        // Always keep full magnetic special map for preview/pricing of already-inserted glyphs.
        const full = createSpecialCharMap(magneticSharedSpecialsList(this.charset), this.charset);
        this.specialByChar = full.byChar;
        if (magneticSymbolsSize) {
          const catalogIds = new Set(uniq.map((sp) => sp.id));
          this.specialMap = {
            byChar: full.byChar,
            list: sortMagneticSymbolsCatalog(full.list.filter((sp) => catalogIds.has(sp.id))),
          };
        } else {
          this.specialMap = { byChar: full.byChar, list: [] };
        }
      } else {
        this.specialMap = createSpecialCharMap(uniq, this.charset);
        this.specialByChar = this.specialMap.byChar;
      }
    }

    _isRgbMode() {
      const type = this._typeConfig();
      const size = this._sizeConfig();
      return isRgbCraft(this.config, type, size);
    }

    /** Asset paths: force letter-rgb-* even when craftMode is "all". */
    _glyphAssetConfig() {
      if (this._isRgbMode()) {
        if (this.config?.craftMode === 'rgb') return this.config;
        return { ...this.config, craftMode: 'rgb', forceRgbAssets: true };
      }
      if (this._isSlidingMode()) return { ...this.config, craftMode: 'rail' };
      if (this._isFloorMode()) return { ...this.config, craftMode: 'floor' };
      return this.config;
    }

    _isRailMode() {
      const type = this._typeConfig();
      const size = this._sizeConfig();
      const mode = type?.mode || size?.mode;
      return mode === 'rail' || mode === 'floor' || type?.id === 'sliding' || type?.id === 'floor';
    }

    /** Üstten askılı (ray) — not floor. Preview uses Arial Bold text, not letter PNGs. */
    _isSlidingMode() {
      if (this._isFloorMode()) return false;
      const type = this._typeConfig();
      const size = this._sizeConfig();
      const mode = type?.mode || size?.mode;
      return mode === 'rail' || type?.id === 'sliding' || this.config?.craftMode === 'sliding';
    }

    _isFloorMode() {
      const type = this._typeConfig();
      const size = this._sizeConfig();
      return type?.mode === 'floor' || size?.mode === 'floor' || type?.id === 'floor';
    }

    _isMagneticMode() {
      return !this._isRgbMode() && !this._isRailMode() && !this._isFloorMode();
    }

    /**
     * Charset/asset size for magnetic glyphs. "Semboller" is a picker only —
     * letters keep the last real letter size (like RGB mixed text across sizes).
     */
    _activeCharsetSizeId() {
      if (this._isMagneticMode() && this.state.sizeId === 'symbols') {
        return this._magneticLetterSizeId || 'h120';
      }
      return this.state.sizeId;
    }

    _railMaxWidth() {
      return (
        Number(this._sizeConfig()?.maxWidthMm) ||
        Number(this._typeConfig()?.maxWidthMm) ||
        Number(this.config.railMaxWidthMm) ||
        Number(sizeEntry(this.charset, this.state.sizeId)?.maxWidthMm) ||
        2000
      );
    }

    _sanitizeCurrent(value) {
      if (this._isRgbMode()) {
        const rawMax = Number(this._typeConfig()?.maxGlyphs);
        const maxGlyphs = Number.isFinite(rawMax) ? rawMax : 0;
        // Preserve mixed-size glyphs; only gate newly typed chars for active size.
        return filterRgbInputChange(
          this._rgbPrevValue || value,
          value,
          maxGlyphs,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
      }
      if (this._isRailMode()) {
        const cleaned = sanitizeRailInput(
          value,
          this.maxChars,
          this.charset,
          this.state.sizeId,
          this.specialByChar,
          this._railMaxWidth()
        );
        if (this._isFloorMode()) return this._limitFloorText(cleaned);
        return cleaned;
      }
      if (this._isMagneticMode() && magneticSupportsMixedColors(this.config, this.state.sizeId)) {
        const filtered = filterMagneticColorInput(
          this.state.text || '',
          this.state.charColors || [],
          value,
          this.state.colorId || 'white',
          this.maxChars,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
        this.state.charColors = filtered.colors;
        return filtered.text;
      }
      const cleaned = sanitizeInput(value, this.maxChars, this.charset, this.state.sizeId, this.specialByChar);
      // Single active color for non-mixed magnetic sizes
      const cols = Array.from(cleaned).map((ch) => (ch === ' ' ? (this.state.colorId || 'white') : (this.state.colorId || 'white')));
      this.state.charColors = cols;
      return cleaned;
    }

    /** White P3 glyphs cap at 20. RGB glyphs in the same word are not capped by that. */
    _limitFloorText(value) {
      const filtered = filterFloorColorInput(
        this.state?.text || '',
        this.state?.charColors || [],
        value,
        this.state?.colorId || 'white',
        this.maxChars
      );
      this.state.charColors = filtered.colors;
      this._floorLimitNote = filtered.hitWhiteCap
        ? 'Beyaz renkte en fazla 20 karakter kullanılabilir. Boşluk ve Renkli harfler bu sayıya dahil değildir.'
        : '';
      return filtered.text;
    }

    _floorVariantByRole(role) {
      const variants = this.config.variants || [];
      const direct = variants.find((v) => v.floorRole === role);
      if (direct) return direct;
      const roleOf = (title) => {
        const name = String(title || '').toLocaleLowerCase('tr');
        if (name.includes('ara boşluk') || name.includes('ara bosluk')) return 'spacer';
        if (name.includes('1000')) return 'rgb1000';
        if (name.includes('350')) return 'white350';
        if (name.includes('600') && name.includes('rgb')) return 'rgb600';
        if (name.includes('600')) return 'white600';
        if (name.includes('rgb')) return 'letter-rgb';
        if (name.includes('beyaz')) return 'letter-white';
        return '';
      };
      return variants.find((v) => roleOf(v.title) === role) || null;
    }

    _renderSymbolControls() {
      this._rebuildSpecials();
      this.state.catalogTab = 'specials';
      this._renderCatalog();
      // specialsEl kept for legacy; catalog is the clickable specials UI
      if (this.specialsEl) this.specialsEl.innerHTML = '';
      if (this.symbolsEl) this.symbolsEl.innerHTML = '';
      if (this.catalogTabsEl) this.catalogTabsEl.innerHTML = '';
      this._syncSpecialsPanel();
    }

    /** RGB size gates for specials UI + unit price label copy. */
    _isRgbDigitsOnlySize() {
      return this._isRgbMode() && String(this.state?.sizeId || '') === 'h200';
    }

    _syncUnitLabel() {
      const el = this.unitLabelEl || this.querySelector('[data-lc-unit-label]');
      if (!el) return;
      if (this._isRgbDigitsOnlySize()) {
        el.textContent = t(this.i18n, 'unitPriceDigit', 'Rakam başı');
      } else {
        el.textContent = t(this.i18n, 'unitPrice', 'Harf başı');
      }
    }

    _syncSpecialsPanel() {
      const labelEl = this.specialsLabelEl || this.querySelector('[data-lc-specials-label]');
      const noteEl = this.specialsNoteEl;
      const catalogEl = this.catalogEl;
      const rgb = this._isRgbMode();
      const sizeId = String(this.state?.sizeId || '');

      if (rgb && sizeId === 'h200') {
        if (labelEl) labelEl.hidden = false;
        if (catalogEl) catalogEl.hidden = false;
        if (noteEl) {
          noteEl.hidden = false;
          noteEl.textContent = t(
            this.i18n,
            'noteSpecialsH200Rgb',
            "20 cm'lik ölçüde yazıya sadece rakam eklenebilir; ara boşluk ve yakında logolar aşağıdadır."
          );
        }
        return;
      }

      if (rgb && sizeId === 'h170') {
        if (labelEl) labelEl.hidden = false;
        if (catalogEl) catalogEl.hidden = false;
        if (noteEl) {
          noteEl.hidden = false;
          noteEl.textContent = t(
            this.i18n,
            'noteSpecialsH170Rgb',
            "17 cm'lik ölçüde sadece ağaç ve çift seçeneği mevcuttur."
          );
        }
        return;
      }

      if (this._isMagneticMode() && sizeId !== 'symbols') {
        if (labelEl) labelEl.hidden = true;
        if (catalogEl) catalogEl.hidden = true;
        if (noteEl) noteEl.hidden = true;
        return;
      }

      if (labelEl) labelEl.hidden = false;
      if (catalogEl) catalogEl.hidden = false;
      if (noteEl) {
        if (this._isMagneticMode() && sizeId === 'symbols') {
          noteEl.hidden = false;
          noteEl.textContent = t(
            this.i18n,
            'noteSymbols',
            'Kalp, bardak, logo (13,5 cm ve 22 cm) ve diğer özel şekiller yalnızca bu ölçüde seçilir.'
          );
        } else if (this._isSlidingMode()) {
          noteEl.hidden = false;
          noteEl.textContent = 'Kalp ve oklar seçili harf ölçüsündedir. Kare ve yuvarlak ekran yalnızca 20 cm’de görünür.';
        } else {
          noteEl.hidden = true;
        }
      }
    }

    _catalogBuckets() {
      const sizeId = this.state.sizeId;
      const entry = sizeEntry(this.charset, sizeId);
      const chars = entry?.chars || {};
      const letters = [];
      const digits = [];
      const symbols = [];
      Object.keys(chars).forEach((ch) => {
        if (ch === ' ') {
          letters.push(ch);
          return;
        }
        if (/^[0-9]$/.test(ch)) digits.push(ch);
        else if (isSymbolChar(ch, this.charset, sizeId)) symbols.push(ch);
        else letters.push(ch);
      });
      const sortAlpha = (a, b) => String(a).localeCompare(String(b), 'tr');
      letters.sort(sortAlpha);
      digits.sort();
      symbols.sort(sortAlpha);
      return {
        letters,
        digits,
        symbols,
        specials: this.specialMap?.list || [],
      };
    }

    _renderCatalogTabs() {
      if (!this.catalogTabsEl) return;
      const buckets = this._catalogBuckets();
      const tabs = [
        { id: 'letters', label: t(this.i18n, 'catalogLetters', 'Harfler'), count: buckets.letters.length },
        { id: 'digits', label: t(this.i18n, 'catalogDigits', 'Rakamlar'), count: buckets.digits.length },
        { id: 'symbols', label: t(this.i18n, 'catalogSymbols', 'Semboller'), count: buckets.symbols.length },
        { id: 'specials', label: t(this.i18n, 'catalogSpecials', 'Özel'), count: buckets.specials.length },
      ].filter((tab) => tab.count > 0);
      if (!tabs.some((tab) => tab.id === this.state.catalogTab)) {
        this.state.catalogTab = tabs[0]?.id || 'letters';
      }
      this.catalogTabsEl.innerHTML = '';
      tabs.forEach((tab) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__catalog-tab';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', tab.id === this.state.catalogTab ? 'true' : 'false');
        btn.textContent = tab.label;
        if (tab.id === this.state.catalogTab) btn.classList.add('is-active');
        btn.addEventListener('click', () => {
          this.state.catalogTab = tab.id;
          this._renderCatalogTabs();
          this._renderCatalog();
        });
        this.catalogTabsEl.appendChild(btn);
      });
    }

    _appendChar(ch) {
      if (!this.input) return;
      if (!this._isRgbMode() && Array.from(this.input.value).length >= this.maxChars) return;
      // RGB: insert at caret; keep prior text across sizes — never full-sanitize wipe.
      if (this._isRgbMode()) {
        const el = this.input;
        // selectionStart/End are UTF-16 units; emoji (🎄/👫) are surrogate pairs.
        let start = el.selectionStart ?? el.value.length;
        let end = el.selectionEnd ?? el.value.length;
        // Snap caret off surrogate midpoints so the next insert cannot split an emoji.
        start = utf16SafeBoundary(el.value, start);
        end = utf16SafeBoundary(el.value, end);
        const piece = normalizeRgbTypedChar(ch, this.charset, this.state.sizeId, this.specialByChar);
        if (piece == null) return;
        const rawMax = Number(this._typeConfig()?.maxGlyphs);
        const maxGlyphs = Number.isFinite(rawMax) ? rawMax : 0;
        const prev = el.value;
        const candidate = prev.slice(0, start) + piece + prev.slice(end);
        const cleaned = filterRgbInputChange(
          this._rgbPrevValue || prev,
          candidate,
          maxGlyphs,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
        el.value = cleaned;
        // setSelectionRange needs UTF-16 offsets — never Array.from(...).length (code points).
        const caretCp =
          Array.from(prev.slice(0, start)).length + Array.from(piece).length;
        const caret = utf16OffsetForCodePoints(cleaned, caretCp);
        try {
          el.setSelectionRange(caret, caret);
        } catch (err) {
          /* ignore */
        }
        this._onInput();
        return;
      }
      this.input.value = this._sanitizeCurrent(this.input.value + ch);
      this._onInput();
    }

    /** Insert a special; RGB auto-switches to a size that owns that special. */
    _appendSpecial(sp) {
      if (!sp) return;
      if (sp.comingSoon) {
        this._setStatus(
          t(this.i18n, 'comingSoon', 'Yakında'),
          'info'
        );
        return;
      }
      if (sp.insertSpace || sp.stem === 'SPACE' || sp.char === ' ') {
        this._appendChar(' ');
        return;
      }
      if (!sp.char) return;
      if (this._isRgbMode()) {
        const allowed = specialAllowedSizeIds(sp);
        if (allowed && allowed.length && !allowed.includes(this.state.sizeId)) {
          const next = allowed[0];
          this.state.sizeId = next;
          this.dataset.sizeId = next;
          if (this.sizesEl) {
            const btn = this.sizesEl.querySelector(`[data-size-id="${next}"]`);
            if (btn) this._setActive(this.sizesEl, btn);
          }
          this._constrainOptions(false);
          this._rebuildSpecials();
          // Keep shared RGB text across size jumps from specials
          if (this._isRgbMode()) this._rgbPrevValue = this.input?.value || '';
          else if (this.input) this.input.value = this._sanitizeCurrent(this.input.value);
          this._renderApplyControls();
          this._renderColorControls();
          this._renderSymbolControls();
          this._syncVariant();
        }
        // Always append specials at the end so a selected emoji is not replaced,
        // and caret never sits mid-surrogate after a prior emoji insert.
        if (this.input) {
          const len = this.input.value.length;
          try {
            this.input.setSelectionRange(len, len);
          } catch (err) {
            /* ignore */
          }
        }
      }
      this._appendChar(sp.char);
    }

    _fillSpecialButton(btn, sp) {
      const glyph = specialDisplayGlyph(sp);
      const files = specialPickerCandidateFiles(sp, this._glyphAssetConfig());
      const urls = files
        .map((f) => assetUrlFor(this.assetSample, f, this.charset?.version))
        .filter(Boolean);
      if (!urls.length) {
        btn.textContent = glyph;
        return;
      }
      const img = document.createElement('img');
      img.alt = sp.label || sp.stem || glyph;
      img.decoding = 'async';
      const stemKey = String(sp?.stem || '').toUpperCase();
      if (/^(SOL_OK|SAG_OK|YUKARI_OK|ASAGI_OK|OFF)$/.test(stemKey)) {
        btn.classList.add('letter-craft__symbol--plate');
      }
      const skipCrop = Boolean(
        sp.buttonFile ||
          sp.noCrop ||
          sp.comingSoon ||
          /LOGO/.test(stemKey)
      );
      // Do NOT set crossOrigin — Shopify CDN often lacks CORS; anonymous mode
      // made every picker icon fail and fall back to ◆ diamonds.
      let attempt = 0;
      const tryNext = () => {
        if (attempt >= urls.length) {
          img.remove();
          btn.textContent = glyph;
          return;
        }
        const src = urls[attempt++];
        img.src = src;
      };
      img.addEventListener('error', tryNext);
      img.addEventListener('load', () => {
        if (skipCrop) return;
        // Crop is best-effort; tainted canvas is fine — icon still visible.
        try {
          applyCroppedGlyph(img, img.currentSrc || img.src);
        } catch (e) {
          /* ignore */
        }
      });
      btn.appendChild(img);
      tryNext();
    }

    _renderCatalog() {
      if (!this.catalogEl) return;
      this.catalogEl.innerHTML = '';
      const list = this.specialMap?.list || [];
      if (!list.length) {
        this.catalogEl.hidden = true;
        return;
      }
      this.catalogEl.hidden = false;

      list.forEach((sp) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__symbol letter-craft__symbol--special';
        if (String(sp.stem || '').toUpperCase() === 'KALP' || String(sp.id || '').toUpperCase().includes('KALP')) {
          btn.classList.add('letter-craft__symbol--heart');
        }
        if (sp.comingSoon) {
          btn.classList.add('letter-craft__symbol--coming-soon');
          btn.disabled = true;
        }
        // Size only in parentheses — strip any cm already baked into the label.
        const baseLabel = String(sp.label || sp.stem || '')
          .replace(/\s*\d+[.,]?\d*\s*cm\s*$/i, '')
          .trim();
        const cm =
          sp.mm != null && Number(sp.mm) > 0
            ? ` (${Number(sp.mm) / 10} cm)`
            : '';
        const soon = sp.comingSoon
          ? ` — ${t(this.i18n, 'comingSoon', 'Yakında')}`
          : '';
        btn.setAttribute('aria-label', `${baseLabel}${cm}${soon}`);
        btn.title = `${baseLabel}${cm}${soon}`;
        this._fillSpecialButton(btn, sp);
        if (sp.comingSoon) {
          const badge = document.createElement('span');
          badge.className = 'letter-craft__coming-soon';
          badge.textContent = t(this.i18n, 'comingSoon', 'Yakında');
          btn.appendChild(badge);
        }
        btn.addEventListener('click', () => this._appendSpecial(sp));
        this.catalogEl.appendChild(btn);
      });
    }

    _renderSpecialControls() {
      if (!this.specialsEl) return;
      this.specialsEl.innerHTML = '';
      (this.specialMap?.list || []).forEach((sp) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__symbol letter-craft__symbol--special';
        const baseLabel = String(sp.label || sp.stem || '')
          .replace(/\s*\d+[.,]?\d*\s*cm\s*$/i, '')
          .trim();
        const cm =
          sp.mm != null && Number(sp.mm) > 0
            ? ` (${Number(sp.mm) / 10} cm)`
            : '';
        btn.setAttribute('aria-label', `${baseLabel}${cm}`);
        btn.title = `${baseLabel}${cm}`;
        this._fillSpecialButton(btn, sp);
        btn.addEventListener('click', () => {
          if (!this._isRgbMode() && Array.from(this.input.value).length >= this.maxChars) return;
          this._appendSpecial(sp);
        });
        this.specialsEl.appendChild(btn);
      });
    }

    _setActive(root, activeBtn) {
      root.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
      activeBtn.classList.add('is-active');
    }

    _bind() {
      this.input.addEventListener('input', () => this._onInput());
      this.input.addEventListener('scroll', () => {
        if (this.inputMirror) this.inputMirror.scrollLeft = this.input.scrollLeft;
      });
      this.input.addEventListener('blur', () => {
        if (countValidLetters(this.input.value, this.specialByChar) === 0) this.warningEl.hidden = false;
      });
      this.clearBtn?.addEventListener('click', () => {
        this.input.value = '';
        this._rgbPrevValue = '';
        this._onInput();
        this.input.focus();
      });
      this.backspaceBtn?.addEventListener('click', () => {
        this._backspaceOne();
      });
      this.resetBtn?.addEventListener('click', () => {
        if (this.input) this.input.value = '';
        this._rgbPrevValue = '';
        this._connectorOptOut = false;
        this._adapterOptOut = false;
        this._rodOptOut = false;
        this._controllerOptOut = false;
        this._shelves = emptyMagneticShelves();
        if (this.connectorCheck) this.connectorCheck.checked = false;
        if (this.adapterCheck) this.adapterCheck.checked = false;
        this._onInput();
      });
      this.connectorCheck?.addEventListener('change', () => this._render());
      this.adapterCheck?.addEventListener('change', () => this._render());
      this.connectorOptional?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // RGB: "Konnektör" button controls L25 controller include/exclude
        if (this._isRgbMode()) {
          if (!this._controllerOptOut) {
            const msg = t(
              this.i18n,
              'addonControllerRemoveWarn',
              'L25 kontrol cihazı olmadan RGB harfler uzaktan / uygulamadan yönetilemez. Kaldırmak istiyor musunuz?'
            );
            if (!window.confirm(msg)) return;
            this._controllerOptOut = true;
          } else {
            this._controllerOptOut = false;
          }
          this._render();
          return;
        }
        if (this._isMagneticMode()) {
          this._connectorOptOut = !this._connectorOptOut;
          if (this.connectorCheck) this.connectorCheck.checked = !this._connectorOptOut;
        } else {
          if (!this.connectorCheck) return;
          this.connectorCheck.checked = !this.connectorCheck.checked;
        }
        this._render();
      });
      this.adapterOptional?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._adapterOptOut = !this._adapterOptOut;
        if (this.adapterCheck) this.adapterCheck.checked = !this._adapterOptOut;
        this._render();
      });
      this.rodOptional?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._rodOptOut = !this._rodOptOut;
        this._render();
      });
      this.controllerOptional?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this._isRgbMode()) return;
        if (!this._controllerOptOut) {
          const msg = t(
            this.i18n,
            'addonControllerRemoveWarn',
            'L25 kontrol cihazı olmadan RGB harfler uzaktan / uygulamadan yönetilemez. Kaldırmak istiyor musunuz?'
          );
          if (!window.confirm(msg)) return;
          this._controllerOptOut = true;
        } else {
          this._controllerOptOut = false;
        }
        this._render();
      });
      this.querySelectorAll('[data-lc-shelf]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!this._isMagneticMode()) return;
          const id = btn.getAttribute('data-lc-shelf');
          if (!MAGNETIC_SHELF_IDS.includes(id)) return;
          if (!this._shelves) this._shelves = emptyMagneticShelves();
          this._shelves[id] = !this._shelves[id];
          this._render();
        });
      });
      this.querySelectorAll('[data-lc-shelf-info]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._openShelfInfo(btn.getAttribute('data-lc-shelf-info'));
        });
      });
      this.querySelector('[data-lc-shelf-dialog-close]')?.addEventListener('click', (e) => {
        e.preventDefault();
        this._closeShelfInfo();
      });
      this.shelfDialog?.addEventListener('click', (e) => {
        if (e.target === this.shelfDialog) this._closeShelfInfo();
      });
      this._bindShelfZoom();
      this._bindNativeCart();
      window.addEventListener('resize', () => {
        const nextMobile = window.matchMedia('(max-width: 749px)').matches;
        const flipped = nextMobile !== this.isMobile;
        this.isMobile = nextMobile;
        // Magnetic/RGB/sliding: Masaüstü Boyut ↔ Mobil Boyut — re-scale on breakpoint flip.
        if (
          flipped &&
          (this._isMagneticMode() || this._isRgbMode() || this._isSlidingMode() || this._isFloorMode())
        ) {
          this._render();
        } else {
          this._applyScenePosition();
          this._fitPreview();
        }
      });
    }

    _mainLineQty(plan) {
      if (plan?.floor) return Number(plan.analysis?.glyphs || 0);
      if (plan?.rgb) {
        const glyphs = Number(plan.analysis?.glyphs || 0);
        const spaces = Number(plan.analysis?.spaces || 0);
        // RGB UI "Konnektör" = L25 controller; count it when included.
        const connector = plan.includeController ? 1 : 0;
        return glyphs + spaces + connector;
      }
      return plan?.analysis?.billable || 0;
    }

    _addonPlan() {
      const text = this.input?.value || '';
      const analysis = analyzeText(text, this.specialByChar);
      const addons = this.config.addons || {};

      if (this._isRgbMode()) {
        const maxGlyphs = Number(this._typeConfig()?.maxGlyphs) || 0;
        // Full mixed string (all size classes) — do not strip other sizes.
        const sizedText = clampRgbGlyphs(text, maxGlyphs);
        const sized = analyzeText(sizedText, this.specialByChar);
        analysis.letters = sized.letters;
        analysis.symbols = sized.symbols;
        analysis.spaces = sized.spaces;
        analysis.specials = sized.specials;
        analysis.glyphs = sized.glyphs;
        analysis.billable = sized.glyphs + sized.spaces;
        analysis.hasLetters = sized.hasLetters;
        const buckets = rgbGlyphSizeBuckets(sizedText, this.charset, this.specialByChar);
        const controllerId = addons.controllerVariantId ? Number(addons.controllerVariantId) : null;
        const spacerId = spacerVariantIdForSize(addons, this.state.sizeId);
        const hasContent = analysis.glyphs > 0 || analysis.spaces > 0;
        // RGB: Konnektör = L25 controller (preview, length, price, cart). Power Adaptör is RGB-only off.
        const optionalController = hasContent && Boolean(controllerId);
        const includeController = optionalController && !this._controllerOptOut;
        const rgbMeasure = measureRgbText(sizedText, this.charset, this.state.sizeId, this.specialByChar, {
          includeController,
        });
        analysis.railWidthMm = rgbMeasure.widthMm;
        return {
          analysis,
          rgbMeasure,
          railMeasure: rgbMeasure,
          rgbBuckets: buckets,
          adapterKey: null,
          adapterId: null,
          connectorId: null,
          controllerId: includeController ? controllerId : null,
          spacerId: analysis.spaces > 0 && spacerId ? spacerId : null,
          spacerQty: analysis.spaces,
          autoConnector: false,
          optionalConnector: false,
          optionalController,
          optionalAdapter: false,
          includeConnector: false,
          includeAdapter: false,
          includeController,
          includeSpacer: analysis.spaces > 0 && Boolean(spacerId),
          showDimmerPreview: false,
          showControllerPreview: includeController,
          showSpacerPreview: analysis.spaces > 0,
          hasConnectorProduct: false,
          rgb: true,
        };
      }

      if (this._isFloorMode()) {
        const counts = floorColorCounts(text, this.state.charColors, this.state.colorId);
        const glyphs = analysis.glyphs;
        const spaces = analysis.spaces;
        const floorSupplies = [];
        if (counts.white > 0) {
          const key = floorSupplyKey('white', counts.white);
          if (key) floorSupplies.push(key);
        }
        if (counts.rgb > 0) {
          const key = floorSupplyKey('rgb', counts.rgb);
          if (key) floorSupplies.push(key);
        }
        const supplyKey = floorSupplies[0] || null;
        const supply = supplyKey ? this._floorVariantByRole(supplyKey) : null;
        const spacer = this._floorVariantByRole('spacer');
        const includeAdapter = glyphs > 0 && !this._adapterOptOut;
        if (this.adapterCheck) this.adapterCheck.checked = includeAdapter;
        const railMeasure = measureRailText(
          text,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
        const colorId = counts.rgb > 0 && counts.white === 0
          ? 'rgb'
          : counts.white > 0 && counts.rgb === 0
            ? 'white'
            : (this.state.colorId === 'rgb' ? 'rgb' : 'white');
        return {
          analysis,
          railMeasure,
          adapterKey: supplyKey,
          adapterId: includeAdapter && supply?.id ? Number(supply.id) : null,
          floorSupplies,
          floorWhiteQty: counts.white,
          floorRgbQty: counts.rgb,
          connectorId: null,
          autoConnector: false,
          optionalConnector: false,
          optionalAdapter: glyphs > 0,
          includeConnector: false,
          includeAdapter,
          includeSpacer: spaces > 0,
          spacerId: spaces > 0 && spacer?.id ? Number(spacer.id) : null,
          spacerQty: spaces,
          showDimmerPreview: includeAdapter,
          showSpacerPreview: spaces > 0,
          hasConnectorProduct: false,
          magnetic: false,
          floor: true,
          floorColor: colorId,
        };
      }

      const rail = this._isRailMode();
      let railMeasure = { widthMm: 0, powerW: 0, count: 0 };
      if (rail) {
        railMeasure = measureRailText(text, this.charset, this.state.sizeId, this.specialByChar);
        analysis.railWidthMm = railMeasure.widthMm;
        analysis.railPowerW = railMeasure.powerW;
        analysis.hasLetters = railMeasure.count > 0;
        analysis.billable = railMeasure.count;
        analysis.glyphs = railMeasure.count;
        const bill = slidingBillable(text, this.charset, this.state.sizeId, this.specialByChar);
        analysis.spaces = bill.spaces;
        analysis.billable = bill.paid;
        analysis.glyphs = bill.paid;
        analysis.hasLetters = bill.paid > 0;
      }

      const floor = this._isFloorMode();
      const powerRules = floor
        ? this.config.floorAdapterRules || this.config.railAdapterRules
        : this.config.railAdapterRules;
      const adapterKey = rail
        ? analysis.hasLetters
          ? adapterKeyForPower(railMeasure.powerW, powerRules)
          : null
        : analysis.hasLetters
          ? adapterKeyForCount(analysis.glyphs, this.config.adapterRules)
          : null;
      const adapterIdRaw = adapterKey ? addons.adapters?.[adapterKey] : null;
      const connectorId = addons.connectorVariantId || null;
      const magnetic = !rail && !floor;
      // Rayda konnektör yok. Manyetik: harf eklenince otomatik (isteğe bağlı çıkarılır).
      // Zemin: isteğe bağlı opt-in.
      const optionalConnector = (!rail || floor) && Boolean(connectorId);
      const wantFloorConnector = Boolean(this.connectorCheck?.checked);
      let includeConnector = false;
      let autoConnector = false;
      if (magnetic && analysis.hasLetters && connectorId) {
        autoConnector = true;
        includeConnector = !this._connectorOptOut;
        if (this.connectorCheck) this.connectorCheck.checked = includeConnector;
      } else if (floor && optionalConnector) {
        includeConnector = wantFloorConnector;
      }

      if (magnetic) {
        railMeasure = measureMagneticText(
          text,
          this.charset,
          this._activeCharsetSizeId(),
          this.specialByChar,
          { includeConnector }
        );
        analysis.railWidthMm = railMeasure.widthMm;
        analysis.railPowerW = railMeasure.powerW;
      }

      const canAdapter = analysis.hasLetters && Boolean(adapterIdRaw);
      let includeAdapter = false;
      let optionalAdapter = false;
      if (magnetic && canAdapter) {
        optionalAdapter = true;
        includeAdapter = !this._adapterOptOut;
        if (this.adapterCheck) this.adapterCheck.checked = includeAdapter;
      } else if (rail && canAdapter) {
        optionalAdapter = true;
        includeAdapter = !this._adapterOptOut;
        if (this.adapterCheck) this.adapterCheck.checked = includeAdapter;
      } else if (!magnetic && canAdapter) {
        includeAdapter = true;
      }

      const showDimmerPreview = includeConnector;
      const sliding = rail && !floor;
      let rodCm = 0;
      let rodVariantId = null;
      let includeRod = false;
      let optionalRod = false;
      if (sliding && analysis.hasLetters) {
        const stock = railStockMm(railMeasure.widthMm);
        rodCm = stock ? stock / 10 : 0;
        const rodId = this.config.addons?.railRods?.[String(rodCm)];
        optionalRod = Boolean(rodId);
        includeRod = optionalRod && !this._rodOptOut;
        rodVariantId = includeRod ? Number(rodId) : null;
      }

      return {
        analysis,
        railMeasure,
        adapterKey,
        adapterId: includeAdapter
          ? Number((sliding && addons.railAdapters?.[adapterKey]) || adapterIdRaw) || null
          : null,
        connectorId: includeConnector && connectorId ? Number(connectorId) : null,
        autoConnector,
        optionalConnector: magnetic ? Boolean(connectorId && analysis.hasLetters) : optionalConnector && floor,
        optionalAdapter,
        includeConnector,
        includeAdapter,
        includeSpacer: sliding ? false : analysis.spaces > 0,
        showDimmerPreview,
        showSpacerPreview: sliding ? false : analysis.spaces > 0,
        hasConnectorProduct: Boolean(connectorId),
        magnetic,
        sliding,
        rodCm,
        rodVariantId,
        includeRod,
        optionalRod,
        shelves: magnetic ? this._selectedShelfItems() : [],
      };
    }

    _selectedShelfItems() {
      const ids = this.config.addons?.shelves || {};
      return MAGNETIC_SHELF_IDS.filter((id) => this._shelves?.[id])
        .map((id) => ({
          id,
          variantId: Number(ids[id]) || 0,
          label: MAGNETIC_SHELF_LABELS[id]?.cart || id,
        }))
        .filter((row) => row.variantId > 0);
    }

    _openShelfInfo(infoId) {
      const pack = MAGNETIC_SHELF_INFO[infoId];
      if (!pack || !this.shelfDialog) return;
      const src = this.config.shelfInfoAssets?.[infoId] || '';
      if (this.shelfDialogImg) {
        this.shelfDialogImg.hidden = !src;
        this.shelfDialogImg.src = src || '';
        this.shelfDialogImg.alt = pack.title;
        this.shelfDialogImg.width = 1024;
        this.shelfDialogImg.height = infoId === 'lip' ? 547 : infoId === 'clearGlass' ? 914 : 918;
      }
      if (this.shelfDialogTitle) {
        this.shelfDialogTitle.textContent = pack.title;
        this.shelfDialogTitle.hidden = Boolean(src);
      }
      if (this.shelfDialogBody) {
        this.shelfDialogBody.textContent = pack.body;
        this.shelfDialogBody.hidden = Boolean(src);
      }
      this.shelfDialog.classList.toggle('is-image', Boolean(src));
      if (typeof this.shelfDialog.showModal === 'function') this.shelfDialog.showModal();
      else this.shelfDialog.hidden = false;
    }

    _closeShelfInfo() {
      if (!this.shelfDialog) return;
      const zoom = this.querySelector('[data-lc-shelf-zoom]');
      zoom?.classList.remove('is-zooming');
      if (this.shelfDialogImg) this.shelfDialogImg.style.transformOrigin = 'center center';
      if (typeof this.shelfDialog.close === 'function' && this.shelfDialog.open) this.shelfDialog.close();
      else this.shelfDialog.hidden = true;
    }

    _bindShelfZoom() {
      const host = this.querySelector('[data-lc-shelf-zoom]');
      const img = this.shelfDialogImg;
      if (!host || !img) return;
      const apply = (e) => {
        if (window.matchMedia('(max-width: 749px)').matches) return;
        if (img.hidden) return;
        const r = host.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
        host.classList.add('is-zooming');
      };
      host.addEventListener('mouseenter', apply);
      host.addEventListener('mousemove', apply);
      host.addEventListener('mouseleave', () => {
        host.classList.remove('is-zooming');
        img.style.transformOrigin = 'center center';
      });
    }

    _syncShelfButtons() {
      const magnetic = this._isMagneticMode();
      if (this.shelvesWrap) this.shelvesWrap.hidden = !magnetic;
      this.querySelectorAll('[data-lc-shelf]').forEach((btn) => {
        const id = btn.getAttribute('data-lc-shelf');
        const on = Boolean(magnetic && this._shelves?.[id]);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = btn.querySelector('[data-lc-shelf-label]');
        const copy = MAGNETIC_SHELF_LABELS[id];
        if (label && copy) paintShelfLabel(label, on ? copy.on : copy.off, copy.cm);
        const priceEl = btn.querySelector('[data-lc-shelf-price]');
        if (priceEl) {
          const usd = magneticAddonUsd(this.config, 'shelf', id);
          if (usd != null) {
            priceEl.hidden = false;
            priceEl.textContent = formatMoney(usdToTryCents(usd, this.config), this.moneyFormat);
          } else {
            priceEl.hidden = true;
            priceEl.textContent = '';
          }
        }
      });
    }

    _getProductForms() {
      const scope =
        this.closest('.product-information') ||
        this.closest('product-info') ||
        this.closest('.product') ||
        document;
      const forms = [
        ...scope.querySelectorAll('form[action*="/cart/add"]'),
        ...document.querySelectorAll(
          'sticky-add-to-cart form[action*="/cart/add"], .sticky-add-to-cart form[action*="/cart/add"], [data-sticky-add-to-cart] form[action*="/cart/add"]'
        ),
      ];
      return [...new Set(forms)];
    }

    _lineProperties() {
      const plan = this._addonPlan();
      const count = this._mainLineQty(plan);
      const text = normalizeDisplayText(
        this.input.value,
        this.specialByChar,
        this.charset,
        this.state.sizeId
      ).trim();
      const i18n = this.i18n;
      const size = this._sizeConfig();
      const color = this._colorConfig();
      const type = this._typeConfig();
      const apply = (this.config.applies || []).find((a) => a.id === this.state.applyId);
      const scene = this._currentScene();
      const props = {};
      if (count <= 0) return props;

      // Customer-visible properties only — always the full mixed composed string
      props[t(i18n, 'propCustomText', 'Özel Metin')] = text;
      props[t(i18n, 'propLetterCount', 'Harf Sayısı')] = String(count);
      props[t(i18n, 'propType', 'Ürün Tipi')] = type?.label || this.state.typeId;
      if (plan?.rgb && plan.rgbBuckets) {
        const parts = [];
        if (plan.rgbBuckets.h200.glyphs) parts.push(`20 cm×${plan.rgbBuckets.h200.glyphs}`);
        if (plan.rgbBuckets.h170.glyphs) parts.push(`17 cm×${plan.rgbBuckets.h170.glyphs}`);
        if (plan.rgbBuckets.h120.glyphs) parts.push(`12 cm×${plan.rgbBuckets.h120.glyphs}`);
        props[t(i18n, 'propSize', 'Ölçü')] =
          parts.length > 1
            ? `${t(i18n, 'propSizeMixed', 'Karışık')} (${parts.join(', ')})`
            : size?.label || this.state.sizeId;
        props['_lc_sizes'] = parts.join('|') || String(this.state.sizeId);
      } else if (this._isMagneticMode() && this.state.sizeId === 'symbols') {
        const letterSize =
          (this.config.sizes || []).find((s) => s.id === this._activeCharsetSizeId()) || size;
        props[t(i18n, 'propSize', 'Ölçü')] =
          letterSize?.label || this._activeCharsetSizeId();
      } else {
        props[t(i18n, 'propSize', 'Ölçü')] = size?.label || this.state.sizeId;
      }
      if (apply) {
        props[t(i18n, 'propApply', 'Uygulama')] = apply.label || this.state.applyId;
      }
      if (plan?.floor && (plan.floorWhiteQty > 0 || plan.floorRgbQty > 0)) {
        const labels = [];
        if (plan.floorWhiteQty > 0) labels.push('Beyaz');
        if (plan.floorRgbQty > 0) labels.push('Renkli');
        props[t(i18n, 'propColor', 'Renk')] = labels.join(' + ');
      } else if (
        this._isMagneticMode() &&
        magneticSupportsMixedColors(this.config, this.state.sizeId) &&
        Array.isArray(this.state.charColors) &&
        this.state.charColors.length
      ) {
        const used = [...new Set(this.state.charColors.filter((c) => c && c !== ' '))];
        const labels = used.map((id) => {
          const c = (this.config.colors || []).find((x) => x.id === id);
          return c?.label || id;
        });
        props[t(i18n, 'propColor', 'Renk')] = labels.length ? labels.join(' + ') : (color?.label || this.state.colorId);
      } else {
        props[t(i18n, 'propColor', 'Renk')] = color?.label || this.state.colorId;
      }

      // Underscore = hidden in customer cart/checkout; still on order in admin
      props['_lc'] = '1';
      props['_Sahne'] = scene?.alt || scene?.id || '';
      props['_lc_text'] = text;
      return props;
    }

    _rgbVariantForSizeId(sizeId) {
      const size =
        (this.config.sizes || []).find((s) => s.id === sizeId) ||
        { id: sizeId, match: [sizeId, String(sizeId).replace(/^h/, '') + ' cm'] };
      const type = this._typeConfig();
      return findVariantForSize(this.config.variants || [], size, type?.id || 'rgb');
    }

    _syncNativeForm() {
      const plan = this._addonPlan();
      const count = this._mainLineQty(plan);
      const canAdd = this.available && count > 0 && Boolean(this.variant?.id);
      const props = this._lineProperties();
      const qty = Math.max(count, 1);

      this._getProductForms().forEach((form) => {
        const idInput = form.querySelector('[name="id"]');
        if (idInput && this.variant?.id) {
          idInput.value = String(this.variant.id);
          idInput.dispatchEvent(new Event('input', { bubbles: true }));
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        form.querySelectorAll('[name="quantity"]').forEach((q) => {
          q.value = String(qty);
          q.dispatchEvent(new Event('input', { bubbles: true }));
          q.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const qtyComponent = form.querySelector('quantity-selector-component, quantity-input');
        if (qtyComponent) {
          if ('value' in qtyComponent) qtyComponent.value = qty;
          qtyComponent.setAttribute('value', String(qty));
        }

        form.querySelectorAll('[data-lc-property]').forEach((el) => el.remove());
        Object.entries(props).forEach(([key, val]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = String(val);
          input.dataset.lcProperty = '1';
          form.appendChild(input);
        });

        form.querySelectorAll('button[type="submit"], button[name="add"]').forEach((btn) => {
          btn.disabled = !canAdd;
          if (canAdd) btn.removeAttribute('aria-disabled');
          else btn.setAttribute('aria-disabled', 'true');
        });
      });

      // Sticky / theme ATC buttons that may sit outside the synced forms
      document
        .querySelectorAll(
          'body:has(.letter-craft) sticky-add-to-cart button[type="submit"], body:has(.letter-craft) .sticky-add-to-cart button[type="submit"]'
        )
        .forEach((btn) => {
          btn.disabled = !canAdd;
        });

      this._syncStandaloneBuyButtons();
    }

    _bindNativeCart() {
      const isOurCartForm = (form) =>
        form instanceof HTMLFormElement &&
        String(form.getAttribute('action') || '').includes('/cart/add') &&
        this._getProductForms().includes(form);

      const isOurAtcButton = (el) => {
        if (!(el instanceof Element)) return false;
        const btn = el.closest(
          'button[type="submit"], button[name="add"], button.product-form__submit, [name="add"], buy-buttons-component button, sticky-add-to-cart button, .sticky-add-to-cart button'
        );
        if (!btn) return false;
        const form = btn.closest('form');
        if (form && isOurCartForm(form)) return true;
        if (
          document.body.contains(this) &&
          btn.closest('sticky-add-to-cart, .sticky-add-to-cart, [data-sticky-add-to-cart]')
        ) {
          return true;
        }
        return false;
      };

      const guard = (event) => {
        const target = event.target;
        if (!(target instanceof Element) && !(target instanceof HTMLFormElement)) return;

        if (event.type === 'submit') {
          const form = target instanceof HTMLFormElement ? target : target.closest?.('form');
          if (!isOurCartForm(form)) return;
          event.preventDefault();
          event.stopPropagation();
          this._addWithAddons();
          return;
        }

        if (!isOurAtcButton(target)) return;
        event.preventDefault();
        event.stopPropagation();
        this._addWithAddons();
      };

      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }

    async _addWithAddons(opts = {}) {
      const checkout = Boolean(opts?.checkout);
      this._syncVariant();
      const plan = this._addonPlan();
      const count = this._mainLineQty(plan);
      const i18n = this.i18n;

      if (!this.available || count <= 0 || !this.variant?.id) {
        this.warningEl.hidden = false;
        this._setStatus(t(i18n, 'warningEmpty', 'Lütfen metin girin.'), 'error');
        this.input?.focus();
        return;
      }

      const props = this._lineProperties();
      const items = [];
      if (plan.rgb) {
        const buckets = plan.rgbBuckets || rgbGlyphSizeBuckets(
          this.input.value,
          this.charset,
          this.specialByChar
        );
        const kalpVar = findKalpVariant(this.config.variants || [], 'rgb');
        const pushSized = (sizeId, qty, extraProps) => {
          if (qty <= 0) return;
          const variant =
            sizeId === String(this.state.sizeId)
              ? this.variant
              : this._rgbVariantForSizeId(sizeId);
          const id = Number(variant?.id || this.variant?.id);
          if (!id) return;
          items.push({
            id,
            quantity: qty,
            properties: {
              ...props,
              [t(i18n, 'propSize', 'Ölçü')]:
                (this.config.sizes || []).find((s) => s.id === sizeId)?.label || sizeId,
              _lc_size: sizeId,
              ...(extraProps || {}),
            },
          });
        };
        pushSized('h200', buckets.h200.glyphs);
        pushSized('h170', buckets.h170.glyphs);
        if (buckets.h120.letters > 0) pushSized('h120', buckets.h120.letters);
        if (buckets.h120.hearts > 0 && kalpVar?.id) {
          items.push({
            id: Number(kalpVar.id),
            quantity: buckets.h120.hearts,
            properties: {
              ...props,
              [t(i18n, 'propType', 'Ürün Tipi')]: 'Kalp',
              [t(i18n, 'propSize', 'Ölçü')]:
                (this.config.sizes || []).find((s) => s.id === 'h120')?.label || 'h120',
              _lc_special: 'kalp',
              _lc_size: 'h120',
            },
          });
        } else if (buckets.h120.hearts > 0) {
          pushSized('h120', buckets.h120.hearts, { _lc_special: 'kalp' });
        }
        if (!items.length) {
          items.push({
            id: Number(this.variant.id),
            quantity: count,
            properties: props,
          });
        }
      } else if (plan.floor) {
        const whiteV = plan.floorWhiteQty > 0 ? this._floorVariantByRole('letter-white') : null;
        const rgbV = plan.floorRgbQty > 0 ? this._floorVariantByRole('letter-rgb') : null;
        if (whiteV?.id) {
          items.push({
            id: Number(whiteV.id),
            quantity: plan.floorWhiteQty,
            properties: { ...props, [t(i18n, 'propColor', 'Renk')]: 'Beyaz' },
          });
        }
        if (rgbV?.id) {
          items.push({
            id: Number(rgbV.id),
            quantity: plan.floorRgbQty,
            properties: { ...props, [t(i18n, 'propColor', 'Renk')]: 'Renkli' },
          });
        }
      } else {
        items.push({
          id: Number(this.variant.id),
          quantity: count,
          properties: props,
        });
      }

      if (plan.sliding && plan.rodVariantId) {
        items.push({
          id: Number(plan.rodVariantId),
          quantity: 1,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: 'Tavan asma çubuğu',
            [t(i18n, 'propSize', 'Ölçü')]: `${plan.rodCm} cm`,
            _lc_addon: 'rail-rod',
          },
        });
      }

      if (plan.connectorId) {
        items.push({
          id: plan.connectorId,
          quantity: 1,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: t(i18n, 'addonDimmerLabel', 'Konnektör'),
            _lc_addon: 'connector',
          },
        });
      }
      if (plan.floor && plan.includeAdapter && Array.isArray(plan.floorSupplies) && plan.floorSupplies.length) {
        plan.floorSupplies.forEach((key) => {
          const supply = this._floorVariantByRole(key);
          if (!supply?.id) return;
          items.push({
            id: Number(supply.id),
            quantity: 1,
            properties: {
              [t(i18n, 'propType', 'Ürün Tipi')]: `Güç kaynağı ${floorSupplyLabel(key)}`,
              _lc_addon: 'psu',
              _lc_adapter: key,
            },
          });
        });
      } else if (plan.adapterId) {
        items.push({
          id: plan.adapterId,
          quantity: 1,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: plan.floor
              ? `Güç kaynağı ${floorSupplyLabel(plan.adapterKey)}`
              : `Adaptör ${String(plan.adapterKey || '').toUpperCase()}`,
            _lc_addon: plan.floor ? 'psu' : 'adapter',
            _lc_adapter: plan.adapterKey || '',
          },
        });
      }
      if (plan.controllerId) {
        items.push({
          id: plan.controllerId,
          quantity: 1,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: t(i18n, 'addonControllerLabel', 'L25 Kontrol Cihazı'),
            _lc_addon: 'controller',
          },
        });
      }
      if (plan.spacerId && plan.spacerQty > 0) {
        items.push({
          id: plan.spacerId,
          quantity: plan.spacerQty,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: plan.floor
              ? 'Ara Boşluk'
              : t(i18n, 'addonSpacerLabel', 'Manyetik Boşluk'),
            _lc_addon: 'spacer',
          },
        });
      }
      (plan.shelves || []).forEach((row) => {
        if (!row?.variantId) return;
        items.push({
          id: Number(row.variantId),
          quantity: 1,
          properties: {
            [t(i18n, 'propType', 'Ürün Tipi')]: row.label,
            _lc_addon: 'shelf',
            _lc_shelf: row.id,
          },
        });
      });

      this._setStatus(t(i18n, 'adding', 'Ekleniyor…'), 'success');
      try {
        const sectionIds = cartSectionIds();
        const payload = { items };
        if (sectionIds.length) {
          payload.sections = sectionIds.join(',');
          payload.sections_url = window.location.pathname;
        }
        const res = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.description || data.message || t(i18n, 'addError', 'Sepete eklenemedi.'));
        }
        this._setStatus(t(i18n, 'added', 'Sepete eklendi.'), 'success');
        const lines = items.map((it) => ({
          merchandiseId: String(it.id),
          quantity: Number(it.quantity) || 1,
        }));
        await notifyHorizonCart(data, lines);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data, bubbles: true }));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'letter-craft', cartData: data });
          } catch (_) {}
        }
        if (checkout) {
          window.location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
          return;
        }
        // Drawer auto-opens via CartLinesUpdateEvent (auto-open). Fallback click if needed.
        try {
          const drawer = document.querySelector('cart-drawer-component');
          const themeDrawer = drawer?.closest?.('theme-drawer') || document.getElementById('cart-drawer');
          if (themeDrawer && !themeDrawer.hasAttribute?.('open') && !themeDrawer.open) {
            document
              .querySelector('[data-testid="cart-drawer-trigger"], .header-actions__action[aria-controls="cart-drawer"]')
              ?.click?.();
          }
        } catch (_) {}
      } catch (err) {
        this._setStatus(err.message || t(i18n, 'genericError', 'Bir hata oluştu.'), 'error');
      }
    }

    _hasSpecialChars(text) {
      return textNeedsDisplayMirror(
        text,
        this.specialByChar,
        this.charset,
        this.state?.sizeId
      );
    }

    _ensureMirrorMeasure() {
      if (this._mirrorMeasureEl) return this._mirrorMeasureEl;
      const el = document.createElement('span');
      el.className = 'letter-craft__input-measure';
      el.setAttribute('aria-hidden', 'true');
      (this.input?.parentElement || this).appendChild(el);
      this._mirrorMeasureEl = el;
      return el;
    }

    /** Advance width of a storage char in the same font as the input (keeps caret aligned). */
    _measureInputCharWidth(ch, fallbackGlyph) {
      const measure = this._ensureMirrorMeasure();
      const input = this.input;
      if (input) {
        const cs = getComputedStyle(input);
        measure.style.font = cs.font;
        measure.style.letterSpacing = cs.letterSpacing;
        measure.style.fontSize = cs.fontSize;
      }
      measure.textContent = ch === ' ' ? '\u00a0' : ch || '';
      let w = measure.getBoundingClientRect().width;
      // PUA / missing glyphs often report ~0 — use display emoji width instead
      if (w < 1.5 && fallbackGlyph) {
        measure.textContent = fallbackGlyph;
        w = measure.getBoundingClientRect().width;
      }
      if (w < 1.5) {
        measure.textContent = 'M';
        w = measure.getBoundingClientRect().width || 10;
      }
      return w;
    }

    _syncInputMirror() {
      if (!this.inputMirror || !this.input) return;
      const raw = this.input.value;
      const useMirror = this._hasSpecialChars(raw);
      this.input.classList.toggle('letter-craft__input--mirrored', useMirror);
      this.inputMirror.hidden = !useMirror;
      if (!useMirror) {
        this.inputMirror.textContent = '';
        return;
      }
      // Keep mirror typography locked to the textarea (emoji bleed / nesting otherwise).
      const ics = getComputedStyle(this.input);
      this.inputMirror.style.font = ics.font;
      this.inputMirror.style.fontSize = ics.fontSize;
      this.inputMirror.style.lineHeight = ics.lineHeight;
      this.inputMirror.style.letterSpacing = ics.letterSpacing;
      this.inputMirror.style.fontFamily = `${ics.fontFamily}, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;

      const frag = document.createDocumentFragment();
      const glyphSizeId = this._activeCharsetSizeId();
      for (const ch of Array.from(raw)) {
        const shown = charDisplayGlyph(ch, this.specialByChar, this.charset, glyphSizeId);
        const needsGlyph = shown !== ch || isMappedSpecial(ch, this.specialByChar);
        const slot = document.createElement('span');
        slot.className = 'letter-craft__input-slot';
        // Storage advance keeps caret aligned; for specials also reserve display ink width
        // so emoji do not paint over neighbours (Apple/Segoe emoji overhang).
        const storeWidth = this._measureInputCharWidth(ch, needsGlyph ? shown : null);
        const displayWidth = needsGlyph
          ? this._measureInputCharWidth(shown, null)
          : storeWidth;
        const slotWidth = needsGlyph ? Math.max(storeWidth, displayWidth) : storeWidth;
        slot.style.width = `${slotWidth}px`;
        slot.style.height = ics.lineHeight;
        if (needsGlyph) {
          slot.classList.add('letter-craft__input-special');
          const spMeta = this.specialByChar?.get(ch);
          const stem =
            stemForChar(this.charset, glyphSizeId, ch) ||
            this.charset?.symbolStemByChar?.[ch] ||
            spMeta?.stem ||
            null;
          const boxy = /^[⬜⚪⬛▣▤▥▦▧▨▩▪▫◈◇◆○◎●🔵✦◻◼◽◾]$/.test(shown);
          if (boxy && spMeta?.file) {
            const img = document.createElement('img');
            img.alt = '';
            img.draggable = false;
            img.src = assetUrlFor(this.assetSample, spMeta.file, this.charset?.version);
            slot.appendChild(img);
          } else {
          if (
            emojiForStem(stem) === '💖' ||
            emojiForStem(stem) === '❤️' ||
            ch === '♥' ||
            ch === '💖' ||
            shown === '❤️' ||
            shown === '❤' ||
            shown === '💖'
          ) {
            slot.classList.add('letter-craft__input-special--red');
          }
            slot.textContent = shown;
            if (displayWidth > slotWidth + 0.5 && displayWidth > 0) {
              slot.style.fontSize = `calc(${ics.fontSize} * ${slotWidth / displayWidth})`;
            }
          }
        } else {
          slot.textContent = ch === ' ' ? '\u00a0' : ch;
        }
        frag.appendChild(slot);
      }
      this.inputMirror.replaceChildren(frag);
      this.inputMirror.scrollLeft = this.input.scrollLeft;
    }

    _onInput() {
      const next = this.input.value;
      let cleaned;
      if (this._isRgbMode()) {
        const rawMax = Number(this._typeConfig()?.maxGlyphs);
        const maxGlyphs = Number.isFinite(rawMax) ? rawMax : 0;
        cleaned = filterRgbInputChange(
          this._rgbPrevValue || '',
          next,
          maxGlyphs,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
        this._rgbPrevValue = cleaned;
      } else if (this._isMagneticMode() && magneticSupportsMixedColors(this.config, this.state.sizeId)) {
        const filtered = filterMagneticColorInput(
          this.state.text || '',
          this.state.charColors || [],
          next,
          this.state.colorId || 'white',
          this.maxChars,
          this.charset,
          this.state.sizeId,
          this.specialByChar
        );
        cleaned = filtered.text;
        this.state.charColors = filtered.colors;
      } else {
        cleaned = this._sanitizeCurrent(next);
      }
      if (cleaned !== this.input.value) this.input.value = cleaned;
      this.state.text = cleaned;
      if (this.clearBtn) this.clearBtn.hidden = true;
      this._syncBackspaceVisibility();
      this._clearStatus();
      this._syncVariant();
      this._render();
    }

    /** Remove one visible character (emoji / special glyph = one step). */
    _backspaceOne() {
      if (!this.input) return;
      const chars = Array.from(this.input.value);
      if (!chars.length) return;
      chars.pop();
      const next = chars.join('');
      this.input.value = next;
      if (this._isRgbMode()) this._rgbPrevValue = next;
      if (Array.isArray(this.state.charColors) && this.state.charColors.length) {
        this.state.charColors = this.state.charColors.slice(0, chars.length);
      }
      this.state.text = next;
      this._onInput();
      this.input.focus();
    }

    _syncVariant() {
      const size = this._sizeConfig();
      const type = this._typeConfig();
      const preferredType = type?.id || size?.type || null;
      const variants = this.config.variants || [];
      // Virtual "Semboller" has no Shopify variant — keep last real letter size
      // (variantSizeId is only a legacy fallback when no letter size was chosen yet).
      let variantSize = size;
      if (this._isMagneticMode() && this.state.sizeId === 'symbols') {
        const letterId = this._activeCharsetSizeId();
        variantSize =
          (this.config.sizes || []).find((s) => s.id === letterId) ||
          (size?.variantSizeId
            ? (this.config.sizes || []).find((s) => s.id === size.variantSizeId)
            : null) ||
          size;
      } else if (size?.variantSizeId) {
        variantSize =
          (this.config.sizes || []).find((s) => s.id === size.variantSizeId) || size;
      }
      const match = findVariantForSize(variants, variantSize, preferredType);
      const prevId = this._lastVariantId;

      this.variant = match;
      this.unitPrice = Number(match?.price || 0);
      this.available = Boolean(match?.available);

      // Magnetic app list prices (USD→TL); do not change Shopify Admin product prices
      if (this._isMagneticMode() && magneticPriceTables(this.config)) {
        const usd = magneticLetterUsd(
          this.config,
          this.state.colorId,
          this._activeCharsetSizeId()
        );
        if (usd != null) this.unitPrice = usdToTryCents(usd, this.config);
      }

      let activeVariant = match;
      if (this._isFloorMode()) {
        const counts = floorColorCounts(this.input?.value || '', this.state.charColors, this.state.colorId);
        const whiteV = counts.white > 0 ? this._floorVariantByRole('letter-white') : null;
        const rgbV = counts.rgb > 0 ? this._floorVariantByRole('letter-rgb') : null;
        const picked = whiteV || rgbV;
        if (picked) activeVariant = picked;
        this.variant = activeVariant;
        const whiteOk = !whiteV || Boolean(whiteV.available);
        const rgbOk = !rgbV || Boolean(rgbV.available);
        this.available = Boolean(activeVariant) && whiteOk && rgbOk;
        const letterUsd = this.config.floorPrices?.letters?.[
          this.state.colorId === 'rgb' ? 'rgb' : 'white'
        ];
        if (letterUsd != null) this.unitPrice = usdToTryCents(letterUsd, this.config);
      }

      if (!activeVariant && size) {
        this._lastVariantId = null;
        this._setStatus(
          t(this.i18n, 'variantMissing', 'Varyant bulunamadı. Ölçü seçeneklerini kontrol edin.'),
          'error'
        );
        return;
      }

      if (activeVariant && String(activeVariant.id) !== String(prevId || '')) {
        this._lastVariantId = activeVariant.id;
        this.variant = activeVariant;
        this._clearStatus();
        this._publishVariantChange(activeVariant);
      }
    }

    _publishVariantChange(variant) {
      if (!variant?.id) return;
      const priceText = formatMoney(variant.price, this.moneyFormat);

      document
        .querySelectorAll(
          [
            '.product-details .price-item--regular',
            '.product-details .price__regular .price-item',
            '.product-information .price-item--regular',
            '.product-information [data-product-price]',
            'product-price .price',
            '.product-details .price .money',
            'sticky-add-to-cart .price',
            '.sticky-add-to-cart .price',
          ].join(', ')
        )
        .forEach((el) => {
          if (el.childElementCount > 2) return;
          el.textContent = priceText;
        });

      const detail = {
        id: Number(variant.id),
        price: Number(variant.price),
        available: Boolean(variant.available),
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
      };
      document.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { resource: detail, variant: detail },
        })
      );
    }

    _scenesForSelection() {
      const mode = this.config.craftMode || 'magnetic';
      const typeId = this.state.typeId;
      // "Semboller" reuses last letter size so scene photos stay put.
      const sizeId = this._activeCharsetSizeId();
      const applies = this._allowedApplies();

      if (mode === 'rgb') {
        const rgbOnly = this.scenes.filter((s) => !s.types?.length || s.types.includes('rgb'));
        let list = rgbOnly.length ? rgbOnly : this.scenes.slice();
        const bySize = list.filter((s) => !s.sizes?.length || s.sizes.includes(sizeId));
        return bySize.length ? bySize : list;
      }

      // Filter by Ölçü (+ Uygulama when shown) for magnetic / sliding / floor / all.
      // Sliding: Alt Ölçü:H150 → only on 15 cm; Ölçü:H200 → only on 20 cm.
      if (mode !== 'all' && mode !== 'magnetic' && mode !== 'sliding' && mode !== 'floor') {
        return this.scenes.slice();
      }

      let list = this.scenes.filter((s) => {
        const typeOk = mode !== 'all' || !s.types?.length || s.types.includes(typeId);
        const sizeOk = !s.sizes?.length || s.sizes.includes(sizeId);
        return typeOk && sizeOk;
      });

      if (applies.length && this.state.applyId) {
        const withApply = list.filter((s) => s.apply === this.state.applyId);
        if (withApply.length) list = withApply;
      }

      // 10 cm has no own photos — reuse the 15 cm rail scenes (letters scale by 10/15).
      if (!list.length && sizeId === 'h100' && (mode === 'sliding' || typeId === 'sliding')) {
        list = this.scenes.filter((s) => {
          const typeOk = mode !== 'all' || !s.types?.length || s.types.includes(typeId);
          return typeOk && (s.sizes || []).includes('h150');
        });
      }

      // Never fall back to the full mixed pool when a type is selected (avoids
      // showing magnetic scenes on RGB after a size mismatch emptied the list).
      if (!list.length && mode === 'all' && typeId) {
        list = this.scenes.filter((s) => !s.types?.length || s.types.includes(typeId));
      }
      // Sliding/floor: if no Ölçü match, show only untagged scenes — never mix H150↔H200.
      if (!list.length && (mode === 'sliding' || mode === 'floor')) {
        list = this.scenes.filter((s) => !s.sizes?.length);
        return list;
      }
      if (!list.length) return this.scenes.slice();
      return list;
    }

    _renderScenes(resetSelection) {
      const list = this._scenesForSelection();
      this.thumbs.innerHTML = '';

      if (!list.length) {
        this.bg.removeAttribute('src');
        this.bg.alt = '';
        return;
      }

      if (resetSelection || !list.some((s) => s.id === this.state.sceneId)) {
        this.state.sceneId = list[0].id;
      }

      list.forEach((scene) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'letter-craft__thumb';
        if (scene.id === this.state.sceneId) btn.classList.add('is-active');
        const img = document.createElement('img');
        img.src = scene.thumb;
        img.alt = scene.alt || '';
        img.loading = 'lazy';
        btn.appendChild(img);
        btn.addEventListener('click', () => {
          this.state.sceneId = scene.id;
          this.thumbs.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
          btn.classList.add('is-active');
          this._applyScene();
        });
        this.thumbs.appendChild(btn);
      });

      this._applyScene();
    }

    _currentScene() {
      const list = this._scenesForSelection();
      return list.find((s) => s.id === this.state.sceneId) || list[0] || null;
    }

    _applyScene() {
      const scene = this._currentScene();
      if (!scene) return;
      this.bg.src = scene.src;
      this.bg.alt = scene.alt || '';
      // Thumb/photo change must re-apply this scene's Alt Boyut scale + wrap fit.
      // Position-only update left stale scale from the previous photo until retype.
      this._render();
      this._applyScenePosition();
      const refit = () => {
        this._fitPreview();
        this._applyScenePosition();
      };
      if (this.bg && this.bg.complete && this.bg.naturalWidth) {
        requestAnimationFrame(refit);
      } else if (this.bg) {
        this.bg.addEventListener('load', () => requestAnimationFrame(refit), { once: true });
      }
    }

    /**
     * RGB + magnetic: taller specials/hearts/logos on a flex-end row would push
     * letters down. Lift the card so the letter rail stays on Üst and tall
     * glyphs grow upward from that baseline.
     */
    _letterRailHeightPx() {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const cqi = (this.preview?.clientWidth || 0) / 100;
      return Math.min(6.25 * rem, Math.max(2.4 * rem, 26 * cqi));
    }

    _baselineRailLiftPx() {
      if ((!this._isRgbMode() && !this._isMagneticMode() && !this._isSlidingMode()) || !this.preview) return 0;
      const nodes = [
        ...this.preview.querySelectorAll(
          '[data-lc-glyph], [data-lc-space], [data-lc-controller], [data-lc-dimmer]'
        ),
      ].filter((el) => (el.offsetHeight || 0) > 0);
      if (!nodes.length) return 0;
      // flex-end row: shorter letters sit lower, so the shared edge is the bottom.
      // Grouping by offsetTop kept only the tall 17cm symbol and skipped the lift.
      const bottomOf = (el) => el.offsetTop + el.offsetHeight;
      const firstBottom = Math.min(...nodes.map(bottomOf));
      const firstLine = nodes.filter((el) => Math.abs(bottomOf(el) - firstBottom) <= 3);
      const isTallSpecial = (el) =>
        el.classList.contains('letter-craft__glyph--heart') ||
        el.classList.contains('letter-craft__glyph--logo') ||
        el.classList.contains('letter-craft__glyph--arrow') ||
        el.classList.contains('letter-craft__glyph--special');
      let maxH = 0;
      let letterH = 0;
      let hasLetterOnLine = false;
      firstLine.forEach((el) => {
        const h = el.offsetHeight || 0;
        if (h > maxH) maxH = h;
        if (isTallSpecial(el)) return;
        hasLetterOnLine = true;
        if (this._isRgbMode()) {
          const sid = el.dataset.lcSize || '';
          if (sid === 'h120' || (!sid && el.classList.contains('letter-craft__glyph'))) {
            letterH = Math.max(letterH, h);
          }
          if (el.classList.contains('letter-craft__controller')) {
            letterH = Math.max(letterH, h);
          }
        } else {
          letterH = Math.max(letterH, h);
        }
      });
      // RGB specials-only: keep Üst on the special top. Magnetic still uses the letter rail.
      if (!hasLetterOnLine && !this._isMagneticMode() && !this._isSlidingMode()) return 0;
      if (!letterH && (this._isMagneticMode() || this._isSlidingMode())) letterH = this._letterRailHeightPx();
      if (!letterH && this._isRgbMode()) {
        const raw = getComputedStyle(this).getPropertyValue('--lc-rgb-h120').trim();
        const rem = raw.endsWith('rem')
          ? parseFloat(raw) * parseFloat(getComputedStyle(document.documentElement).fontSize)
          : parseFloat(raw);
        if (Number.isFinite(rem) && rem > 0) letterH = rem;
      }
      if (!letterH) return 0;
      const liftLayout = Math.max(0, maxH - letterH);
      if (liftLayout < 0.5) return 0;
      const tr = this.preview.style.transform || '';
      const m = tr.match(/scale\(\s*([0-9.]+)\s*\)/);
      const scale = m ? Math.max(parseFloat(m[1]) || 1, 0.01) : 1;
      return liftLayout * scale;
    }

    /** @deprecated use _baselineRailLiftPx */
    _rgbRailLiftPx() {
      return this._baselineRailLiftPx();
    }

    _applyScenePosition() {
      const scene = this._currentScene();
      if (!scene || !this.card) return;
      const stage = this.stageEl || this.querySelector('[data-lc-stage]');
      const raw = this.isMobile ? scene.mbTop : scene.pcTop;
      const top = resolveTopPercent(raw, stage, this.isMobile);
      const leftRaw = this.isMobile ? scene.mbLeft || scene.pcLeft : scene.pcLeft;
      const startFromLeft = this._isSlidingMode() && leftRaw;
      this.card.style.left = startFromLeft
        ? resolveLeftPercent(leftRaw, stage) || '0%'
        : resolveTopPercent(leftRaw || '50', stage, this.isMobile);
      if (this._isMagneticMode() || this._isRgbMode() || this._isSlidingMode() || this._isFloorMode()) {
        // Pin card TOP to scene Üst%. Lift when taller specials would drop letters.
        // Üstten askılı Sol / Soldan is the first letter's left edge, not the card center.
        this.card.style.top = top;
        const lift = this._baselineRailLiftPx();
        const x = startFromLeft ? '0' : '-50%';
        this.card.style.transform = lift > 0 ? `translate(${x}, -${lift}px)` : `translate(${x}, 0)`;
      } else {
        this.card.style.top = top;
        this.card.style.transform = 'translate(-50%, -50%)';
      }
    }

    _render() {
      this._syncInputMirror();
      // Always re-resolve variant/price for current size before painting
      this._syncVariant();

      const text = normalizeDisplayText(
        this.input.value,
        this.specialByChar,
        this.charset,
        this.state.sizeId
      );
      const plan = this._addonPlan();
      const glyphs = plan.analysis.glyphs;
      const spaces = plan.analysis.spaces;
      const count = this._mainLineQty(plan);
      let unit = Number(this.unitPrice) || 0;
      let total = count * unit;
      const size = this._sizeConfig();

      if (plan.rgb) {
        const sizedForPrice = clampRgbGlyphs(
          this.input.value,
          Number(this._typeConfig()?.maxGlyphs) || 0
        );
        const buckets =
          plan.rgbBuckets ||
          rgbGlyphSizeBuckets(sizedForPrice, this.charset, this.specialByChar);
        const kalpVar = findKalpVariant(this.config.variants || [], 'rgb');
        const kalpUnit = Number(kalpVar?.price || 0) || unit;
        const priceForSize = (sizeId) => {
          if (sizeId === String(this.state.sizeId)) return unit;
          const v = this._rgbVariantForSizeId(sizeId);
          return Number(v?.price || 0) || unit;
        };
        // Price each glyph by its natural size class (digits@20, specials@17, letters@12).
        total =
          buckets.h200.glyphs * priceForSize('h200') +
          buckets.h170.glyphs * priceForSize('h170') +
          buckets.h120.letters * priceForSize('h120') +
          buckets.h120.hearts * (kalpVar ? kalpUnit : priceForSize('h120'));
        total += spaces * rgbSpacerPriceCents(this.config.addons, this.state.sizeId);
        if (plan.includeController) total += rgbControllerPriceCents(this.config.addons);
      } else if (!this._isRailMode()) {
        if (this._isMagneticMode() && magneticPriceTables(this.config)) {
          const br = magneticTextPriceCents(
            this.input.value,
            this.config,
            this.state.colorId,
            this._activeCharsetSizeId(),
            this.specialByChar,
            this.charset
          );
          total = br.total;
          if (br.letterUsd != null) unit = usdToTryCents(br.letterUsd, this.config);
        } else {
          total = (glyphs + spaces) * unit;
        }
      }
      if (plan.floor && this.config.floorPrices) {
        const letters = this.config.floorPrices.letters || {};
        const whiteUsd = Number(letters.white);
        const rgbUsd = Number(letters.rgb);
        const activeUsd = plan.floorColor === 'rgb' ? rgbUsd : whiteUsd;
        if (Number.isFinite(activeUsd)) unit = usdToTryCents(activeUsd, this.config);
        total = 0;
        if (Number.isFinite(whiteUsd)) total += (plan.floorWhiteQty || 0) * usdToTryCents(whiteUsd, this.config);
        if (Number.isFinite(rgbUsd)) total += (plan.floorRgbQty || 0) * usdToTryCents(rgbUsd, this.config);
        if (plan.includeAdapter) {
          (plan.floorSupplies || []).forEach((key) => {
            const psuUsd = Number(this.config.floorPrices.psu?.[key]);
            if (Number.isFinite(psuUsd)) total += usdToTryCents(psuUsd, this.config);
          });
        }
        if (spaces > 0 && this.config.floorPrices.spacer != null) {
          total += spaces * usdToTryCents(this.config.floorPrices.spacer, this.config);
        }
      }
      let slidingRod = null;
      if (this._isSlidingMode() && this.config.railPrices) {
        const prices = this.config.railPrices;
        const letterUsd = Number(prices.letters?.[this.state.sizeId]);
        if (Number.isFinite(letterUsd)) unit = usdToTryCents(letterUsd, this.config);
        total = 0;
        for (const ch of Array.from(this.input?.value || '')) {
          if (ch === ' ') continue;
          if (!railMetricsForChar(this.charset, this.state.sizeId, ch, this.specialByChar)) continue;
          total += usdToTryCents(slidingGlyphUsd(ch, this.state.sizeId, prices, this.specialByChar), this.config);
        }
        if (plan.analysis?.hasLetters && plan.includeRod) {
          const stock = railStockMm(plan.railMeasure?.widthMm || 0);
          const cm = stock ? stock / 10 : 0;
          const rodUsd = Number(prices.rods?.[String(cm)]);
          slidingRod = {
            cm,
            cents: Number.isFinite(rodUsd) ? usdToTryCents(rodUsd, this.config) : 0,
          };
          total += slidingRod.cents;
        }
      }
      if (plan.includeConnector) {
        const connUsd = this._isMagneticMode()
          ? magneticAddonUsd(this.config, 'connector')
          : null;
        total +=
          connUsd != null
            ? usdToTryCents(connUsd, this.config)
            : Number(this.config.addons?.connectorPrice || 0) || 0;
      }
      if (plan.includeAdapter && plan.adapterKey && !plan.floor) {
        const adUsd = this._isMagneticMode()
          ? magneticAddonUsd(this.config, 'adapter', plan.adapterKey)
          : this._isSlidingMode()
            ? slidingAdapterUsd(plan.adapterKey, this.config.railPrices)
            : null;
        if (adUsd != null) {
          total += usdToTryCents(adUsd, this.config);
        } else {
          const ap = this.config.addons?.adapterPrices || {};
          total += Number(ap[plan.adapterKey] || 0) || 0;
        }
      }
      if (this._isMagneticMode()) {
        (plan.shelves || []).forEach((row) => {
          const shelfUsd = magneticAddonUsd(this.config, 'shelf', row.id);
          if (shelfUsd != null) total += usdToTryCents(shelfUsd, this.config);
        });
      }

      if (this.railRodRow) {
        const showRod = Boolean(slidingRod && slidingRod.cm);
        this.railRodRow.hidden = !showRod;
        if (showRod && this.railRodLabel) {
          this.railRodLabel.textContent = `Tavan asma çubuğu (${slidingRod.cm} cm)`;
        }
        if (showRod && this.railRodPrice) {
          this.railRodPrice.textContent = formatMoney(slidingRod.cents, this.moneyFormat);
        }
      }

      this.countEl.textContent = String(count);
      this.unitEl.textContent = formatMoney(unit, this.moneyFormat);
      this._syncUnitLabel();
      this.totalEl.textContent = formatMoney(total, this.moneyFormat);
      this.noteEl.textContent =
        this.notes[this._sizeConfig()?.noteKey] || this.notes[this.state.sizeId] || '';

      const railMode = this._isRailMode();
      const floorMode = this._isFloorMode();
      const rgbMode = Boolean(plan.rgb) || this._isRgbMode();
      const magneticMode = this._isMagneticMode();
      const showLength = railMode || rgbMode || magneticMode;
      if (this.railLengthRow) this.railLengthRow.hidden = !showLength;
      // RGB: never show Güç wattage (Adaptör already off). Rail/floor only.
      if (this.railPowerRow) this.railPowerRow.hidden = !railMode || rgbMode;
      if (rgbMode) {
        const rm =
          plan.rgbMeasure ||
          plan.railMeasure ||
          measureRgbText(this.input.value, this.charset, this.state.sizeId, this.specialByChar, {
            includeController: Boolean(plan.includeController),
          });
        if (this.railLengthEl) {
          this.railLengthEl.textContent = formatLengthCm(rm.widthMm || 0);
        }
        const maxEl = this.querySelector('[data-lc-rail-max]');
        if (maxEl) maxEl.hidden = true;
      } else if (railMode) {
        const rm =
          plan.railMeasure ||
          measureRailText(this.input.value, this.charset, this.state.sizeId, this.specialByChar);
        if (this.railLengthEl) {
          this._railLengthIsLetters = false;
          if (floorMode) {
            this.railLengthEl.textContent = formatLengthCm(floorDisplayWidthMm(rm, plan));
          } else if (this._isSlidingMode() && !plan.includeRod && rm.widthMm > 0) {
            this._railLengthIsLetters = true;
            this.railLengthEl.textContent = formatLengthCm(rm.widthMm);
          } else {
            const stock = railStockMm(rm.widthMm);
            this.railLengthEl.textContent = stock ? `${stock / 10} cm` : '0 cm';
          }
        }
        const maxEl = this.querySelector('[data-lc-rail-max]');
        if (maxEl) maxEl.hidden = true;
        if (this.railPowerEl) {
          if (floorMode) {
            const watts = plan.includeAdapter
              ? (plan.floorSupplies || []).map((key) => floorSupplyWatts(key)).filter(Boolean).join(' + ')
              : '0';
            this.railPowerEl.textContent = watts === '0' || watts === '' ? '0 W' : `${watts} W`;
          } else {
            this.railPowerEl.textContent = `${Number(rm.powerW || 0)
              .toFixed(2)
              .replace(/\.00$/, '')} W`;
          }
        }
        if (!floorMode && rm.widthMm >= this._railMaxWidth() - 0.05) {
          this.warningEl.hidden = false;
          this.warningEl.textContent = t(
            this.i18n,
            'warningRailFull',
            'Ray kapasitesi doldu (max 200 cm).'
          );
        }
      } else {
        // Magnetic (non-rail): letters + optional connector footprint
        const rm =
          plan.railMeasure ||
          measureMagneticText(
            this.input.value,
            this.charset,
            this._activeCharsetSizeId(),
            this.specialByChar,
            { includeConnector: Boolean(plan.includeConnector) }
          );
        if (this.railLengthEl) {
          this.railLengthEl.textContent = formatLengthCm(rm.widthMm || 0);
        }
        const maxEl = this.querySelector('[data-lc-rail-max]');
        if (maxEl) maxEl.hidden = true;
        if (this.railPowerEl) {
          this.railPowerEl.textContent = `${Number(rm.powerW || 0)
            .toFixed(2)
            .replace(/\.00$/, '')} W`;
        }
      }

      // Boşluk ücreti manyetik, RGB ve P3 içindir. Üstten askılıda boşluk yalnızca genişlik katar.
      if (this.spaceRow) {
        const showSpaces = spaces > 0 && !this._isSlidingMode();
        this.spaceRow.hidden = !showSpaces;
        if (showSpaces) {
          if (this.spaceCountEl) this.spaceCountEl.textContent = String(spaces);
          if (this.spacePriceEl) {
            const spaceTotal = plan.rgb
              ? spaces * rgbSpacerPriceCents(this.config.addons, this.state.sizeId)
              : plan.floor && this.config.floorPrices?.spacer != null
                ? spaces * usdToTryCents(this.config.floorPrices.spacer, this.config)
                : this._isMagneticMode() && magneticAddonUsd(this.config, 'spacer') != null
                  ? spaces * usdToTryCents(magneticAddonUsd(this.config, 'spacer'), this.config)
                  : spaces * unit;
            this.spacePriceEl.textContent = formatMoney(spaceTotal, this.moneyFormat);
          }
        }
      }

      if (this.connectorOptional) {
        // RGB: reuse Konnektör button UI for L25 controller (no magnetic connector product)
        const showToggle = plan.rgb ? Boolean(plan.optionalController) : plan.optionalConnector;
        this.connectorOptional.hidden = !showToggle;
        if (!showToggle && this.connectorCheck && !plan.magnetic && !plan.rgb) {
          this.connectorCheck.checked = false;
        }
        const on = plan.rgb ? Boolean(plan.includeController) : Boolean(plan.includeConnector);
        this.connectorOptional.classList.toggle('is-active', on);
        this.connectorOptional.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (this.dimmerBtnLabel) {
          if (plan.rgb || plan.magnetic) {
            this.dimmerBtnLabel.textContent = on
              ? t(this.i18n, 'addonDimmerOn', 'Konnektör eklendi — kaldırmak için tıklayın')
              : t(this.i18n, 'addonDimmerOffAuto', 'Konnektör ekle (otomatik önerilir)');
          } else {
            this.dimmerBtnLabel.textContent = on
              ? t(this.i18n, 'addonDimmerOn', 'Konnektör eklendi — kaldırmak için tıklayın')
              : t(this.i18n, 'addonDimmerOff', 'Konnektör ekle (isteğe bağlı)');
          }
        }
      }
      this._syncShelfButtons();
      if (this.adapterOptional) {
        // RGB: no power/Adaptör toggle (Konnektör/L25 only)
        const showAdapter = Boolean(plan.optionalAdapter) && !rgbMode;
        this.adapterOptional.hidden = !showAdapter;
        const on = Boolean(plan.includeAdapter);
        this.adapterOptional.classList.toggle('is-active', on);
        this.adapterOptional.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (this.adapterBtnLabel && plan.floor) {
          const watts = (plan.floorSupplies || []).map(floorSupplyLabel).filter(Boolean).join(' + ');
          this.adapterBtnLabel.textContent = on
            ? `Güç kaynağı ${watts} eklendi — kaldırmak için tıklayın`
            : `Güç kaynağı ekle (${watts || 'otomatik'})`;
        } else if (this.adapterBtnLabel && this._isSlidingMode()) {
          const amp = String(plan.adapterKey || '').toUpperCase();
          this.adapterBtnLabel.textContent = on
            ? `Güç kaynağı ${amp} eklendi — kaldırmak için tıklayın`
            : `Güç kaynağı ekle${amp ? ` (${amp})` : ''}`;
        } else if (this.adapterBtnLabel) {
          const amp = String(plan.adapterKey || '').toUpperCase();
          this.adapterBtnLabel.textContent = on
            ? t(this.i18n, 'addonAdapterOn', 'Adaptör {{adapter}} eklendi — kaldırmak için tıklayın').replace(
                '{{adapter}}',
                amp || ''
              )
            : t(this.i18n, 'addonAdapterOff', 'Adaptör ekle (otomatik önerilir)').replace(
                '{{adapter}}',
                amp || ''
              );
        }
      }
      if (this.rodOptional) {
        const showRodToggle = Boolean(plan.sliding && plan.optionalRod);
        this.rodOptional.hidden = !showRodToggle;
        const rodOn = Boolean(plan.includeRod);
        this.rodOptional.classList.toggle('is-active', rodOn);
        this.rodOptional.setAttribute('aria-pressed', rodOn ? 'true' : 'false');
        if (this.rodBtnLabel) {
          const cm = plan.rodCm ? `${plan.rodCm} cm` : '';
          this.rodBtnLabel.textContent = rodOn
            ? `Tavan asma çubuğu${cm ? ` (${cm})` : ''} eklendi — kaldırmak için tıklayın`
            : `Tavan asma çubuğu ekle${cm ? ` (${cm})` : ''}`;
        }
      }
      if (this.controllerOptional) {
        // Never show a separate L25 button — RGB uses the Konnektör-labeled toggle above
        this.controllerOptional.hidden = true;
        this.controllerOptional.setAttribute('hidden', '');
        this.controllerOptional.style.display = 'none';
      }
      if (this.addonNoteEl) {
        if (plan.rgb && plan.analysis.hasLetters) {
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = t(
            this.i18n,
            'addonNoteRgb',
            'Konnektör otomatik eklenir (uyarı ile kaldırılabilir). Boşluklar Ara Boşluk ile ücretlendirilir.'
          );
        } else if (plan.floor && plan.analysis.glyphs > 0) {
          const watts = (plan.floorSupplies || []).map(floorSupplyLabel).filter(Boolean).join(' ve ');
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = `Beyaz ve Renkli aynı yazıda kullanılabilir; seçili renk yeni harflere uygulanır. Güç kaynağı ${watts} otomatik eklenir; istemezseniz kaldırabilirsiniz.`;
        } else if (this._isSlidingMode() && plan.analysis.hasLetters) {
          const amp = String(plan.adapterKey || '').toUpperCase();
          const power = amp ? `Güç kaynağı ${amp}` : 'Güç kaynağı';
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = `${power} ve tavan asma çubuğu otomatik eklenir. İstemezseniz kaldırabilirsiniz.`;
        } else if (plan.magnetic && plan.analysis.hasLetters) {
          const amp = String(plan.adapterKey || '').toUpperCase();
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = t(
            this.i18n,
            'addonNoteLettersMagnetic',
            'Harf eklenince adaptör{{adapterPart}} ve konnektör otomatik eklenir; istemezseniz kaldırabilirsiniz.'
          ).replace('{{adapterPart}}', amp ? ` ${amp}` : '');
        } else if (plan.analysis.hasLetters) {
          const amp = String(plan.adapterKey || '').toUpperCase();
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = amp
            ? t(
                this.i18n,
                'addonNoteLettersWithAdapter',
                'Adaptör {{adapter}} otomatik eklenir. Konnektör isteğe bağlıdır.'
              ).replace('{{adapter}}', amp)
            : t(
                this.i18n,
                'addonNoteLetters',
                'Harf siparişlerinde adaptör otomatik eklenir. Konnektör isteğe bağlıdır.'
              );
        } else if (plan.optionalConnector && !plan.magnetic) {
          this.addonNoteEl.hidden = false;
          this.addonNoteEl.textContent = t(
            this.i18n,
            'addonNoteSymbols',
            'Konnektör isteğe bağlıdır; eklemek için butona tıklayın.'
          );
        } else {
          this.addonNoteEl.hidden = true;
          this.addonNoteEl.textContent = '';
        }
      }

      const magnetic = this._isMagneticMode();
      const sliding = this._isSlidingMode();
      // rgbMode already declared above in this method — do not redeclare (SyntaxError).
      const floorModeAnchor = this._isFloorMode();
      if (magnetic || rgbMode || sliding || floorModeAnchor) this.dataset.anchor = 'baseline';
      else delete this.dataset.anchor;
      // Magnetic/RGB/sliding: scene Alt Boyut (Masaüstü Boyut / Mobil Boyut / Boyut) → CSS scale.
      const sceneLetterScale = this._sceneLetterScale();
      const effLetter = effectiveSceneLetterScale(sceneLetterScale);
      const floorModeScale = this._isFloorMode();
      const baseScale = magnetic
        ? magneticPreviewScale(sceneLetterScale)
        : sliding
          ? slidingPreviewScale(sceneLetterScale, size)
          : rgbMode
          ? rgbPreviewScale(sceneLetterScale, this.config)
          : floorModeScale
            ? floorPreviewScale(sceneLetterScale, size)
            : (size?.scale || 1) * (effLetter != null ? effLetter : 1);
      this._renderPreview(this.input.value, baseScale, plan);
      // Re-pin after glyphs/scale so size/apply switches use the new scene Üst.
      requestAnimationFrame(() => this._applyScenePosition());

      const rawGlyphs = analyzeText(this.input.value, this.specialByChar).glyphs;
      const maxGlyphs = Number(this._typeConfig()?.maxGlyphs) || 0;
      if (plan.rgb && maxGlyphs > 0 && rawGlyphs > maxGlyphs) {
        this.warningEl.hidden = false;
        this.warningEl.textContent = t(
          this.i18n,
          'warningMaxLetters',
          'En fazla {{n}} harf ekleyebilirsiniz.'
        ).replace('{{n}}', String(maxGlyphs));
      } else if (this._floorLimitNote) {
        this.warningEl.hidden = false;
        this.warningEl.textContent = this._floorLimitNote;
      } else if (count > 0) {
        this.warningEl.hidden = true;
      }
      this._syncNativeForm();
    }

    _ensureRailChrome() {
      if (!this.preview || !this._isSlidingMode()) return null;
      let track = this.preview.querySelector('[data-lc-rail-track]');
      if (!track) {
        track = document.createElement('div');
        track.className = 'letter-craft__rail-track';
        track.dataset.lcRailTrack = '';
        track.hidden = true;
        const bar = document.createElement('div');
        bar.className = 'letter-craft__rail-track-bar';
        const label = document.createElement('span');
        label.className = 'letter-craft__rail-track-label';
        label.dataset.lcRailTrackLabel = '';
        bar.appendChild(label);
        track.appendChild(bar);
        this.preview.prepend(track);
      }
      let line = this.preview.querySelector('[data-lc-rail-line]');
      if (!line) {
        line = document.createElement('div');
        line.className = 'letter-craft__rail-line';
        line.dataset.lcRailLine = '';
        this.preview.appendChild(line);
      }
      return line;
    }

    _syncRailTrack(text) {
      const track = this.preview?.querySelector('[data-lc-rail-track]');
      if (!track) return;
      const bar = track.querySelector('.letter-craft__rail-track-bar');
      const label = track.querySelector('[data-lc-rail-track-label]');
      if (!this._isSlidingMode() || !text) {
        track.hidden = true;
        return;
      }
      const rm = measureRailText(text, this.charset, this.state.sizeId, this.specialByChar);
      const stock = railStockMm(rm.widthMm);
      if (!stock) {
        track.hidden = true;
        return;
      }
      track.hidden = false;
      const tr = this.preview.style.transform || '';
      const m = tr.match(/scale\(\s*([0-9.]+)\s*\)/);
      const scale = Math.max(m ? parseFloat(m[1]) || 1 : 1, 0.05);
      track.style.transform = `translateY(${-22 / scale}px)`;
      const line = this.preview.querySelector('[data-lc-rail-line]');
      const glyphs = line
        ? [...line.querySelectorAll(':scope > [data-lc-glyph], :scope > [data-lc-space]')]
        : [];
      let lettersW = 0;
      if (glyphs.length) {
        const first = glyphs[0];
        const last = glyphs[glyphs.length - 1];
        lettersW = Math.max(0, last.offsetLeft + last.offsetWidth - first.offsetLeft);
      }
      line?.querySelectorAll('img').forEach((img) => {
        if (img.dataset.lcRailSized) return;
        img.dataset.lcRailSized = '1';
        img.addEventListener('load', () => this._syncRailTrack(this.input?.value || ''), { once: true });
      });
      if (bar) {
        bar.style.width = lettersW > 0 ? `${lettersW}px` : `${Math.min(100, (rm.widthMm / 2000) * 100)}%`;
        bar.style.height = `${0.32 / scale}rem`;
        bar.style.minWidth = '0';
      }
      if (label) {
        const letterLength = Boolean(this._railLengthIsLetters) && rm.widthMm > 0;
        label.textContent = letterLength ? formatLengthCm(rm.widthMm) : `${stock / 10} cm`;
        label.style.fontSize = `${0.8 / scale}rem`;
      }
    }

    _renderPreview(rawText, scale, plan) {
      this.preview.querySelectorAll('[data-lc-glyph], [data-lc-space], [data-lc-dimmer], [data-lc-controller]').forEach((n) => n.remove());
      // Floor stays on the white size rules so Beyaz and RGB glyphs share one height.
      this.dataset.color = this._isRgbMode() ? 'rgb' : this._isFloorMode() ? 'white' : (this.state.colorId || 'white');
      // Re-assert live attrs before rebuilding glyphs (typing must not drop live)
      this._syncRgbLiveToggle();

      let text = this.input?.value || '';
      if (plan?.rgb || this._isRgbMode()) {
        const rawMax = Number(this._typeConfig()?.maxGlyphs);
        const maxGlyphs = Number.isFinite(rawMax) ? rawMax : 0;
        // Full mixed string — never hide glyphs that belong to another size class.
        text = clampRgbGlyphs(text, maxGlyphs);
      }
      if (!text.length) {
        if (this.placeholder) this.placeholder.hidden = true;
        this.preview.style.transform = '';
        this._syncRailTrack('');
        this._applyRgbLiveBand();
        return;
      }
      if (this.placeholder) this.placeholder.hidden = true;

      const color = this._colorConfig();
      // Magnetic "Semboller": keep letter assets at last letter size while specials stay size-locked.
      const sizeId = this._activeCharsetSizeId();
      const frag = document.createDocumentFragment();
      let letterIndex = 0;
      const rgbMixed = Boolean(plan?.rgb || this._isRgbMode());
      // Üstten askılı: typed letters are Arial Bold text — never fetch letter PNGs.
      const slidingText = this._isSlidingMode();
      if (slidingText) this.style.setProperty('--lc-sym', '1');
      const railLine = slidingText ? this._ensureRailChrome() : null;

      const showDimmer = plan ? plan.showDimmerPreview : false;
      const showController = plan ? plan.showControllerPreview : false;
      if (showController) {
        const wrap = document.createElement('span');
        wrap.className = 'letter-craft__dimmer letter-craft__controller';
        wrap.dataset.lcController = '';
        wrap.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        // Merchant letter-rgb-L25.png first; size-specific L25 only as load fallback
        const ctrlPrimary =
          this.config.controllerAsset ||
          assetUrlFor(this.assetSample, 'letter-rgb-L25.png');
        const ctrlFallback = assetUrlFor(
          this.assetSample,
          `letter-rgb-${mmForSize(this.config, sizeId)}-L25.png`
        );
        if (ctrlPrimary || ctrlFallback) {
          let attempt = 0;
          const sources = [ctrlPrimary, ctrlFallback].filter(Boolean);
          const loadNext = () => {
            if (attempt >= sources.length) return false;
            const src = sources[attempt++];
            // Cache-bust once per source so stale CDN stubs don't stick per-browser
            const bust = src.includes('?') ? `${src}&_lc=${Date.now()}` : `${src}?_lc=${Date.now()}`;
            img.src = attempt === 1 ? src : bust;
            return true;
          };
          img.addEventListener('error', () => {
            if (!loadNext()) {
              // RGB: never show magnetic dimmer cable fallback — hide broken controller
              wrap.remove();
            }
          });
          loadNext();
        } else {
          wrap.remove();
        }
        wrap.appendChild(img);
        frag.appendChild(wrap);
      }
      if (showDimmer) {
        const wrap = document.createElement('span');
        // Magnetic: also use controller class so adjacency/join CSS matches RGB connector
        wrap.className = 'letter-craft__dimmer letter-craft__controller';
        wrap.dataset.lcDimmer = '';
        wrap.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        // Magnetic + RGB: connector preview uses letter-rgb-L25.png
        const dimmerUrl = (this._isFloorMode() && this.config.floorConnectorAsset)
          ? this.config.floorConnectorAsset
          : (
              this.config.controllerAsset ||
              this.dimmerAsset ||
              assetUrlFor(this.assetSample, 'letter-rgb-L25.png')
            );
        if (dimmerUrl) {
          img.src = dimmerUrl;
          img.addEventListener('error', () => wrap.classList.add('letter-craft__dimmer--fallback'));
        } else {
          wrap.classList.add('letter-craft__dimmer--fallback');
        }
        wrap.appendChild(img);
        frag.appendChild(wrap);
      }

      const textChars = Array.from(text || '');
      for (let ti = 0; ti < textChars.length; ti += 1) {
        const ch = textChars[ti];
        if (ch === ' ') {
          const space = document.createElement('span');
          space.className = 'letter-craft__space';
          space.dataset.lcSpace = '';
          if (plan?.showSpacerPreview && !slidingText) {
            const img = document.createElement('img');
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            const spacerUrl = this._isFloorMode()
              ? assetUrlFor(this.assetSample, 'letter-floor-680-SPACE.png', this.charset?.version)
              : this.config.spacerAsset ||
                assetUrlFor(this.assetSample, 'letter-rgb-SPACE.png') ||
                assetUrlFor(this.assetSample, `letter-rgb-${mmForSize(this.config, sizeId)}-SPACE.png`);
            if (spacerUrl) img.src = spacerUrl;
            space.classList.add('letter-craft__space--glyph');
            // Pin spacer to same size tier as letters (shared bottom rail)
            if (rgbMixed) space.dataset.lcSize = 'h120';
            else if (sizeId) space.dataset.lcSize = sizeId;
            space.appendChild(img);
          }
          frag.appendChild(space);
          continue;
        }

        const isSpecial = isMappedSpecial(ch, this.specialByChar);
        // Prefer fetched RGB charset over inline magnetic charset for PNG paths
        const charsetForGlyph =
          (this._isRgbMode() && this.rgbCharset) ? this.rgbCharset : this.charset;
        // Mixed RGB: each glyph uses its natural size asset (digit→200, couple→170, letter→120)
        const glyphSizeId = rgbMixed
          ? rgbNaturalSizeIdForChar(ch, charsetForGlyph, this.specialByChar) || sizeId
          : sizeId;
        const label = charDisplayGlyph(ch, this.specialByChar, this.charset, glyphSizeId);

        // Üstten askılı: Arial Bold letters; arrows/logos keep PNG so emoji ⬅️➡️
        // do not render at full cap-height (those two sit much larger than ↑↓).
        if (slidingText) {
          const glyph = document.createElement('span');
          glyph.className = 'letter-craft__glyph letter-craft__glyph--rail-text';
          glyph.dataset.lcGlyph = '';
          const sp = isSpecial ? this.specialByChar.get(ch) : null;
          const stem = String(
            sp?.stem || stemForChar(charsetForGlyph, glyphSizeId, ch) || ''
          ).toUpperCase();
          const isRailArrow = /^(SOL_OK|SAG_OK|YUKARI_OK|ASAGI_OK)$/.test(stem);
          const symbolFile =
            sp?.file ||
            sp?.buttonFile ||
            (isRailArrow ? assetFileFor(charsetForGlyph, glyphSizeId, stem) : null);
          if (symbolFile) {
            glyph.classList.add('letter-craft__glyph--special', 'letter-craft__glyph--rail-symbol');
            if (/^(SOL_OK|SAG_OK|YUKARI_OK|ASAGI_OK)$/.test(stem)) {
              glyph.classList.add('letter-craft__glyph--arrow');
            }
            if (/^(SOL_OK|SAG_OK)$/.test(stem)) {
              glyph.classList.add('letter-craft__glyph--arrow-lr');
              glyph.style.setProperty('--lc-sym', '0.62');
            }
            const img = document.createElement('img');
            img.alt = '';
            img.decoding = 'async';
            img.src = assetUrlFor(this.assetSample, symbolFile, this.charset?.version);
            glyph.appendChild(img);
          } else {
            const isLetter = /[A-Za-zÇçĞğİıÖöŞşÜüÑñ0-9]/.test(label);
            glyph.textContent = isLetter ? String(label).toLocaleUpperCase('tr-TR') : label;
          }
          frag.appendChild(glyph);
          letterIndex += 1;
          continue;
        }

        // RGB: letter PNG photos with always-on live RGB face overlay (alpha mask).
        // Coded text only as last-resort fallback when a PNG fails to load.
        const glyphColorId = this._isRgbMode()
          ? null
          : this._isFloorMode()
            ? ((this.state.charColors && this.state.charColors[ti]) === 'rgb' ? 'rgb' : 'white')
            : (this.state.charColors && this.state.charColors[ti]) || this.state.colorId || 'white';
        const glyphColor = this._isRgbMode()
          ? null
          : (this.config.colors || []).find((c) => c.id === glyphColorId) || this._colorConfig();
        const url = glyphAssetUrl(
          this.assetSample,
          charsetForGlyph,
          glyphSizeId,
          ch,
          this.specialByChar,
          this._glyphAssetConfig(),
          glyphColor
        );
        const hasStem =
          Boolean(stemForChar(this.charset, glyphSizeId, ch)) ||
          Boolean(this.charset?.symbolStemByChar?.[ch]) ||
          Boolean(this.charset?.symbolStemByChar?.[toTurkishUpper(ch)]);
        if (!url && !isSpecial && !hasStem && !plan?.rgb) continue;

        const glyph = document.createElement('span');
        glyph.className = 'letter-craft__glyph';
        if (isSpecial) glyph.classList.add('letter-craft__glyph--special');
        if (isHeartSpecialChar(ch, this.specialByChar)) {
          glyph.classList.add('letter-craft__glyph--heart');
        }
        if (isSpecial) {
          const spMeta = this.specialByChar?.get(ch);
          const stem = String(spMeta?.stem || '').toUpperCase();
          if (stem.includes('LOGO')) {
            glyph.classList.add('letter-craft__glyph--logo');
            const mm = String(spMeta?.mm || '');
            if (mm === '220') glyph.classList.add('letter-craft__glyph--logo-220');
            else if (mm === '135') glyph.classList.add('letter-craft__glyph--logo-135');
          }
          if (/^(SOL_OK|SAG_OK|YUKARI_OK|ASAGI_OK)$/.test(stem)) {
            glyph.classList.add('letter-craft__glyph--arrow');
          }
        }
        if (this._isMagneticMode()) {
          const scale = isSpecial
            ? magneticGlyphScale(this.specialByChar?.get(ch))
            : 1;
          glyph.style.setProperty('--lc-h', String(scale));
        }
        glyph.dataset.lcGlyph = '';
        if (glyphColorId) glyph.dataset.lcColor = glyphColorId;
        if (rgbMixed && glyphSizeId) glyph.dataset.lcSize = glyphSizeId;

        const applyRgbCodedLetter = () => {
          glyph.classList.add('letter-craft__glyph--rgb-text');
          glyph.classList.remove('letter-craft__glyph--fallback');
          // Don't uppercase emoji / symbols into ugly codes like "KALP"
          const isLetter = /[A-Za-zÇçĞğİıÖöŞşÜüÑñ]/.test(label);
          glyph.textContent = isLetter ? String(label).toLocaleUpperCase('tr-TR') : label;
        };
        if (url) {
          const img = document.createElement('img');
          img.alt = label;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.src = url;
          img.addEventListener('error', () => {
            // Latest shared pack first (white-BUYUK_*, yellow short); never double-inject mm.
            const file = String(img.currentSrc || img.src || url).split('?')[0].split('/').pop() || '';
            let next = '';
            const prefixMatch = file.match(/^(letter-(?:white-|yellow-|daylight-|red-)?)/i);
            const afterPrefix = prefixMatch ? file.slice(prefixMatch[0].length) : file;
            const alreadyHasMm = /^\d+-/.test(afterPrefix);
            const cur = () => String(img.currentSrc || img.src || '');

            if (
              !img.dataset.lcBuyukStem &&
              /^(letter-(?:white-|yellow-|daylight-|red-)?)(?:\d+-)?(KUCUK_)/i.test(file)
            ) {
              img.dataset.lcBuyukStem = '1';
              const buyukName = file.replace(/KUCUK_/i, 'BUYUK_');
              next = assetUrlFor(this.assetSample, buyukName, this.charset?.version);
              if (next && next !== url && next !== cur()) {
                img.src = next;
                return;
              }
            }
            if (
              !img.dataset.lcShortStem &&
              /^(letter-(?:white-|yellow-|daylight-|red-)?)(?:\d+-)?(BUYUK_|KUCUK_)/i.test(file)
            ) {
              // letter-yellow-BUYUK_A.png → letter-yellow-A.png (daylight short pack)
              img.dataset.lcShortStem = '1';
              const shortName = file.replace(/(BUYUK_|KUCUK_)/i, '');
              next = assetUrlFor(this.assetSample, shortName, this.charset?.version);
              if (next && next !== url && next !== cur()) {
                img.src = next;
                return;
              }
            }
            if (!img.dataset.lcTryMm && prefixMatch && !alreadyHasMm) {
              // Legacy sized color folders (local letter-white-120-*)
              img.dataset.lcTryMm = '1';
              const withMm = `${prefixMatch[1]}120-${afterPrefix}`;
              next = assetUrlFor(this.assetSample, withMm, this.charset?.version);
              if (next && next !== url && next !== cur()) {
                img.src = next;
                return;
              }
            }
            if (
              file.startsWith('letter-white-') ||
              file.startsWith('letter-yellow-') ||
              file.startsWith('letter-daylight-') ||
              file.startsWith('letter-red-')
            ) {
              next = assetUrlFor(
                this.assetSample,
                'letter-' + file.replace(/^letter-(white|yellow|daylight|red)-/, ''),
                this.charset?.version
              );
            }
            if (next && next !== url && next !== cur() && !img.dataset.lcLegacy) {
              img.dataset.lcLegacy = '1';
              img.src = next;
              return;
            }
            // Last resort: older letter-95-* pack
            if (!img.dataset.lcTryH95 && /^letter-/i.test(file) && !/^letter-(?:rgb-|rail-|floor-)/i.test(file)) {
              img.dataset.lcTryH95 = '1';
              const stemPart = file
                .replace(/^letter-(?:white-|yellow-|daylight-|red-)?/i, '')
                .replace(/^(?:\d+-)+/, '')
                .replace(/\.png$/i, '');
              const short = String(stemPart).replace(/^(BUYUK_|KUCUK_)/i, '');
              if (short) {
                next = assetUrlFor(this.assetSample, `letter-95-${short}.png`, this.charset?.version);
                if (next && next !== cur()) {
                  img.src = next;
                  return;
                }
              }
            }
            img.remove();
            if (plan?.rgb) applyRgbCodedLetter();
            else {
              glyph.classList.add('letter-craft__glyph--fallback');
              glyph.textContent = label;
            }
          });
          glyph.appendChild(img);
        } else if (plan?.rgb) {
          applyRgbCodedLetter();
        } else {
          glyph.classList.add('letter-craft__glyph--fallback');
          glyph.textContent = label;
        }

        frag.appendChild(glyph);
        letterIndex += 1;
      }

      (railLine || this.preview).appendChild(frag);
      this._syncRailTrack(slidingText ? text : '');
      if (this._isMagneticMode() || this._isRgbMode() || slidingText || this._isFloorMode()) {
        this.dataset.anchor = 'baseline';
        this.preview.style.transformOrigin = 'center top';
      } else {
        delete this.dataset.anchor;
        this.preview.style.transformOrigin = '';
        this.preview.style.width = '';
        this.preview.style.maxWidth = '';
        this.preview.style.flexShrink = '';
      }
      this.preview.style.transform = `scale(${scale})`;
      this._fitPreview(letterIndex, scale);
      // Live band after layout; rAF catches post-scale offsetLeft
      this._applyRgbLiveBand();
      requestAnimationFrame(() => this._applyRgbLiveBand());
    }

    _fitPreview(letterCount, baseScale) {
      const size = this._sizeConfig();
      const magnetic = this._isMagneticMode();
      const sliding = this._isSlidingMode();
      const rgbMode = this._isRgbMode();
      let scale = baseScale;
      if (scale == null) {
        const sceneLetterScale = this._sceneLetterScale();
        const effLetter = effectiveSceneLetterScale(sceneLetterScale);
        scale = magnetic
          ? magneticPreviewScale(sceneLetterScale)
          : sliding
            ? slidingPreviewScale(sceneLetterScale, size)
            : rgbMode
            ? rgbPreviewScale(sceneLetterScale, this.config)
            : this._isFloorMode()
              ? floorPreviewScale(sceneLetterScale, size)
              : (size?.scale || 1) * (effLetter != null ? effLetter : 1);
      }
      const applyFit = () => {
        if (!this.preview) return;
        const stage = this.stageEl || this.querySelector('[data-lc-stage]');
        if (!stage) {
          this.preview.style.transform = `scale(${scale})`;
          return;
        }
        // RGB + magnetic + sliding: wrap at nearly full photo width; first line top-anchored.
        // Explicit width + flex-shrink:0 so card max-width cannot clamp early (empty sides).
        if (this._isRgbMode() || magnetic || sliding || this._isFloorMode()) {
          this.dataset.anchor = 'baseline';
          this.preview.style.transformOrigin = 'center top';
          const safeFrac = 0.96;
          const s = Math.max(Number(scale) || 1, 0.01);
          const visualW = Math.max(40, stage.clientWidth * safeFrac);
          const layoutW = Math.max(40, visualW / s);
          this.preview.style.width = `${layoutW}px`;
          this.preview.style.maxWidth = `${layoutW}px`;
          this.preview.style.flexShrink = '0';
          this.preview.style.transform = `scale(${scale})`;
          if (sliding) this._syncRailTrack(this.input?.value || '');
          this._applyScenePosition();
          if (this._isRgbMode()) this._applyRgbLiveBand();
          return;
        }
        const maxW = stage.clientWidth * 0.96;
        const contentW = this.preview.scrollWidth;
        if (!contentW || contentW <= maxW) {
          this.preview.style.transform = `scale(${scale})`;
          return;
        }
        const fitted = Math.max(0.4, (maxW / contentW) * scale);
        this.preview.style.transform = `scale(${fitted})`;
      };
      applyFit();
      requestAnimationFrame(applyFit);
    }

    _setStatus(message, tone) {
      this.statusEl.hidden = !message;
      this.statusEl.textContent = message || '';
      this.statusEl.dataset.tone = tone || 'success';
    }

    _clearStatus() {
      this._setStatus('', 'success');
    }
  }

  if (!customElements.get('letter-craft')) {
    customElements.define('letter-craft', LetterCraft);
  }
})();
